import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import Masonry from 'react-masonry-css';
import GoogleDriveVideoEmbed from '../components/common/GoogleDriveVideoEmbed';
import { 
  UsersIcon, 
  BriefcaseIcon, 
  PresentationChartLineIcon,
  PlusIcon,
  FunnelIcon,
  HashtagIcon,
  FolderIcon,
  IdentificationIcon,
  DocumentTextIcon,
  BanknotesIcon,
  ReceiptPercentIcon,
  XMarkIcon,
  KeyIcon,
  ClipboardDocumentIcon,
  CloudArrowUpIcon,
  EyeIcon,
  CalendarDaysIcon,
  MapPinIcon,
  GlobeAltIcon,
  ArrowTopRightOnSquareIcon,
  PencilIcon,
  PrinterIcon,
  LinkIcon,
  TrashIcon, 
  HeartIcon, 
  CheckBadgeIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  EnvelopeIcon,
  PhoneIcon,
  CurrencyRupeeIcon,
  TagIcon,
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  PhotoIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid, CheckBadgeIcon as CheckBadgeIconSolid, CheckCircleIcon as CheckCircleIconSolid, PlayIcon } from '@heroicons/react/24/solid';
import InvoicePrintView from '../components/crm/InvoicePrintView';
import api, { crmAPI, userAPI, collaborationAPI, API_BASE_URL } from '../services/api';
import LocationPicker from '../components/common/LocationPicker';
import toast from 'react-hot-toast';
import 'react-quill-new/dist/quill.snow.css';
import { resolveImageUrl } from '../utils/image';
import Avatar from '../components/common/Avatar';
import RequestDetailModal from '../components/collaboration/RequestDetailModal';
import { UserSuggestion } from '../types';
import ReactQuill from 'react-quill-new';
import PromptModal from '../components/common/PromptModal';
import { ConfirmModal } from '../components/common/ConfirmModal';
import { compressImage } from '../utils/compression';
import { useRestrictions } from '../hooks/useRestrictions';
// [Subscription removed] UpgradePopup removed — platform is fully free.
// import UpgradePopup from '../components/common/UpgradePopup';
import { addUploadJob, updateUploadJob } from '../store/slices/uploadSlice';
import { registerUploadController, unregisterUploadController } from '../utils/uploadTracker';
import { useAppDispatch } from '../hooks/redux';
import ClientPortal from '../components/profile/ClientPortal';

const checkVideoSupport = async (file: File): Promise<{ supported: boolean; reason?: string }> => {
  const headerSize = Math.min(file.size, 2 * 1024 * 1024);
  const blob = file.slice(0, headerSize);
  
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const arrayBuffer = e.target?.result as ArrayBuffer;
      if (!arrayBuffer) {
        resolve({ supported: true });
        return;
      }
      
      const decoder = new TextDecoder('latin1');
      const binaryStr = decoder.decode(arrayBuffer);
      
      const hasPCM = binaryStr.includes('sowt') || binaryStr.includes('twos') || binaryStr.includes('raw ') || binaryStr.includes('in24') || binaryStr.includes('in32');
      const hasProRes = binaryStr.includes('apch') || binaryStr.includes('apcn') || binaryStr.includes('apcs') || binaryStr.includes('apco');
      const hasHEVC = binaryStr.includes('hev1') || binaryStr.includes('hvc1');
      
      if (hasPCM) {
        resolve({ 
          supported: false, 
          reason: "Linear PCM Audio (Uncompressed DSLR/Mirrorless Audio)" 
        });
      } else if (hasProRes) {
        resolve({ 
          supported: false, 
          reason: "Apple ProRes Video" 
        });
      } else if (hasHEVC) {
        const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
        if (isChrome) {
          resolve({ 
            supported: false, 
            reason: "H.265 / HEVC Video" 
          });
        } else {
          resolve({ supported: true });
        }
      } else {
        resolve({ supported: true });
      }
    };
    reader.onerror = () => resolve({ supported: true });
    reader.readAsArrayBuffer(blob);
  });
};

const generateVideoThumbnail = (file: File): Promise<string> => {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;
    
    const objUrl = URL.createObjectURL(file);
    video.src = objUrl;
    
    video.onloadeddata = () => {
      video.currentTime = Math.min(1, video.duration / 2);
    };

    video.onseeked = () => {
      try {
        const canvas = document.createElement('canvas');
        const scale = 320 / Math.max(1, video.videoWidth);
        canvas.width = 320;
        canvas.height = Math.max(1, video.videoHeight * scale);

        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
          resolve(dataUrl);
        } else {
          resolve('');
        }
      } catch (e) {
        console.error('Failed to capture video frame:', e);
        resolve('');
      } finally {
        URL.revokeObjectURL(objUrl);
      }
    };

    video.onerror = () => {
      URL.revokeObjectURL(objUrl);
      resolve('');
    };
  });
};

type Tab = 'overview' | 'leads' | 'clients' | 'projects' | 'calendar' | 'contracts' | 'bookings';

export interface CRMCalendarEvent {
  id: string;
  user_id: string;
  title: string;
  description: string;
  is_all_day: boolean;
  start_time: string;
  end_time: string;
}

const getRoleBasedChecklist = (userType: string) => {
  if (userType === 'model') {
    return [
      { id: '1', label: 'Casting Details Confirmed', completed: false },
      { id: '2', label: 'Wardrobe Prepped', completed: false },
      { id: '3', label: 'Booking Date Confirmed', completed: false },
      { id: '4', label: 'Shoot Completed', completed: false },
      { id: '5', label: 'Invoice Paid', completed: false },
      { id: '6', label: 'Gallery Received', completed: false }
    ];
  }
  return [
    { id: '1', label: 'Shoot Prepped', completed: false },
    { id: '2', label: 'Photos Culled', completed: false },
    { id: '3', label: 'Initial Edits', completed: false },
    { id: '4', label: 'Final Retouching', completed: false },
    { id: '5', label: 'Gallery Delivered', completed: false },
    { id: '6', label: 'Invoice Paid', completed: false }
  ];
};

const getMapsLink = (lat: number, lng: number, address: string) => {
    if (lat && lng) return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
};

const CRMPage: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const dispatch = useAppDispatch();
  const isModel = user?.user_type === 'model';
  const professionLabel = isModel ? 'Model' : 'Photographer';
  const sessionLabel = isModel ? 'Booking' : 'Shoot';
  
  const navigate = useNavigate();
  const location = useLocation();

  const { checkRestriction, usage } = useRestrictions();
  // [Subscription removed] upgradeConfig state removed — no upgrade popups.
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [projectsTab, setProjectsTab] = useState<'active' | 'history'>('active');
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [showAddLead, setShowAddLead] = useState(false);
  const [showLeadConfirmationModal, setShowLeadConfirmationModal] = useState(false);
  
  const handleOpenAddLead = () => {
    // [Subscription removed] Lead creation is unrestricted — platform is fully free.
    setShowAddLead(true);
  };
  const [showConvertLead, setShowConvertLead] = useState<any>(null);
  const [leadAlreadyConvertedFor, setLeadAlreadyConvertedFor] = useState<{lead: any, project: any} | null>(null);
  const [isEditingProject, setIsEditingProject] = useState(false);
  const [selectedProject, setSelectedProject] = useState<any>(null);
  const [galleryImages, setGalleryImages] = useState<any[]>([]);
  const [selectedImageForView, setSelectedImageForView] = useState<any>(null);
  const [activities, setActivities] = useState<any[]>([]);

  const navigateCRMGallery = useCallback((direction: 'next' | 'prev') => {
    if (!selectedImageForView || galleryImages.length === 0) return;
    const currentIndex = galleryImages.findIndex(img => img.id === selectedImageForView.id);
    if (currentIndex === -1) return;
    let nextIndex;
    if (direction === 'next') {
      nextIndex = (currentIndex + 1) % galleryImages.length;
    } else {
      nextIndex = (currentIndex - 1 + galleryImages.length) % galleryImages.length;
    }
    setSelectedImageForView(galleryImages[nextIndex]);
  }, [selectedImageForView, galleryImages]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!selectedImageForView) return;
      if (e.key === 'ArrowRight') navigateCRMGallery('next');
      if (e.key === 'ArrowLeft') navigateCRMGallery('prev');
      if (e.key === 'Escape') setSelectedImageForView(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedImageForView, navigateCRMGallery]);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [contracts, setContracts] = useState<any[]>([]);

  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
    }
  }, [location.state?.activeTab]);

  useEffect(() => {
    if (location.state?.selectedProjectId && projects.length > 0) {
      const foundProject = projects.find(p => p.id === location.state.selectedProjectId);
      if (foundProject) {
        setSelectedProject(foundProject);
      }
    }
  }, [location.state?.selectedProjectId, projects]);
  const [projectInvoices, setProjectInvoices] = useState<any[]>([]);
  const [projectContracts, setProjectContracts] = useState<any[]>([]);
  const [templates, setTemplates] = useState<any[]>([]);
  const [modalTab, setModalTab] = useState<'overview' | 'gallery' | 'invoices' | 'contracts'>('overview');
  const [showAddInvoice, setShowAddInvoice] = useState(false);
  const [receivedRequests, setReceivedRequests] = useState<any[]>([]);
  const [selectedRequest, setSelectedRequest] = useState<any>(null);
  const [viewingInvoice, setViewingInvoice] = useState<any>(null);
  
  const [showGenerateContract, setShowGenerateContract] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [contractTitle, setContractTitle] = useState('');
  const [contractBody, setContractBody] = useState('');
  const [viewingContract, setViewingContract] = useState<any>(null);
  const [clientSignature, setClientSignature] = useState('');
  const [newInvoice, setNewInvoice] = useState({
    project_id: '',
    invoice_number: `INV-${Math.random().toString(36).substr(2, 6).toUpperCase()}`,
    due_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    tax: 0,
    notes: '',
    items: [{ description: 'Session Fee', quantity: 1, unit_price: 0 }],
    include_gst: false,
    gst_percentage: 18,
    discount_amount: '' as string | number,
    discount_type: 'fixed' as 'fixed' | 'percentage'
  });
  const [newLead, setNewLead] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    interest_type: 'Portrait',
    estimated_budget: '',
    notes: '',
    portal_user_id: null as string | null
  });
  const [newProject, setNewProject] = useState({
    title: '',
    description: '',
    location: '',
    location_name: '',
    latitude: 0 as number | null,
    longitude: 0 as number | null,
    shoot_dates: [{ 
      start_date: '', 
      end_date: '', 
      is_range: false, 
      location: '', 
      location_name: '',
      latitude: 0 as number | null,
      longitude: 0 as number | null
    }],
    total_amount: '',
    retainer_amount: '',
    retainer_paid: false,
    collaboration_id: null as string | null,
    usage_rights: '',
    wardrobe_notes: ''
  });
  const [showSyncPrompt, setShowSyncPrompt] = useState(false);
  const [projectToSync, setProjectToSync] = useState<any>(null);
  
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalState, setConfirmModalState] = useState<{
    title: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    isDestructive?: boolean;
    onConfirm: () => void;
    children?: React.ReactNode;
  }>({
    title: '',
    message: '',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    isDestructive: false,
    onConfirm: () => {}
  });

  const deleteFromDriveRef = useRef(false);

  const [showPromptModal, setShowPromptModal] = useState(false);
  const [promptModalState, setPromptModalState] = useState({
    title: '',
    message: '',
    initialValue: '',
    placeholder: '',
    confirmText: 'Confirm',
    onConfirm: (val: string) => {}
  });

  const [stats, setStats] = useState({
    totalLeads: 0,
    activeProjects: 0,
    totalRevenue: 0,
    conversionRate: 0
  });
  const [viewDate, setViewDate] = useState(new Date());
  const [calendarDays, setCalendarDays] = useState<Date[]>([]);
  const [calendarEvents, setCalendarEvents] = useState<CRMCalendarEvent[]>([]);
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [viewingCalendarEvent, setViewingCalendarEvent] = useState<CRMCalendarEvent | null>(null);
  const [isEditingEvent, setIsEditingEvent] = useState(false);
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    is_all_day: false,
    start_time: '',
    end_time: ''
  });

  const [viewingLead, setViewingLead] = useState<any | null>(null);
  const [isEditingLead, setIsEditingLead] = useState(false);
  const [editedLead, setEditedLead] = useState<any>(null);

  // User Search for Leads
  const [userSuggestions, setUserSuggestions] = useState<UserSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
  const searchTimeout = React.useRef<NodeJS.Timeout | null>(null);
  const suggestionsRef = React.useRef<HTMLDivElement>(null);
  const videoInputRef = React.useRef<HTMLInputElement>(null);
  
  const [showVideoOptionsModal, setShowVideoOptionsModal] = useState(false);
  const [isLinkingVideo, setIsLinkingVideo] = useState(false);
  const [videoLinkUrl, setVideoLinkUrl] = useState('');
  const [videoLinkTitle, setVideoLinkTitle] = useState('');
  const [videoLinkDesc, setVideoLinkDesc] = useState('');

  // Click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowSuggestions(false);
      }
    };

    if (showSuggestions) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEsc);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [showSuggestions]);

  const searchUsers = async (query: string) => {
    if (query.length < 2) {
      setUserSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    try {
      const response = await userAPI.searchUsers(query);
      setUserSuggestions(response.data.data || []);
      setShowSuggestions(true);
    } catch {
      setUserSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const debouncedSearchUsers = (name: string) => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => searchUsers(name), 300);
  };

  useEffect(() => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    const days = [];
    const startDay = firstDay.getDay(); // 0-6 (Sun-Sat)
    
    // Fill previous month days
    for (let i = startDay - 1; i >= 0; i--) {
      days.push(new Date(year, month, -i));
    }
    
    // Fill current month days
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }
    
    // Fill next month days
    const remainingDays = 42 - days.length; // 6 rows of 7 days
    for (let i = 1; i <= remainingDays; i++) {
      days.push(new Date(year, month + 1, i));
    }
    
    setCalendarDays(days);
  }, [viewDate]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchData = async () => {
    try {
      setLoading(true);
      const [leadsRes, projectsRes, clientsRes, eventsRes, contractsRes, requestsRes, invoicesRes] = await Promise.all([
        crmAPI.getLeads(),
        crmAPI.getProjects(),
        crmAPI.getClients(),
        crmAPI.getCalendarEvents(),
        crmAPI.getContracts(),
        collaborationAPI.getCollaborationRequests({ type: 'received', status: 'pending' }),
        crmAPI.getInvoices()
      ]);
      
      setLeads(leadsRes.data.data.leads || []);
      setProjects(projectsRes.data.data.projects || []);
      setInvoices(invoicesRes.data.data.invoices || []);
      setClients(clientsRes.data.data.clients || []);
      setCalendarEvents(eventsRes.data.data.events || []);
      setContracts(contractsRes.data.data.contracts || []);
      setReceivedRequests(requestsRes.data.data?.requests || []);
      
      // Calculate real stats
      const totalRevenue = (projectsRes.data.data.projects || []).reduce((acc: number, p: any) => {
        const amount = (p.status === 'completed' && p.final_amount > 0) ? p.final_amount : p.total_amount;
        return acc + (amount || 0);
      }, 0);
      setStats({
        totalLeads: leadsRes.data.data.leads?.length || 0,
        activeProjects: (projectsRes.data.data.projects || []).filter((p: any) => p.status !== 'completed' && p.status !== 'cancelled').length,
        totalRevenue: totalRevenue,
        conversionRate: (leadsRes.data.data.leads?.length + (projectsRes.data.data.projects || []).length) > 0 
            ? Math.round(((projectsRes.data.data.projects || []).length / (leadsRes.data.data.leads.length + (projectsRes.data.data.projects || []).length)) * 100)
            : 0
      });

      if (selectedProject) {
          const updated = (projectsRes.data.data.projects || []).find((p: any) => p.id === selectedProject.id);
          if (updated) setSelectedProject(updated);
      }
    } catch (err) {
      console.error('Error fetching CRM data:', err);
      toast.error('Failed to load CRM data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEvent = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await crmAPI.createCalendarEvent(newEvent);
      toast.success('Event added successfully!');
      setShowAddEvent(false);
      setNewEvent({ title: '', description: '', is_all_day: false, start_time: '', end_time: '' });
      fetchData(); // Refresh calendar
    } catch (err) {
      console.error('Failed to create event:', err);
      toast.error('Failed to create event');
    }
  };

  const handleDeleteEvent = async (id: string) => {
    setConfirmModalState({
      title: 'Delete Event',
      message: 'Delete this event?',
      isDestructive: true,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onConfirm: async () => {
        try {
          await crmAPI.deleteCalendarEvent(id);
          toast.success('Event deleted');
          setViewingCalendarEvent(null);
          fetchData();
        } catch (err) {
          toast.error('Failed to delete event');
        }
        setShowConfirmModal(false);
      }
    });
    setShowConfirmModal(true);
  };

  const handleUpdateEvent = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!viewingCalendarEvent) return;
    try {
      await crmAPI.updateCalendarEvent(viewingCalendarEvent.id, viewingCalendarEvent);
      toast.success('Event updated');
      setViewingCalendarEvent(null);
      setIsEditingEvent(false);
      fetchData();
    } catch (err) {
      toast.error('Failed to update event');
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // [Subscription removed] CRM access check removed — CRM is freely accessible to all non-hirers.
    fetchData();
  }, []);

  useEffect(() => {
    if (user?.gst_number) {
        setNewInvoice(prev => ({
            ...prev,
            include_gst: true,
            gst_percentage: user.gst_percentage || 18
        }));
    }
  }, [user]);

  const fetchGallery = async (projectId: string) => {
    try {
        const res = await crmAPI.getGalleryImages(projectId);
        setGalleryImages(res.data.data.images || []);
    } catch (err) {
        console.error('Failed to fetch gallery');
    }
  };

   useEffect(() => {
    if (selectedProject) {
        // Fetch activities
        crmAPI.getActivities(selectedProject.id)
            .then(res => setActivities(res.data.data.activities || []))
            .catch(() => console.error('Failed to fetch activities'));
            
        // Fetch gallery
        fetchGallery(selectedProject.id);

        // Fetch invoices
        crmAPI.getProjectInvoices(selectedProject.id)
            .then(res => setProjectInvoices(res.data.data.invoices || []))
            .catch(() => console.error('Failed to fetch project invoices'));

        // Fetch contracts
        crmAPI.getProjectContracts(selectedProject.id)
            .then(res => setProjectContracts(res.data.data.contracts || []))
            .catch(() => console.error('Failed to fetch project contracts'));
            
        // Fetch templates
        crmAPI.getContractTemplates()
            .then(res => setTemplates(res.data.data.templates || []))
            .catch(() => console.error('Failed to fetch templates'));
    } else {
        setActivities([]);
        setGalleryImages([]);
        setProjectInvoices([]);
        setProjectContracts([]);
    }
  }, [selectedProject?.id]);

  useEffect(() => {
    if (showGenerateContract && selectedTemplate) {
        const tmpl = templates.find(t => t.id === selectedTemplate);
        if (tmpl) {
            let text = tmpl.body;
            text = text.replace(/{{client_name}}/g, selectedProject?.client_name || '');
            text = text.replace(/{{project_title}}/g, selectedProject?.title || '');
            text = text.replace(/{{total_amount}}/g, selectedProject?.total_amount || '');
            text = text.replace(/{{advance_amount}}/g, selectedProject?.retainer_amount || '0');
            text = text.replace(/{{project_date}}/g, selectedProject?.shoot_dates?.[0]?.start_date ? new Date(selectedProject.shoot_dates[0].start_date).toLocaleDateString() : '');
            setContractBody(text);
        }
    } else if (showGenerateContract && !selectedTemplate) {
        setContractBody('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate, selectedProject?.id, templates, showGenerateContract]);

  const handleGenerateContract = async (e: React.FormEvent) => {
      e.preventDefault();
      try {
          const sanitizedBody = contractBody.replace(/href="(?!(http|https|mailto:|tel:|\/|#))(.*?)"/g, 'href="https://$2"');
          await crmAPI.createContract(selectedProject.id, {
              template_id: selectedTemplate,
              title: contractTitle,
              body: sanitizedBody
          });
          toast.success('Contract generated successfully');
          setShowGenerateContract(false);
          // Refresh contracts
          const res = await crmAPI.getProjectContracts(selectedProject.id);
          setProjectContracts(res.data.data.contracts || []);
          fetchData(); // Sync global list

      } catch (error: any) {
          toast.error(error.response?.data?.error || 'Failed to generate contract. Please ensure you are logged in.');
      }
  };

  const handleSignContract = async (e: React.FormEvent) => {
      e.preventDefault();
      if (!clientSignature.trim()) {
          toast.error('Please type your name to sign');
          return;
      }
      try {
          await crmAPI.signAsPhotographer(viewingContract.id, clientSignature);
          toast.success('Contract signed as photographer');
          setViewingContract({ ...viewingContract, photographer_signature: clientSignature, photographer_signed_at: new Date().toISOString() });
          setClientSignature('');
          // Refresh contracts
          const res = await crmAPI.getProjectContracts(selectedProject.id);
          setProjectContracts(res.data.data.contracts || []);
          fetchData(); // Sync global list

      } catch (error) {
          toast.error('Failed to sign contract');
      }
  };

  const handleAddLead = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await crmAPI.createLead({
        ...newLead,
        estimated_budget: parseFloat(newLead.estimated_budget) || 0
      });
      setShowAddLead(false);
      setShowLeadConfirmationModal(true);
      setNewLead({
        client_name: '',
        client_email: '',
        client_phone: '',
        interest_type: 'Portrait',
        estimated_budget: '',
        notes: '',
        portal_user_id: null
      });
      fetchData();
    } catch (err) {
      toast.error('Failed to add lead');
    }
  };

  const handleConvertLead = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!showConvertLead) return;
    try {
      if (isEditingProject && selectedProject) {
        await crmAPI.updateProject(selectedProject.id, {
          client_name: showConvertLead.client_name,
          title: newProject.title,
          description: newProject.description,
          shoot_dates: newProject.shoot_dates.map(sd => ({
            start_date: new Date(sd.start_date).toISOString(),
            end_date: new Date(sd.is_range ? sd.end_date : sd.start_date).toISOString(),
            is_range: sd.is_range,
            location: sd.location,
            location_name: sd.location_name,
            latitude: sd.latitude,
            longitude: sd.longitude
          })),
          location: newProject.location,
          location_name: newProject.location_name,
          latitude: newProject.latitude,
          longitude: newProject.longitude,
          total_amount: parseFloat(newProject.total_amount),
          retainer_amount: parseFloat(newProject.retainer_amount) || 0,
          retainer_paid: newProject.retainer_paid,
          usage_rights: newProject.usage_rights,
          wardrobe_notes: newProject.wardrobe_notes
        });
        toast.success('Project updated successfully!');
      } else {
        // 1. Create client from lead (Backend now deduplicates automatically based on email/phone/owner_id)
        const clientRes = await crmAPI.createClient({
          first_name: showConvertLead.client_name.split(' ')[0],
          last_name: showConvertLead.client_name.split(' ').slice(1).join(' ') || 'Client',
          email: showConvertLead.client_email,
          phone: showConvertLead.client_phone,
          notes: showConvertLead.notes,
          source: showConvertLead.source,
          portal_user_id: showConvertLead.portal_user_id || null
        });

        // 2. Create project
        await crmAPI.createProject({
          client_id: clientRes.data.data.client.id,
          client_name: showConvertLead.client_name,
          title: newProject.title,
          description: newProject.description,
          shoot_dates: newProject.shoot_dates.map(sd => ({
            start_date: new Date(sd.start_date).toISOString(),
            end_date: new Date(sd.is_range ? sd.end_date : sd.start_date).toISOString(),
            is_range: sd.is_range,
            location: sd.location,
            location_name: sd.location_name,
            latitude: sd.latitude,
            longitude: sd.longitude
          })),
          location: newProject.location,
          location_name: newProject.location_name,
          latitude: newProject.latitude,
          longitude: newProject.longitude,
          total_amount: parseFloat(newProject.total_amount) || showConvertLead.estimated_budget,
          retainer_amount: parseFloat(newProject.retainer_amount) || 0,
          retainer_paid: newProject.retainer_paid,
          checklist: JSON.stringify(getRoleBasedChecklist(user?.user_type || 'creator')),
          collaboration_id: newProject.collaboration_id, // Link to post
          lead_id: showConvertLead.id, // Link to original lead
          usage_rights: newProject.usage_rights,
          wardrobe_notes: newProject.wardrobe_notes
        });

        // 3. Update lead status to converted
        await crmAPI.updateLeadStatus(showConvertLead.id, 'converted');
        toast.success('Lead converted to project!');
      }

      setShowConvertLead(null);
      setIsEditingProject(false);
      setNewProject({ 
        title: '', 
        description: '', 
        location: '',
        location_name: '',
        latitude: 0,
        longitude: 0,
        shoot_dates: [{ 
          start_date: '', 
          end_date: '', 
          is_range: false,
          location: '',
          location_name: '',
          latitude: 0,
          longitude: 0
        }], 
        total_amount: '',
        retainer_amount: '',
        retainer_paid: false,
        collaboration_id: null,
        usage_rights: '',
        wardrobe_notes: ''
      });
      fetchData();
    } catch (err: any) {
      // console.log(err);
      if (err.response?.status === 409) {
        // Show modal to navigate to existing project instead of plain toast
        const existingProject = projects.find(p => p.lead_id === showConvertLead?.id);
        setLeadAlreadyConvertedFor({ lead: showConvertLead, project: existingProject || null });
        setShowConvertLead(null);
        fetchData();
      } else {
        toast.error(err.response?.data?.error || 'Failed to convert lead');
      }
    }
  };

  const updateLeadStatus = async (id: string, status: string, lead: any) => {
    try {
      if (status === 'converted') {
        if (!lead.is_confirmed) {
          toast.error("Cannot convert lead until the client has confirmed the request.");
          return;
        }
        const existingProjects = projects.filter(p => p.lead_id === id);
        if (existingProjects.length > 0) {
            // Update the lead status to converted in DB, then show the modal
            await crmAPI.updateLeadStatus(id, 'converted');
            setLeads(leads.map(l => l.id === id ? { ...l, status: 'converted' } : l));
            setLeadAlreadyConvertedFor({ lead, project: existingProjects[0] });
            return;
        }
        // [Subscription removed] Project creation is unrestricted — platform is fully free.
        setShowConvertLead(lead);
        setNewProject({
          ...newProject, 
          title: `${lead.client_name} Session`, 
          description: lead.notes || '',
          total_amount: lead.estimated_budget?.toString() || '', 
          shoot_dates: [{ 
            start_date: '', 
            end_date: '', 
            is_range: false,
            location: '',
            location_name: '',
            latitude: 0,
            longitude: 0
          }]
        });
        return; // handleConvertLead will trigger the actual status update
      }
      await crmAPI.updateLeadStatus(id, status);
      toast.success('Status updated');
      fetchData();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const updateProjectStatus = async (id: string, status: string) => {
    if (status === 'completed') {
      const currentProject = projects.find(p => p.id === id) || selectedProject;
      setPromptModalState({
        title: 'Final Earnings',
        message: `Enter final earnings for this project (Initial Quote: ₹${currentProject?.total_amount || 0}):`,
        initialValue: (currentProject?.total_amount || 0).toString(),
        placeholder: 'Amount',
        confirmText: 'Mark Completed',
        onConfirm: async (val: string) => {
          const finalAmount = parseFloat(val) || 1;
          try {
            await crmAPI.updateProjectStatus(id, status, finalAmount);
            toast.success('Project status updated');
            if (selectedProject && selectedProject.id === id) {
              setSelectedProject((prev: any) => prev ? { ...prev, status, final_amount: finalAmount } : null);
            }
            fetchData();
            setShowPromptModal(false);
          } catch (err) {
            toast.error('Failed to update project status');
          }
        }
      });
      setShowPromptModal(true);
      return;
    }

    if (status === 'cancelled') {
      setConfirmModalState({
        title: 'Cancel Project',
        message: 'Are you sure you want to cancel this project? This will automatically cancel all associated unpaid invoices and unsigned contracts, and you will not be able to change its status back.',
        confirmText: 'Yes, Cancel Project',
        cancelText: 'Keep Project Active',
        isDestructive: true,
        onConfirm: async () => {
          try {
            await crmAPI.updateProjectStatus(id, status, 0);
            toast.success('Project status updated');
            if (selectedProject && selectedProject.id === id) {
              setSelectedProject((prev: any) => prev ? { ...prev, status } : null);
            }
            fetchData();
          } catch (err) {
            toast.error('Failed to update project status');
          }
          setShowConfirmModal(false);
        }
      });
      setShowConfirmModal(true);
      return;
    }

    try {
      await crmAPI.updateProjectStatus(id, status, 0);
      toast.success('Project status updated');
      if (selectedProject && selectedProject.id === id) {
        setSelectedProject((prev: any) => prev ? { ...prev, status } : null);
      }
      fetchData();
    } catch (err) {
      toast.error('Failed to update project status');
    }
  };

  const updateProjectChecklist = async (id: string, checklist: any[]) => {
      try {
          await crmAPI.updateProjectChecklist(id, JSON.stringify(checklist));
          fetchData();
      } catch (err) {
          toast.error('Failed to update checklist');
      }
  };

  const fetchInvoices = async (projectId: string) => {
    try {
      const res = await crmAPI.getProjectInvoices(projectId);
      setProjectInvoices(res.data.data.invoices || []);
    } catch (err) {
      console.error('Failed to fetch invoices');
    }
  };

  const handleCreateInvoice = async (e: React.FormEvent) => {
    e.preventDefault();
    const subtotal = newInvoice.items.reduce((acc, item) => acc + (item.quantity * item.unit_price), 0);
    const taxAmount = newInvoice.include_gst ? (subtotal * (newInvoice.gst_percentage / 100)) : 0;
    
    let discountValue = 0;
    const rawDiscount = parseFloat(newInvoice.discount_amount.toString() || '0');
    if (newInvoice.discount_type === 'percentage') {
        discountValue = subtotal * (rawDiscount / 100);
    } else {
        discountValue = rawDiscount;
    }

    try {
      await crmAPI.createInvoice({
        ...newInvoice,
        project_id: selectedProject.id,
        due_date: new Date(newInvoice.due_date).toISOString(),
        tax: taxAmount,
        discount_amount: discountValue,
        gst_number: user?.gst_number || '',
        items: newInvoice.items.map(item => ({
            ...item,
            quantity: parseFloat(item.quantity.toString()),
            unit_price: parseFloat(item.unit_price.toString())
        }))
      });
      toast.success('Invoice created');
      setShowAddInvoice(false);
      fetchInvoices(selectedProject.id);
      fetchData(); // Refresh project totals
    } catch (err) {
      toast.error('Failed to create invoice');
    }
  };

  const updateInvoiceStatus = async (id: string, status: string) => {
    try {
      await crmAPI.updateInvoiceStatus(id, status);
      toast.success('Invoice updated');
      if (selectedProject?.id) fetchInvoices(selectedProject.id);
      fetchData(); // Refresh project totals
    } catch (err) {
      toast.error('Failed to update invoice');
    }
  };

  const updateContractStatus = async (id: string, status: string) => {
    try {
      await crmAPI.updateContractStatus(id, status);
      toast.success('Contract sent successfully');
      if (selectedProject?.id) {
        const res = await crmAPI.getProjectContracts(selectedProject.id);
        setProjectContracts(res.data.data.contracts || []);
      }
      fetchData(); // Sync global list
    } catch (err) {
      toast.error('Failed to update contract status');
    }
  };

  const checkBusinessDetails = () => {
    if (!user?.company_name || !user?.address) {
      setConfirmModalState({
        title: 'Update Business Details',
        message: 'You need to provide your business name and address to generate professional contracts and invoices.',
        confirmText: 'Lets do it',
        cancelText: 'Maybe later',
        isDestructive: false,
        onConfirm: () => {
          navigate('/settings?tab=business');
          setShowConfirmModal(false);
        }
      });
      setShowConfirmModal(true);
      return false;
    }
    return true;
  };

  const renderActionItems = () => {
    const today = new Date();
    const next7Days = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
    const staleLeadDate = new Date(today.getTime() - 14 * 24 * 60 * 60 * 1000);

    const actionItems: any[] = [];

    // 1. Client Activity Alerts (Proofing, Contracts, Invoices)
    projects.forEach(p => {
        const ackAt = p.photographer_acknowledged_at ? new Date(p.photographer_acknowledged_at) : new Date(0);

        // Images Proofed
        if (p.client_proofing_at && new Date(p.client_proofing_at) > ackAt) {
            actionItems.push({
                id: `proof-${p.id}`,
                title: `Images Proofed: ${p.client_name}`,
                description: `${p.title}: Client selected images for proofing.`,
                type: 'proofing',
                icon: CheckCircleIconSolid,
                color: 'text-teal-600',
                bg: 'bg-teal-50',
                action: async () => {
                   setSelectedProject(p);
                   setModalTab('gallery');
                   try {
                       await crmAPI.acknowledgeProjectUpdates(p.id);
                       fetchData();
                   } catch (e) { console.error('Ack error:', e); }
                }
            });
        }

        // Contracts Signed
        const signedContracts = contracts.filter(c => c.project_id === p.id && c.signed_at && new Date(c.signed_at) > ackAt);
        signedContracts.forEach(c => {
            actionItems.push({
                id: `contract-signed-${c.id}`,
                title: `Contract Signed: ${p.client_name}`,
                description: `Client signed "${c.title}"`,
                type: 'contract-signed',
                icon: CheckBadgeIconSolid,
                color: 'text-indigo-600',
                bg: 'bg-indigo-50',
                action: async () => {
                    setSelectedProject(p);
                    setViewingContract(c);
                    setModalTab('contracts');
                    try {
                        await crmAPI.acknowledgeProjectUpdates(p.id);
                        fetchData();
                    } catch (e) { console.error('Ack error:', e); }
                }
            });
        });

        // Invoices Accepted/Paid
        const newInvoices = invoices.filter(inv => 
            inv.project_id === p.id && 
            ((inv.accepted_at && new Date(inv.accepted_at) > ackAt) || 
             (inv.status === 'paid' && inv.updated_at && new Date(inv.updated_at) > ackAt))
        );
        newInvoices.forEach(inv => {
            const isPaid = inv.status === 'paid';
            actionItems.push({
                id: `invoice-act-${inv.id}`,
                title: isPaid ? `Payment Received` : `Invoice Accepted`,
                description: `${p.client_name} ${isPaid ? 'paid' : 'accepted'} Inv #${inv.invoice_number}`,
                type: 'invoice-activity',
                icon: isPaid ? CheckBadgeIconSolid : CurrencyRupeeIcon,
                color: isPaid ? 'text-emerald-600' : 'text-purple-600',
                bg: isPaid ? 'bg-emerald-50' : 'bg-purple-50',
                action: async () => {
                    setSelectedProject(p);
                    setViewingInvoice(inv);
                    setModalTab('invoices');
                    try {
                        await crmAPI.acknowledgeProjectUpdates(p.id);
                        fetchData();
                    } catch (e) { console.error('Ack error:', e); }
                }
            });
        });
    });

    // 2. Upcoming Shoots
    const upcomingShoots = projects.filter(p => {
        if (!p.shoot_dates || p.status === 'completed' || p.status === 'cancelled') return false;
        return p.shoot_dates.some((sd: any) => {
            const shootDate = new Date(sd.start_date);
            return shootDate >= today && shootDate <= next7Days;
        });
    });
    upcomingShoots.forEach(p => {
        actionItems.push({
            id: `shoot-${p.id}`,
            title: `Upcoming Shoot: ${p.title}`,
            description: `Scheduled for ${new Date(p.shoot_dates[0].start_date).toLocaleDateString()}`,
            type: 'upcoming',
            icon: ClockIcon,
            color: 'text-blue-500',
            bg: 'bg-blue-50',
            action: () => setSelectedProject(p)
        });
    });

    // 3. Awaiting Signature
    const pendingContracts = contracts.filter(c => c.status !== 'signed' && c.status !== 'rejected' && !c.photographer_signature);
    pendingContracts.forEach(c => {
         actionItems.push({
            id: `contract-${c.id}`,
            title: `Countersign Contract`,
            description: `Project: ${projects.find(p => p.id === c.project_id)?.title || 'Unknown'}`,
            type: 'signature',
            icon: DocumentTextIcon,
            color: 'text-purple-500',
            bg: 'bg-purple-50',
            action: () => {
                const project = projects.find(p => p.id === c.project_id);
                if (project) {
                    setSelectedProject(project);
                    setViewingContract(c);
                    setModalTab('contracts');
                }
            }
        });
    });

    // 4. Stale Leads
    const staleLeads = leads.filter(l => l.status === 'confirmed' && new Date(l.created_at) < staleLeadDate);
    staleLeads.forEach(l => {
        actionItems.push({
            id: `lead-${l.id}`,
            title: `Stale Lead: ${l.client_name}`,
            description: `Inquiry received over 14 days ago.`,
            type: 'lead',
            icon: ExclamationTriangleIcon,
            color: 'text-amber-500',
            bg: 'bg-amber-50',
            action: () => setActiveTab('leads')
        });
    });

    // 5. Pending Delivery (Draft galleries with images)
    const pendingDelivery = projects.filter(p => p.gallery_status === 'draft' && p.status === 'active');
    pendingDelivery.forEach(p => {
        actionItems.push({
            id: `delivery-${p.id}`,
            title: `Draft Gallery: ${p.title}`,
            description: `Images uploaded. Awaiting publication.`,
            type: 'delivery',
            icon: ArrowPathIcon,
            color: 'text-indigo-500',
            bg: 'bg-indigo-50',
            action: () => setSelectedProject(p)
        });
    });

    // 6. Financial Alerts (Pending Payments)
    const unpaidProjects = projects.filter(p => {
        const totalPaid = Math.min(p.total_amount, (p.amount_paid || 0) + (p.retainer_paid ? (p.retainer_amount || 0) : 0));
        return (p.total_amount - totalPaid) > 0 && p.status !== 'cancelled' && p.status !== 'completed';
    });
    unpaidProjects.forEach(p => {
        const totalPaid = Math.min(p.total_amount, (p.amount_paid || 0) + (p.retainer_paid ? (p.retainer_amount || 0) : 0));
        const remaining = Math.max(0, Math.round((p.total_amount - totalPaid) * 100) / 100);
        
        if (p.retainer_amount > 0 && !p.retainer_paid) {
            actionItems.push({
                id: `finance-${p.id}`,
                title: `Follow up Advance Payment`,
                description: `₹${p.retainer_amount} advance due for ${p.title}`,
                type: 'finance',
                icon: PresentationChartLineIcon,
                color: 'text-orange-500',
                bg: 'bg-orange-50',
                action: () => setSelectedProject(p)
            });
        } else if (remaining > 5000) {
            actionItems.push({
                id: `finance-${p.id}`,
                title: `Payment Pending`,
                description: `₹${remaining} due for ${p.title}`,
                type: 'finance',
                icon: PresentationChartLineIcon,
                color: 'text-rose-500',
                bg: 'bg-rose-50',
                action: () => setSelectedProject(p)
            });
        }
    });

    // 7. New Leads (Recent activity)
    const recentLeads = leads.filter(l => l.status === 'confirmed' && new Date(l.created_at) > staleLeadDate);
    recentLeads.forEach(l => {
        actionItems.push({
            id: `new-lead-${l.id}`,
            title: `New Inquiry: ${l.client_name}`,
            description: `Interested in ${l.interest_type}. Follow up today!`,
            type: 'lead',
            icon: UsersIcon,
            color: 'text-teal-500',
            bg: 'bg-teal-50',
            action: () => setActiveTab('leads')
        });
    });

    // 8. Collaboration Integration
    receivedRequests.forEach(req => {
        actionItems.push({
            id: `collab-${req.id}`,
            title: `New Lead Contacted: ${req.sender?.first_name} ${req.sender?.last_name}`,
            description: `Wants to collaborate on a ${req.type} project.`,
            type: 'collab-req',
            icon: UsersIcon,
            color: 'text-teal-500',
            bg: 'bg-teal-50',
            action: () => setSelectedRequest(req)
        });
    });

    // If nothing, show a positive placeholder
    if (actionItems.length === 0) {
        return (
            <div className="mb-12">
                <h2 className="text-xl font-black text-gray-900 dark:text-white mb-6">Smart <span className="text-teal-500">Updates</span></h2>
                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-[2rem] p-8 text-center border border-dashed border-gray-200 dark:border-gray-700">
                    <CheckCircleIcon className="w-12 h-12 text-teal-500/20 mx-auto mb-3" />
                    <p className="text-sm font-bold text-gray-400">Your workflow is up to date. No immediate actions required.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="mb-12">
            <h2 className="text-xl font-black text-gray-900 dark:text-white mb-6">Smart <span className="text-teal-500">Updates</span></h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {actionItems.slice(0, 6).map((item) => (
                    <motion.button
                        key={item.id}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        onClick={item.action}
                        className="flex items-center p-4 bg-white dark:bg-gray-900 rounded-[1.5rem] border border-gray-100 dark:border-gray-800 shadow-sm text-left hover:border-teal-500 transition-colors group"
                    >
                        <div className={`p-3 rounded-xl ${item.bg} ${item.color} mr-4`}>
                            <item.icon className="w-6 h-6" />
                        </div>
                        <div className="flex-1 truncate">
                            <h4 className="font-bold text-gray-900 dark:text-white truncate">{item.title}</h4>
                            <p className="text-xs text-gray-400 font-medium truncate">{item.description}</p>
                        </div>
                        <div className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <ArrowTopRightOnSquareIcon className="w-5 h-5 text-teal-500" />
                        </div>
                    </motion.button>
                ))}
            </div>
        </div>
    );
  };

  const toggleChecklistItem = (project: any, itemId: string) => {
      const checklist = project.checklist ? JSON.parse(project.checklist) : getRoleBasedChecklist(user?.user_type || 'creator');
      const updated = checklist.map((item: any) => 
          item.id === itemId ? { ...item, completed: !item.completed } : item
      );
      updateProjectChecklist(project.id, updated);
  };

  const uploadImages = async (projectID: string, files: FileList) => {
      // Check the 1000-image gallery limit before uploading
      const check = checkRestriction('upload_gallery_image', files.length);
      if (!check.allowed) {
          toast.error(check.reason || 'You have reached the 1000 gallery image limit. Please delete some images to upload more.');
          return;
      }
      const jobId = `crm-gallery-${Date.now()}`;
      
      // Add background job
      dispatch(addUploadJob({
        id: jobId,
        title: `Project Images (${files.length})`,
        type: 'gallery',
        status: 'uploading',
        progress: 0,
      }));

      const controller = new AbortController();
      registerUploadController(jobId, controller);

      // Async process
      const uploadProcess = async () => {
        const formData = new FormData();
        try {
          // Compress images sequentially (to avoid memory pressure on mobile)
          for (let i = 0; i < files.length; i++) {
            const compressedFile = await compressImage(files[i]);
            formData.append('images', compressedFile);
          }

          const res = await crmAPI.uploadGalleryImages(projectID, formData, {
            signal: controller.signal,
            onUploadProgress: (progressEvent: any) => {
              if (progressEvent.total) {
                const pct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                dispatch(updateUploadJob({ id: jobId, progress: pct }));
                if (pct === 100) {
                  dispatch(updateUploadJob({ id: jobId, status: 'processing' }));
                }
              }
            }
          });

          if (res.data.success && res.data.data.images) {
            setGalleryImages(prev => [...prev, ...res.data.data.images]);
            dispatch(updateUploadJob({ id: jobId, status: 'completed' }));
            fetchData(); // Refresh activities too
          } else {
            throw new Error(res.data.error || 'Upload failed');
          }
        } catch (err: any) {
          if (axios.isCancel(err) || err.name === 'CanceledError' || err.message === 'canceled') {
            console.log('Upload canceled');
            return;
          }
          console.error('Gallery upload error:', err);
          const errorMsg = err.response?.data?.error || err.message || 'Failed to upload images';
          dispatch(updateUploadJob({ id: jobId, status: 'error', error: errorMsg }));
        } finally {
          unregisterUploadController(jobId);
        }
      };

      // Run background upload immediately
      uploadProcess();
  };

  const toggleImageSelection = async (imageID: string) => {
      try {
          const res = await crmAPI.toggleImageSelection(imageID);
          const updatedImage = res.data?.data?.image;
          const newStatus = updatedImage?.is_photographer_selected;

          setGalleryImages(prev => prev.map(img => 
              img.id === imageID ? { 
                ...img, 
                is_photographer_selected: newStatus !== undefined ? newStatus : !img.is_photographer_selected 
              } : img
          ));

          if (selectedImageForView?.id === imageID) {
              setSelectedImageForView((prev: any) => prev ? { 
                ...prev, 
                is_photographer_selected: newStatus !== undefined ? newStatus : !prev.is_photographer_selected 
              } : prev);
          }
      } catch (err) {
          toast.error('Failed to update selection');
      }
  };

  const handleUpdateLead = async () => {
    if (!editedLead) return;
    try {
      toast.loading('Updating lead...', { id: 'update-lead' });
      const response = await crmAPI.updateLead(editedLead.id, editedLead);
      toast.success('Lead updated', { id: 'update-lead' });
      
      // Update local state
      setLeads(leads.map(l => l.id === editedLead.id ? response.data.data.lead : l));
      setViewingLead(response.data.data.lead);
      setIsEditingLead(false);
      fetchData(); // Refresh summary/stats
    } catch (error: any) {
      console.error('Update lead error:', error);
      toast.error(error.response?.data?.error || 'Failed to update lead', { id: 'update-lead' });
    }
  };

  const renderLeadDetailModal = () => (
    <AnimatePresence>
      {viewingLead && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setViewingLead(null)}
            className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="relative w-full max-w-2xl bg-white dark:bg-gray-900 rounded-[2.5rem] shadow-2xl overflow-hidden border border-gray-100 dark:border-gray-800"
          >
            {/* Header */}
            <div className="p-6 md:p-8 border-b border-gray-50 dark:border-gray-800 flex items-center justify-between bg-gray-50/50 dark:bg-gray-800/50">
              <div className="flex items-center space-x-4">
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center font-bold text-xl ${viewingLead.portal_user_id ? 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600' : 'bg-teal-50 dark:bg-teal-900/20 text-teal-600'}`}>
                  {viewingLead.client_name.charAt(0)}
                </div>
                <div>
                  <h3 className="text-xl font-black text-gray-900 dark:text-white">Lead Details</h3>
                  <p className="text-xs text-gray-500 flex items-center">
                    <span className={`w-2 h-2 rounded-full mr-2 ${
                      viewingLead.status === 'confirmed' ? 'bg-blue-500' :
                      viewingLead.status === 'contacted' ? 'bg-amber-500' :
                      viewingLead.status === 'proposal' ? 'bg-purple-500' :
                      'bg-teal-500'
                    }`} />
                    Status: <span className="uppercase ml-1 font-bold">{viewingLead.status}</span>
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {!isEditingLead ? (
                  <button
                    onClick={() => {
                      setEditedLead({ ...viewingLead });
                      setIsEditingLead(true);
                    }}
                    className="p-2 hover:bg-white dark:hover:bg-gray-800 rounded-xl text-gray-400 hover:text-teal-600 transition-colors border border-transparent hover:border-teal-100 dark:hover:border-teal-500/20"
                    title="Edit Lead"
                  >
                    <PencilIcon className="w-5 h-5" />
                  </button>
                ) : (
                  <button
                    onClick={() => setIsEditingLead(false)}
                    className="px-4 py-2 text-sm font-bold text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                  >
                    Cancel
                  </button>
                )}
                <button
                  onClick={() => setViewingLead(null)}
                  className="p-2 hover:bg-white dark:hover:bg-gray-800 rounded-xl text-gray-400 hover:text-red-500 transition-colors border border-transparent hover:border-red-100 dark:hover:border-red-500/20"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 md:p-8 max-h-[70vh] overflow-y-auto">
              {isEditingLead ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Client Name</label>
                      <input
                        type="text"
                        value={editedLead.client_name}
                        onChange={(e) => setEditedLead({ ...editedLead, client_name: e.target.value })}
                        className="w-full bg-gray-50 dark:bg-gray-800 border-none rounded-2xl p-4 text-sm focus:ring-2 focus:ring-teal-500/20 transition-all font-bold"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Interest Type</label>
                      <select
                        value={editedLead.interest_type}
                        onChange={(e) => setEditedLead({ ...editedLead, interest_type: e.target.value })}
                        className="w-full bg-gray-50 dark:bg-gray-800 border-none rounded-2xl p-4 text-sm focus:ring-2 focus:ring-teal-500/20 transition-all font-bold"
                      >
                        <option value="Portrait">Portrait</option>
                        <option value="Wedding">Wedding</option>
                        <option value="Commercial">Commercial</option>
                        <option value="Fashion">Fashion</option>
                        <option value="Event">Event</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Email</label>
                      <input
                        type="email"
                        value={editedLead.client_email}
                        onChange={(e) => setEditedLead({ ...editedLead, client_email: e.target.value })}
                        className="w-full bg-gray-50 dark:bg-gray-800 border-none rounded-2xl p-4 text-sm focus:ring-2 focus:ring-teal-500/20 transition-all font-bold"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Phone</label>
                      <input
                        type="text"
                        value={editedLead.client_phone}
                        onChange={(e) => setEditedLead({ ...editedLead, client_phone: e.target.value.replace(/\D/g, '') })}
                        className="w-full bg-gray-50 dark:bg-gray-800 border-none rounded-2xl p-4 text-sm focus:ring-2 focus:ring-teal-500/20 transition-all font-bold"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Estimated Budget (₹)</label>
                      <input
                        type="number"
                        value={editedLead.estimated_budget}
                        onChange={(e) => setEditedLead({ ...editedLead, estimated_budget: parseFloat(e.target.value) || 0 })}
                        className="w-full bg-gray-50 dark:bg-gray-800 border-none rounded-2xl p-4 text-sm focus:ring-2 focus:ring-teal-500/20 transition-all font-bold"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Expected Shoot Date</label>
                      <input
                        type="date"
                        value={editedLead.expected_shoot_at ? new Date(editedLead.expected_shoot_at).toISOString().split('T')[0] : ''}
                        onChange={(e) => setEditedLead({ ...editedLead, expected_shoot_at: e.target.value ? new Date(e.target.value).toISOString() : null })}
                        className="w-full bg-gray-50 dark:bg-gray-800 border-none rounded-2xl p-4 text-sm focus:ring-2 focus:ring-teal-500/20 transition-all font-bold"
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Notes</label>
                    <textarea
                      rows={4}
                      value={editedLead.notes}
                      onChange={(e) => setEditedLead({ ...editedLead, notes: e.target.value })}
                      className="w-full bg-gray-50 dark:bg-gray-800 border-none rounded-[1.5rem] p-4 text-sm focus:ring-2 focus:ring-teal-500/20 transition-all font-bold"
                      placeholder="Any specific requirements or details..."
                    />
                  </div>

                  <button
                    onClick={handleUpdateLead}
                    className="w-full py-4 bg-teal-600 hover:bg-teal-700 text-white font-black rounded-2xl shadow-lg shadow-teal-500/30 transition-all active:scale-95"
                  >
                    Save Changes
                  </button>
                </div>
              ) : (
                <div className="space-y-8">
                  {/* Info Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-gray-50 dark:bg-gray-800/50 p-5 rounded-[1.5rem] border border-gray-100 dark:border-gray-800">
                      <p className="text-[9px] font-black uppercase tracking-widest text-gray-400 mb-3">Contact Information</p>
                      <div className="space-y-3">
                        <div className="flex items-center text-sm">
                          <EnvelopeIcon className="w-4 h-4 text-teal-500 mr-2" />
                          <span className="font-bold text-gray-700 dark:text-gray-300">{viewingLead.client_email}</span>
                        </div>
                        {viewingLead.client_phone && (
                          <div className="flex items-center text-sm">
                            <PhoneIcon className="w-4 h-4 text-teal-500 mr-2" />
                            <span className="font-bold text-gray-700 dark:text-gray-300">{viewingLead.client_phone}</span>
                          </div>
                        )}
                        <div className="flex items-center text-sm">
                          <TagIcon className="w-4 h-4 text-teal-500 mr-2" />
                          <span className="font-bold text-gray-700 dark:text-gray-300">{viewingLead.source || 'Direct Entry'}</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-50 dark:bg-gray-800/50 p-5 rounded-[1.5rem] border border-gray-100 dark:border-gray-800">
                      <p className="text-[9px] font-black uppercase tracking-widest text-gray-400 mb-3">Project Details</p>
                      <div className="space-y-3">
                        <div className="flex items-center text-sm">
                          <BriefcaseIcon className="w-4 h-4 text-teal-500 mr-2" />
                          <span className="font-bold text-gray-700 dark:text-gray-300">{viewingLead.interest_type}</span>
                        </div>
                        {viewingLead.estimated_budget > 0 && (
                          <div className="flex items-center text-sm">
                            <CurrencyRupeeIcon className="w-4 h-4 text-teal-500 mr-2" />
                            <span className="font-bold text-gray-700 dark:text-gray-300">₹{viewingLead.estimated_budget}</span>
                          </div>
                        )}
                        {viewingLead.expected_shoot_at && (
                          <div className="flex items-center text-sm">
                            <CalendarIcon className="w-4 h-4 text-teal-500 mr-2" />
                            <span className="font-bold text-gray-700 dark:text-gray-300">{new Date(viewingLead.expected_shoot_at).toLocaleDateString()}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Notes Section */}
                  <div className="space-y-3">
                    <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Internal Notes</p>
                    <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-[2rem] border border-gray-100 dark:border-gray-800 min-h-[100px]">
                      <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed italic">
                        {viewingLead.notes || 'No notes added yet.'}
                      </p>
                    </div>
                  </div>

                  {/* Activity Timeline Simplified */}
                  <div className="space-y-4">
                    <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Timeline</p>
                    <div className="space-y-3 pl-4 border-l-2 border-gray-100 dark:border-gray-800">
                      <div className="relative">
                        <div className="absolute -left-[25px] top-1 w-4 h-4 rounded-full bg-teal-500 border-4 border-white dark:border-gray-900" />
                        <p className="text-xs font-bold text-gray-900 dark:text-white">Lead Created</p>
                        <p className="text-[10px] text-gray-400">{new Date(viewingLead.created_at).toLocaleString()}</p>
                      </div>
                      <div className="relative">
                        <div className="absolute -left-[25px] top-1 w-4 h-4 rounded-full bg-gray-200 dark:bg-gray-700 border-4 border-white dark:border-gray-900" />
                        <p className="text-xs font-bold text-gray-900 dark:text-white">Last Updated</p>
                        <p className="text-[10px] text-gray-400">{new Date(viewingLead.updated_at).toLocaleString()}</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Action Bar */}
                  {viewingLead.status !== 'converted' && (
                    <button
                      onClick={() => {
                        updateLeadStatus(viewingLead.id, 'converted', viewingLead);
                        setViewingLead(null);
                      }}
                      className="w-full flex items-center justify-center p-4 bg-teal-50 dark:bg-teal-500/10 text-teal-600 dark:text-teal-400 rounded-2xl font-black text-sm hover:bg-teal-600 hover:text-white transition-all group"
                    >
                      Convert to Project
                      <ChevronRightIcon className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                    </button>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );

  const resendAccessCode = async (projectId: string) => {
      try {
          toast.loading('Resending access code...', { id: 'resend-password' });
          await api.post(`/crm/projects/${projectId}/resend-password`);
          toast.success('Access code resent to client!', { id: 'resend-password' });
      } catch (err) {
          toast.error('Failed to resend code', { id: 'resend-password' });
      }
  };

  const toggleGalleryPublish = async (projectId: string, currentStatus: string) => {
      const newStatus = currentStatus === 'published' ? 'draft' : 'published';
      try {
          await crmAPI.updateProjectConfig(projectId, { gallery_status: newStatus });
          toast.success(newStatus === 'published' ? 'Gallery Published!' : 'Gallery Unpublished');
          setProjects(projects.map(p => p.id === projectId ? { ...p, gallery_status: newStatus } : p));
          if (selectedProject?.id === projectId) {
              setSelectedProject({ ...selectedProject, gallery_status: newStatus });
          }
      } catch (err) {
          toast.error('Failed to update gallery status');
      }
  };

  const renderOverview = () => {
    const galleryUsed = usage?.gallery_images ?? 0;
    const galleryLimit = 1000;
    const galleryPct = Math.min(100, Math.round((galleryUsed / galleryLimit) * 100));
    const galleryNearLimit = galleryUsed >= galleryLimit * 0.9;
    const galleryAtLimit = galleryUsed >= galleryLimit;

    return (
      <>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
        {[
          { label: 'Total Leads', value: stats.totalLeads, icon: FunnelIcon, color: 'text-blue-500', bg: 'bg-blue-50' },
          { label: 'Active Projects', value: stats.activeProjects, icon: BriefcaseIcon, color: 'text-teal-500', bg: 'bg-teal-50' },
          { label: 'Total Revenue', value: `₹${stats.totalRevenue}`, icon: PresentationChartLineIcon, color: 'text-purple-500', bg: 'bg-purple-50' },
          { label: 'Conv. Rate', value: `${stats.conversionRate}%`, icon: HashtagIcon, color: 'text-amber-500', bg: 'bg-amber-50' },
        ].map((stat, i) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            className="bg-white dark:bg-gray-900 p-6 rounded-[2rem] border border-gray-100 dark:border-gray-800 shadow-sm"
          >
            <div className={`p-3 rounded-2xl ${stat.bg} w-fit mb-4`}>
              <stat.icon className={`w-6 h-6 ${stat.color}`} />
            </div>
            <p className="text-gray-500 dark:text-gray-400 text-xs font-bold uppercase tracking-widest">{stat.label}</p>
            <h3 className="text-2xl font-black text-gray-900 dark:text-white mt-1">{stat.value}</h3>
          </motion.div>
        ))}
      </div>

      {/* Gallery Image Usage Counter — global limit across all projects */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className={`mb-6 p-5 rounded-[1.5rem] border ${galleryAtLimit ? 'border-red-200 bg-red-50 dark:bg-red-900/10 dark:border-red-800' : galleryNearLimit ? 'border-amber-200 bg-amber-50 dark:bg-amber-900/10 dark:border-amber-800' : 'border-gray-100 bg-white dark:bg-gray-900 dark:border-gray-800'} shadow-sm`}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className={`p-2 rounded-xl ${galleryAtLimit ? 'bg-red-100 dark:bg-red-900/20' : galleryNearLimit ? 'bg-amber-100 dark:bg-amber-900/20' : 'bg-gray-100 dark:bg-gray-800'}`}>
              <svg className={`w-4 h-4 ${galleryAtLimit ? 'text-red-500' : galleryNearLimit ? 'text-amber-500' : 'text-gray-500'}`} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
              </svg>
            </div>
            <div>
              <p className={`text-xs font-bold uppercase tracking-widest ${galleryAtLimit ? 'text-red-600 dark:text-red-400' : galleryNearLimit ? 'text-amber-600 dark:text-amber-400' : 'text-gray-500 dark:text-gray-400'}`}>Gallery Storage</p>
              {galleryAtLimit && <p className="text-xs text-red-500 dark:text-red-400 font-medium">Limit reached — delete images to upload more</p>}
              {galleryNearLimit && !galleryAtLimit && <p className="text-xs text-amber-500 dark:text-amber-400 font-medium">Approaching limit</p>}
            </div>
          </div>
          <span className={`text-lg font-black ${galleryAtLimit ? 'text-red-600 dark:text-red-400' : galleryNearLimit ? 'text-amber-600 dark:text-amber-400' : 'text-gray-900 dark:text-white'}`}>
            {galleryUsed.toLocaleString()} <span className="text-sm font-semibold text-gray-400">/ {galleryLimit.toLocaleString()}</span>
          </span>
        </div>
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all duration-500 ${galleryAtLimit ? 'bg-red-500' : galleryNearLimit ? 'bg-amber-500' : 'bg-teal-500'}`}
            style={{ width: `${galleryPct}%` }}
          />
        </div>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-2 font-medium">{galleryLimit - galleryUsed > 0 ? `${(galleryLimit - galleryUsed).toLocaleString()} images remaining` : 'No storage remaining'} · across all projects</p>
      </motion.div>

      {renderActionItems()}
    </>
  );
  };


  const renderCalendar = () => {
    const today = new Date();

    const getProjectsForDate = (date: Date) => {
      return projects.filter(p => {
        if (!p.shoot_dates) return false;
        return p.shoot_dates.some((sd: any) => {
          if (!sd.start_date) return false;
          const sdDate = new Date(sd.start_date);
          return sdDate.toDateString() === date.toDateString();
        });
      });
    };

    const getEventsForDate = (date: Date) => {
      return calendarEvents.filter(e => {
        if (!e.start_time) return false;
        const eDate = new Date(e.start_time);
        return eDate.toDateString() === date.toDateString();
      });
    };

    const getRequestsForDate = (date: Date) => {
      return receivedRequests.filter(req => {
         if (!req.preferred_dates || req.preferred_dates.length === 0) return false;
         return req.preferred_dates.some((pd: string) => {
             const reqDate = new Date(pd);
             return reqDate.toDateString() === date.toDateString();
         });
      });
    };

    return (
      <div className="bg-white dark:bg-gray-900 p-4 md:p-8 rounded-[1.5rem] md:rounded-[3rem] border border-gray-100 dark:border-gray-800 shadow-sm overflow-hidden">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div>
            <h2 className="text-2xl font-black text-gray-900 dark:text-white">Shoot & Event Calendar</h2>
            <p className="text-sm text-gray-500 font-medium">{viewDate.toLocaleString('default', { month: 'long', year: 'numeric' })}</p>
          </div>
          <div className="flex items-center space-x-2">
            <button 
              onClick={() => {
                const newDate = new Date(viewDate);
                newDate.setMonth(newDate.getMonth() - 1);
                setViewDate(newDate);
              }}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors"
            >
              <div className="w-5 h-5 border-t-2 border-l-2 border-gray-400 rotate-[-45deg] ml-1" />
            </button>
            <button 
              onClick={() => setViewDate(new Date())}
              className="px-4 py-2 text-xs font-bold bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white rounded-xl transition-colors"
            >
              Today
            </button>
            <button 
              onClick={() => {
                const newDate = new Date(viewDate);
                newDate.setMonth(newDate.getMonth() + 1);
                setViewDate(newDate);
              }}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors"
            >
              <div className="w-5 h-5 border-t-2 border-r-2 border-gray-400 rotate-[45deg] mr-1" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-7 gap-px bg-gray-100 dark:bg-gray-800 rounded-2xl overflow-hidden border border-gray-100 dark:border-gray-800">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="bg-gray-50 dark:bg-gray-800/50 py-3 text-center text-[10px] font-black uppercase tracking-widest text-gray-400">
              {day}
            </div>
          ))}
          {calendarDays.map((date, i) => {
            const dateProjects = getProjectsForDate(date);
            const dateEvents = getEventsForDate(date);
            const dateRequests = getRequestsForDate(date);
            const isToday = date.toDateString() === today.toDateString();
            const isSameMonth = date.getMonth() === viewDate.getMonth();
            
            return (
              <div 
                key={i} 
                className={`min-h-[80px] md:min-h-[140px] bg-white dark:bg-gray-900 p-1 md:p-3 transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/20 cursor-pointer ${!isSameMonth ? 'opacity-30' : ''}`}
                onClick={() => {
                  if (dateProjects.length === 1 && dateEvents.length === 0) {
                    setSelectedProject(dateProjects[0]);
                  } else {
                    const offset = date.getTimezoneOffset()
                    const safeDate = new Date(date.getTime() - (offset*60*1000))
                    const dateStr = safeDate.toISOString().split('T')[0]
                    setNewEvent({ ...newEvent, start_time: dateStr + 'T09:00', end_time: dateStr + 'T10:00', is_all_day: false });
                    setShowAddEvent(true);
                  }
                }}
              >
                <div className="flex items-center justify-between mb-1 md:mb-2 pointer-events-none">
                  <span className={`text-[10px] md:text-xs font-bold ${isToday ? 'bg-teal-500 text-white w-5 h-5 md:w-6 md:h-6 flex items-center justify-center rounded-full' : 'text-gray-400'}`}>
                    {date.getDate()}
                  </span>
                </div>
                <div className="space-y-1">
                  {dateProjects.slice(0, 3).map(p => (
                    <button
                      key={p.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedProject(p);
                      }}
                      className="w-full text-left px-1.5 md:px-2 py-0.5 md:py-1 rounded-md md:rounded-lg bg-teal-50 dark:bg-teal-900/20 text-teal-600 dark:text-teal-400 text-[8px] md:text-[10px] font-black truncate hover:scale-[1.02] transition-transform"
                    >
                      📸 {p.title}
                    </button>
                  ))}
                  {dateEvents.slice(0, 3 - Math.min(dateProjects.length, 3)).map(e => (
                    <button
                       key={e.id}
                       onClick={(ev) => {
                           ev.stopPropagation();
                           setViewingCalendarEvent(e);
                       }}
                       title={e.is_all_day ? 'All Day' : `${new Date(e.start_time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} - ${new Date(e.end_time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}`}
                       className="w-full text-left px-1.5 md:px-2 py-0.5 md:py-1 rounded-md md:rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-[8px] md:text-[10px] font-bold truncate hover:bg-teal-50 hover:text-teal-500 transition-colors"
                    >
                        📅 {e.title}
                    </button>
                  ))}
                  {dateRequests.slice(0, 3 - Math.min(dateProjects.length + dateEvents.length, 3)).map(r => (
                    <button
                      key={r.id}
                      onClick={(ev) => {
                          ev.stopPropagation();
                          setSelectedRequest(r);
                      }}
                      className="w-full text-left px-1.5 md:px-2 py-0.5 md:py-1 rounded-md md:rounded-lg bg-yellow-50 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400 text-[8px] md:text-[10px] font-bold truncate hover:bg-yellow-100 dark:hover:bg-yellow-800 transition-colors"
                    >
                      📩 Request Received
                    </button>
                  ))}
                  {(dateProjects.length + dateEvents.length + dateRequests.length) > 3 && (
                    <p className="text-[8px] md:text-[9px] font-black text-gray-400 pl-1 pointer-events-none">
                        +{(dateProjects.length + dateEvents.length + dateRequests.length) - 3} more
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderContracts = () => {
    return (
      <div className="space-y-4">
        {contracts.length === 0 ? (
          <div className="bg-white dark:bg-gray-900 p-20 rounded-[3rem] border border-dashed border-gray-200 dark:border-gray-800 text-center">
            <DocumentTextIcon className="w-16 h-16 mx-auto text-gray-200 dark:text-gray-800 mb-4" />
            <h3 className="text-xl font-black text-gray-900 dark:text-white">No Contracts Generated</h3>
            <p className="text-gray-500 mt-2">Generate a contract inside any shoot project to see it here.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {contracts.map((contract) => (
              <div 
                key={contract.id} 
                className="bg-white dark:bg-gray-900 p-4 md:p-6 rounded-[1.5rem] md:rounded-[2rem] border border-gray-100 dark:border-gray-800 flex flex-col md:flex-row md:items-center justify-between gap-4 cursor-pointer hover:border-teal-500/30 transition-all font-bold group"
                onClick={() => {
                    const project = projects.find(p => p.id === contract.project_id);
                    if (project) {
                        setSelectedProject(project);
                        setViewingContract(contract);
                        setModalTab('contracts');
                    } else {
                        toast.error('Could not load parent project');
                    }
                }}
              >
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 rounded-2xl bg-purple-50 dark:bg-purple-900/20 flex items-center justify-center text-purple-600 font-bold text-xl">
                    <DocumentTextIcon className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 dark:text-white">{contract.title}</h4>
                    <p className="text-xs text-gray-400 font-medium">Created on {new Date(contract.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <span className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest ${
                    contract.status === 'signed' ? 'bg-teal-50 text-teal-600' :
                    contract.status === 'sent' ? 'bg-amber-50 text-amber-600' :
                    'bg-gray-50 text-gray-600'
                  }`}>
                    {contract.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (user && user.user_type !== 'hirer' && !user.is_profile_complete) {
    return (
      <div className="min-h-[80vh] bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full bg-white dark:bg-gray-800 rounded-3xl shadow-xl border border-gray-100 dark:border-gray-700 overflow-hidden"
        >
          <div className="bg-gray-900 dark:bg-gray-700 p-8 text-center text-white relative">
            <div className="absolute top-4 right-4">
              <span className="bg-amber-500/20 text-amber-400 text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full">
                Locked
              </span>
            </div>
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 rounded-full mb-4">
              <BriefcaseIcon className="w-8 h-8 text-amber-400" />
            </div>
            <h2 className="text-2xl font-black tracking-tight text-white">Unlock CRM Workspace</h2>
            <p className="text-xs text-gray-300 mt-2">
              Run your creative business like a pro. Manage clients, bookings, custom packages, and invoicing all in one place.
            </p>
          </div>

          <div className="p-6">
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-6 text-center">
              Please complete all items on your profile checklist to unlock the CRM workspace:
            </p>

            {(() => {
              const isFirstCompleted = (user.completion_score || 0) >= 60;
              return (
                <div className="space-y-3 mb-6">
                  {/* Profile Details */}
                  <button 
                    type="button"
                    onClick={() => navigate('/profile/edit')}
                    className="w-full flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900/50 rounded-2xl border border-gray-100 dark:border-gray-700 hover:border-primary-500 hover:bg-primary-50/20 text-left transition-all"
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 rounded-xl ${(user.completion_score || 0) >= 60 ? 'bg-teal-50 dark:bg-teal-900/20 text-teal-600' : 'bg-gray-100 dark:bg-gray-800 text-gray-400'}`}>
                        <IdentificationIcon className="w-5 h-5" />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-xs font-bold text-gray-700 dark:text-gray-200">
                          Profile Details Filled (Min 60%)
                        </span>
                        <span className="text-[10px] text-gray-400">Bio, location, sub-type, etc.</span>
                      </div>
                    </div>
                    {(user.completion_score || 0) >= 60 ? (
                      <CheckCircleIconSolid className="w-5 h-5 text-teal-500" />
                    ) : (
                      <span className="text-[10px] font-black text-amber-500">{(user.completion_score || 0)}% completed</span>
                    )}
                  </button>

                  {/* Profile Picture */}
                  <button 
                    type="button"
                    onClick={() => navigate('/profile?action=upload-photo')}
                    disabled={!!user.profile_pic || !isFirstCompleted}
                    className={`w-full flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900/50 rounded-2xl border border-gray-100 dark:border-gray-700 text-left transition-all ${
                      user.profile_pic
                        ? 'cursor-default'
                        : !isFirstCompleted
                          ? 'opacity-50 cursor-not-allowed'
                          : 'hover:border-primary-500 hover:bg-primary-50/20'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 rounded-xl ${user.profile_pic ? 'bg-teal-50 dark:bg-teal-900/20 text-teal-600' : 'bg-gray-100 dark:bg-gray-800 text-gray-400'}`}>
                        <FolderIcon className="w-5 h-5" />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-xs font-bold text-gray-700 dark:text-gray-200">
                          Profile Picture Uploaded
                        </span>
                        <span className="text-[10px] text-gray-400">Upload your profile photo</span>
                      </div>
                    </div>
                    {user.profile_pic ? (
                      <CheckCircleIconSolid className="w-5 h-5 text-teal-500" />
                    ) : (
                      <span className="text-[10px] font-black text-red-500">Pending</span>
                    )}
                  </button>

                  {/* Portfolio Item */}
                  <button 
                    type="button"
                    onClick={() => navigate('/profile?action=add-portfolio')}
                    disabled={(user.portfolio_items_count || 0) > 0 || !isFirstCompleted}
                    className={`w-full flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900/50 rounded-2xl border border-gray-100 dark:border-gray-700 text-left transition-all ${
                      (user.portfolio_items_count || 0) > 0
                        ? 'cursor-default'
                        : !isFirstCompleted
                          ? 'opacity-50 cursor-not-allowed'
                          : 'hover:border-primary-500 hover:bg-primary-50/20'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 rounded-xl ${(user.portfolio_items_count || 0) > 0 ? 'bg-teal-50 dark:bg-teal-900/20 text-teal-600' : 'bg-gray-100 dark:bg-gray-800 text-gray-400'}`}>
                        <PhotoIcon className="w-5 h-5" />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-xs font-bold text-gray-700 dark:text-gray-200">
                          At Least 1 Portfolio Item Added
                        </span>
                        <span className="text-[10px] text-gray-400">Showcase your best work</span>
                      </div>
                    </div>
                    {(user.portfolio_items_count || 0) > 0 ? (
                      <CheckCircleIconSolid className="w-5 h-5 text-teal-500" />
                    ) : (
                      <span className="text-[10px] font-black text-red-500">Pending</span>
                    )}
                  </button>

                  {/* Collaboration Project */}
                  <button 
                    type="button"
                    onClick={() => navigate('/profile?action=create-collaboration')}
                    disabled={(user.project_count || 0) > 0 || !isFirstCompleted}
                    className={`w-full flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900/50 rounded-2xl border border-gray-100 dark:border-gray-700 text-left transition-all ${
                      (user.project_count || 0) > 0
                        ? 'cursor-default'
                        : !isFirstCompleted
                          ? 'opacity-50 cursor-not-allowed'
                          : 'hover:border-primary-500 hover:bg-primary-50/20'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 rounded-xl ${(user.project_count || 0) > 0 ? 'bg-teal-50 dark:bg-teal-900/20 text-teal-600' : 'bg-gray-100 dark:bg-gray-800 text-gray-400'}`}>
                        <UsersIcon className="w-5 h-5" />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-xs font-bold text-gray-700 dark:text-gray-200">
                          At Least 1 Collaboration Project
                        </span>
                        <span className="text-[10px] text-gray-400">Post or join a creative project</span>
                      </div>
                    </div>
                    {(user.project_count || 0) > 0 ? (
                      <CheckCircleIconSolid className="w-5 h-5 text-teal-500" />
                    ) : (
                      <span className="text-[10px] font-black text-red-500">Pending</span>
                    )}
                  </button>
                </div>
              );
            })()}

            <div className="space-y-3">
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="w-full bg-transparent hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 font-bold py-3 px-6 rounded-xl transition-all text-sm text-center block"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8 md:mb-12">
        <div>
          <h1 className="text-4xl font-black text-gray-900 dark:text-white tracking-tight">
            Client <span className="text-teal-500">Hub</span>
          </h1>
          <p className="text-gray-500 dark:text-gray-400 font-medium mt-1">
            Manage your leads, clients, and photography workflows.
          </p>
        </div>
        <button 
          onClick={() => handleOpenAddLead()}
          className="flex items-center space-x-2 px-6 py-3 bg-black dark:bg-white text-white dark:text-black rounded-2xl font-bold shadow-lg shadow-black/10 transition-transform hover:scale-105 active:scale-95"
        >
          <PlusIcon className="w-5 h-5" />
          <span>Add Lead</span>
        </button>
      </header>

      {/* Tabs */}
      <div className="flex space-x-2 p-1.5 bg-gray-100/50 dark:bg-gray-800/50 rounded-2xl w-fit mb-8 md:mb-12 overflow-x-auto overflow-y-hidden scrollbar-hide max-w-full">
        {(['overview', 'leads', 'clients', 'projects', 'calendar', 'contracts', 'bookings'] as Tab[]).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-6 py-2.5 rounded-xl text-sm font-bold transition-all ${
              activeTab === tab 
                ? 'bg-white dark:bg-gray-700 text-black dark:text-white shadow-sm' 
                : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            {tab === 'bookings' ? 'My Bookings' : tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {loading ? (
          <motion.div 
            key="loader"
            className="flex flex-col items-center justify-center py-20"
          >
            <div className="w-10 h-10 border-4 border-teal-500/20 border-t-teal-500 rounded-full animate-spin mb-4" />
          </motion.div>
        ) : (
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
          >
            {activeTab === 'overview' && renderOverview()}
            {activeTab === 'calendar' && renderCalendar()}
            {activeTab === 'contracts' && renderContracts()}
            {activeTab === 'bookings' && <ClientPortal />}
            {activeTab === 'leads' && (
              <div className="space-y-4">
                {leads.length === 0 ? (
                  <div className="bg-white dark:bg-gray-900 p-20 rounded-[3rem] border border-dashed border-gray-200 dark:border-gray-800 text-center">
                    <FolderIcon className="w-16 h-16 mx-auto text-gray-200 dark:text-gray-800 mb-4" />
                    <h3 className="text-xl font-black text-gray-900 dark:text-white">No Leads Yet</h3>
                    <p className="text-gray-500 mt-2">Add your first lead to start tracking potential clients.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-4">
                    {leads.map((lead) => (
                      <div 
                        key={lead.id} 
                        onClick={() => {
                          setViewingLead(lead);
                          setEditedLead({ ...lead });
                          setIsEditingLead(false);
                        }}
                        className="bg-white dark:bg-gray-900 p-4 md:p-6 rounded-[1.5rem] md:rounded-[2rem] border border-gray-100 dark:border-gray-800 flex flex-col md:flex-row md:items-center justify-between gap-4 group cursor-pointer hover:border-teal-500/50 transition-all hover:shadow-lg"
                      >
                        <div className="flex items-center space-x-4">
                          <Avatar
                            src={lead.portal_user_profile_pic}
                            alt={lead.client_name}
                            size="lg"
                            shape="rounded"
                            className="cursor-pointer hover:opacity-85 transition-opacity ring-2 ring-white dark:ring-gray-800"
                            onClick={() => {
                              if (lead.portal_user_id) {
                                navigate(`/profile/${lead.portal_user_id}`);
                              }
                            }}
                          />
                          <div>
                            <div className="flex items-center gap-2">
                                <h4 
                                  onClick={(e) => {
                                    if (lead.portal_user_id) {
                                      e.stopPropagation();
                                      navigate(`/profile/${lead.portal_user_id}`);
                                    }
                                  }}
                                  className={`font-bold text-gray-900 dark:text-white ${lead.portal_user_id ? 'hover:text-indigo-600 dark:hover:text-indigo-400 cursor-pointer underline decoration-dotted' : ''}`}
                                >
                                  {lead.client_name}
                                </h4>
                                {lead.portal_user_id && (
                                    <span className="px-2 py-0.5 bg-indigo-500/10 text-indigo-500 rounded text-[9px] font-black uppercase tracking-widest border border-indigo-500/20">
                                        Portal User
                                    </span>
                                )}
                            </div>
                            <p className="text-xs text-gray-400 font-medium mb-1">{lead.client_email} • {lead.interest_type}</p>
                            <div className="flex items-center gap-2 text-[10px] text-gray-400">
                                <span>Created: {new Date(lead.created_at).toLocaleDateString()}</span>
                                <span>Updated: {new Date(lead.updated_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-4">
                           <span className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest ${
                            lead.status === 'confirmed' ? 'bg-blue-50 text-blue-600' :
                            lead.status === 'contacted' ? 'bg-amber-50 text-amber-600' :
                            lead.status === 'proposal' ? 'bg-purple-50 text-purple-650' :
                            lead.status === 'converted' ? 'bg-teal-50 text-teal-600' :
                            lead.status === 'pending_confirmation' ? 'bg-orange-50 text-orange-600' :
                            lead.status === 'declined' ? 'bg-rose-50 text-rose-600' :
                            'bg-gray-50 text-gray-600'
                          }`}>
                            {lead.status === 'pending_confirmation' ? 'pending confirmation' : lead.status}
                           </span>
                           {lead.portal_user_id && (
                             <button
                               onClick={(e) => {
                                 e.stopPropagation();
                                 navigate('/messages', {
                                   state: {
                                     openConversationWith: lead.portal_user_id,
                                     crm_lead_id: lead.id,
                                     userName: lead.client_name,
                                     userType: 'hirer',
                                     prefilledMessage: `Hey ${lead.client_name}, let's chat about your proposal for the ${lead.interest_type || 'shoot'}.`
                                   }
                                 });
                               }}
                               className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-teal-500 hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors cursor-pointer"
                               title="Chat about Proposal"
                             >
                               <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                               </svg>
                             </button>
                           )}
                           <button 
                             onClick={(e) => {
                               e.stopPropagation();
                               setConfirmModalState({
                                 title: 'Delete Lead',
                                 message: 'Are you sure you want to delete this lead?',
                                 isDestructive: true,
                                 confirmText: 'Delete',
                                 cancelText: 'Cancel',
                                 onConfirm: () => {
                                   crmAPI.deleteLead(lead.id).then(() => {
                                     toast.success('Lead deleted');
                                     fetchData();
                                     setShowConfirmModal(false);
                                   }).catch(err => toast.error('Failed to delete lead'));
                                 }
                               });
                               setShowConfirmModal(true);
                             }}
                             className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer mr-2"
                             title="Delete Lead"
                           >
                             <TrashIcon className="w-4 h-4" />
                           </button>
                          <div className="relative" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
                            <select
                              value={lead.status}
                              onChange={(e) => {
                                e.stopPropagation();
                                updateLeadStatus(lead.id, e.target.value, lead);
                              }}
                              className="text-[10px] font-black uppercase tracking-widest bg-transparent border-none focus:ring-0 text-gray-400 cursor-pointer appearance-none pr-4 relative z-10"
                            >
                              {lead.status === 'pending_confirmation' && (
                                <option value="pending_confirmation">PENDING CONFIRMATION</option>
                              )}
                              {lead.status === 'declined' && (
                                <option value="declined">DECLINED</option>
                              )}
                              <option value="contacted">CONTACTED</option>
                              <option value="proposal">PROPOSAL</option>
                              <option value="confirmed" disabled={lead.status === 'pending_confirmation'}>CONFIRMED</option>
                              <option value="converted" disabled={!lead.is_confirmed}>CONVERTED</option>
                              <option value="lost">LOST</option>
                            </select>
                            <ChevronDownIcon className="w-3 h-3 text-gray-400 absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {activeTab === 'projects' && (
              <div className="space-y-6">
                <div className="flex space-x-2 p-1 bg-gray-100/80 dark:bg-gray-800/80 rounded-xl w-fit">
                  <button
                    onClick={() => setProjectsTab('active')}
                    className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${
                      projectsTab === 'active' 
                        ? 'bg-white dark:bg-gray-700 text-black dark:text-white shadow-sm' 
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Active Projects ({projects.filter(p => p.status !== 'completed' && p.status !== 'cancelled').length})
                  </button>
                  <button
                    onClick={() => setProjectsTab('history')}
                    className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${
                      projectsTab === 'history' 
                        ? 'bg-white dark:bg-gray-700 text-black dark:text-white shadow-sm' 
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Past Projects ({projects.filter(p => p.status === 'completed' || p.status === 'cancelled').length})
                  </button>
                </div>

                {(() => {
                  const filteredProjects = projectsTab === 'active' 
                    ? projects.filter(p => p.status !== 'completed' && p.status !== 'cancelled')
                    : projects.filter(p => p.status === 'completed' || p.status === 'cancelled');

                  if (filteredProjects.length === 0) {
                    return (
                      <div className="bg-white dark:bg-gray-900 p-20 rounded-[3rem] border border-dashed border-gray-200 dark:border-gray-800 text-center">
                        <BriefcaseIcon className="w-16 h-16 mx-auto text-gray-200 dark:text-gray-800 mb-4" />
                        <h3 className="text-xl font-black text-gray-900 dark:text-white">No {projectsTab === 'active' ? 'Active' : 'Past'} Projects</h3>
                        <p className="text-gray-500 mt-2">
                          {projectsTab === 'active' 
                            ? "Convert a lead or add a project to start tracking your active shoots." 
                            : "No completed or cancelled projects found."}
                        </p>
                      </div>
                    );
                  }

                  return (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {filteredProjects.map((project) => (
                        <div 
                          key={project.id} 
                          onClick={() => {
                            setSelectedProject(project);
                            setModalTab('overview');
                          }}
                          className="bg-white dark:bg-gray-900 p-6 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm hover:shadow-md hover:border-teal-500/30 cursor-pointer transition-all duration-300 group flex flex-col justify-between"
                        >
                          <div>
                            <div className="flex justify-between items-center mb-3">
                              <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                                project.status === 'booked' ? 'bg-blue-50/80 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400' :
                                project.status === 'shooting' ? 'bg-amber-50/80 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400' :
                                project.status === 'editing' ? 'bg-purple-50/80 text-purple-600 dark:bg-purple-900/20 dark:text-purple-400' :
                                project.status === 'completed' ? 'bg-teal-50/80 text-teal-600 dark:bg-teal-900/20 dark:text-teal-400' :
                                'bg-gray-50/80 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                              }`}>
                                {project.status}
                              </span>
                              <span className="text-lg font-bold text-gray-900 dark:text-white">₹{project.total_amount.toLocaleString('en-IN')}</span>
                            </div>
                            
                            <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-2 group-hover:text-teal-500 transition-colors line-clamp-1">
                              {project.title}
                            </h4>
                            
                            <div className="flex items-center flex-wrap gap-2 text-sm text-gray-500 dark:text-gray-400 mb-5 font-normal">
                              <span className="flex items-center gap-1.5">
                                Client: 
                                {project.portal_user_id ? (
                                   <Avatar
                                     src={project.portal_user_profile_pic}
                                     alt={project.client_name}
                                     size="xs"
                                     className="cursor-pointer hover:opacity-85 transition-opacity"
                                     onClick={() => navigate(`/profile/${project.portal_user_id}`)}
                                   />
                                 ) : null}
                                <span 
                                  onClick={(e) => {
                                    if (project.portal_user_id) {
                                      e.stopPropagation();
                                      navigate(`/profile/${project.portal_user_id}`);
                                    }
                                  }}
                                  className={`font-semibold text-gray-700 dark:text-gray-300 ${project.portal_user_id ? 'hover:text-indigo-600 dark:hover:text-indigo-400 cursor-pointer underline decoration-dotted' : ''}`}
                                >
                                  {project.client_name}
                                </span>
                              </span>
                              {project.location_name && (
                                <>
                                  <span className="text-gray-300 dark:text-gray-700">•</span>
                                  <span className="bg-teal-50 dark:bg-teal-900/20 text-teal-600 dark:text-teal-400 px-2 py-0.5 rounded text-[10px] font-bold uppercase">
                                    {project.location_name}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between pt-4 border-t border-gray-100 dark:border-gray-800/80 gap-3 sm:gap-0">
                            <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 font-medium">
                              <CalendarDaysIcon className="w-4 h-4 mr-1.5 text-gray-400 shrink-0" />
                              <span>
                                {project.shoot_dates?.length > 1 
                                  ? `${project.shoot_dates.length} Scheduled Dates` 
                                  : project.shoot_dates?.[0] 
                                    ? (project.shoot_dates[0].is_range 
                                      ? `${new Date(project.shoot_dates[0].start_date).toLocaleDateString()} - ${new Date(project.shoot_dates[0].end_date).toLocaleDateString()}`
                                      : new Date(project.shoot_dates[0].start_date).toLocaleDateString())
                                    : project.shoot_date 
                                      ? new Date(project.shoot_date).toLocaleDateString() 
                                      : 'No Date Set'}
                              </span>
                            </div>
                            
                            <div className="flex items-center gap-3 justify-between sm:justify-end w-full sm:w-auto">
                              <div className="flex items-center text-[10px] text-gray-600 dark:text-gray-300 font-bold uppercase tracking-wider bg-gray-50 dark:bg-gray-800 px-2.5 py-1 rounded-lg border border-gray-100 dark:border-gray-700 shrink-0">
                                <PhotoIcon className="w-3.5 h-3.5 mr-1 text-gray-400" />
                                <span>{project.images_count || 0} images</span>
                              </div>
                              <div className="flex items-center gap-3">
                                {project.portal_user_id && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate('/messages', {
                                        state: {
                                          openConversationWith: project.portal_user_id,
                                          crm_project_id: project.id,
                                          crm_lead_id: project.lead_id,
                                          userName: project.client_name,
                                          userType: 'hirer',
                                          prefilledMessage: `Hey ${project.client_name}, let's chat about our project: ${project.title}.`
                                        }
                                      });
                                    }}
                                    className="p-1 text-gray-400 hover:text-teal-500 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded-lg transition-colors cursor-pointer shrink-0"
                                    title="Chat about Project"
                                  >
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                    </svg>
                                  </button>
                                )}
                                <span className="text-xs font-bold text-teal-600 hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-300 uppercase tracking-wider flex items-center shrink-0">
                                  View Details →
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            )}
            {activeTab === 'clients' && (
               <div className="bg-white dark:bg-gray-900 p-6 md:p-12 lg:p-20 rounded-[2rem] md:rounded-[3rem] border border-gray-100 dark:border-gray-800 text-center shadow-sm">
                 <IdentificationIcon className="w-12 h-12 md:w-16 md:h-16 mx-auto text-gray-200 dark:text-gray-800 mb-4" />
                 <h3 className="text-xl font-black text-gray-900 dark:text-white">Client List</h3>
                 <div className="grid grid-cols-1 gap-2 mt-8 max-w-md mx-auto">
                    {clients.map(client => (
                        <div key={client.id} className="flex items-center space-x-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-2xl text-left border border-gray-100 dark:border-gray-700">
                             <Avatar
                               src={client.portal_user_profile_pic}
                               alt={`${client.first_name} ${client.last_name}`}
                               size="md"
                               shape="rounded"
                               className="cursor-pointer hover:opacity-85 transition-opacity ring-2 ring-white dark:ring-gray-800"
                               onClick={() => {
                                 if (client.portal_user_id) {
                                   navigate(`/profile/${client.portal_user_id}`);
                                 }
                               }}
                             />
                             <div className="flex-1 min-w-0">
                                  <div className="flex items-center flex-wrap gap-1.5 mb-0.5">
                                      <span 
                                        onClick={(e) => {
                                          if (client.portal_user_id) {
                                            e.stopPropagation();
                                            navigate(`/profile/${client.portal_user_id}`);
                                          }
                                        }}
                                        className={`font-bold text-sm text-gray-900 dark:text-white truncate ${client.portal_user_id ? 'hover:text-indigo-600 dark:hover:text-indigo-400 cursor-pointer underline decoration-dotted' : ''}`}
                                      >
                                        {client.first_name} {client.last_name}
                                      </span>
                                      {client.portal_user_id && (
                                        <span className="px-1.5 py-0.5 bg-indigo-500/10 text-indigo-500 rounded text-[9px] font-black uppercase tracking-widest border border-indigo-500/20 shrink-0">
                                          Portal User
                                        </span>
                                      )}
                                  </div>
                                  <p className="text-xs text-gray-400 truncate">{client.email}</p>
                             </div>
                            <button 
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setConfirmModalState({
                                        title: 'Delete Client',
                                        message: 'Are you sure you want to delete this client? This will unlink their currently tracked projects.',
                                        isDestructive: true,
                                        confirmText: 'Delete',
                                        cancelText: 'Cancel',
                                        onConfirm: () => {
                                            crmAPI.deleteClient(client.id).then(() => {
                                                toast.success('Client deleted');
                                                fetchData();
                                                setShowConfirmModal(false);
                                            }).catch(err => toast.error('Failed to delete client'));
                                        }
                                    });
                                    setShowConfirmModal(true);
                                }}
                                className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                            >
                                <TrashIcon className="w-4 h-4" />
                            </button>
                        </div>
                    ))}
                 </div>
               </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {selectedRequest && (
          <RequestDetailModal
            request={selectedRequest}
            onClose={() => setSelectedRequest(null)}
            onResponse={async (requestId, status) => {
              try {
                await collaborationAPI.respondToCollaborationRequest(requestId, { status });
                toast.success(`Request ${status}`);
                setSelectedRequest(null);
                fetchData(); // Refresh everything to move the lead to converted and create the project
              } catch (err: any) {
                toast.error(err.response?.data?.error || `Failed to update request`);
              }
            }}
            isReceived={true}
          />
        )}
      </AnimatePresence>

      {/* Project Details Modal */}
      <AnimatePresence>
        {selectedProject && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 min-h-screen">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedProject(null)}
              className="absolute inset-0 bg-black/60 backdrop-blur-md"
            />
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative w-full max-w-2xl bg-white dark:bg-gray-900 rounded-[2rem] sm:rounded-[3rem] overflow-hidden shadow-2xl p-6 sm:p-10 max-h-[92vh] sm:max-h-[90vh] overflow-y-auto"
            >
                {/* Cancellation Warning Banner */}
                {selectedProject.status !== 'cancelled' && 
                 selectedProject.collaboration && 
                 selectedProject.collaboration.status === 'cancelled' && 
                 (!selectedProject.cancelled_collab_alert_dismissed_at || 
                  new Date(selectedProject.collaboration.updated_at) > new Date(selectedProject.cancelled_collab_alert_dismissed_at)) && (
                    <div className="mb-6 p-4 bg-rose-50 dark:bg-rose-950/20 border border-rose-100 dark:border-rose-900/30 rounded-2xl flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex items-start space-x-3">
                        <div className="p-2 bg-rose-100 dark:bg-rose-900/50 text-rose-600 dark:text-rose-400 rounded-xl mt-0.5 shrink-0">
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                        </div>
                        <div>
                          <h4 className="text-xs font-black uppercase tracking-wider text-rose-800 dark:text-rose-300">Client Cancelled Collaboration</h4>
                          <p className="text-xs text-rose-600 dark:text-rose-400 mt-1">
                            The client has cancelled the linked request. Reason: <strong>{selectedProject.collaboration.cancellation_reason || 'Not provided'}</strong>
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 shrink-0 self-end md:self-auto">
                        <button
                          onClick={() => updateProjectStatus(selectedProject.id, 'cancelled')}
                          className="px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-[10px] font-black uppercase tracking-widest transition-all"
                        >
                          Cancel CRM Project
                        </button>
                        <button
                          onClick={async () => {
                            try {
                              await crmAPI.dismissCancelledCollabAlert(selectedProject.id);
                              setSelectedProject((prev: any) => prev ? { ...prev, cancelled_collab_alert_dismissed_at: new Date().toISOString() } : null);
                            } catch (err) {
                              toast.error("Failed to dismiss warning");
                            }
                          }}
                          className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all"
                        >
                          Dismiss
                        </button>
                      </div>
                    </div>
                )}

                {/* Completion Warning Banner */}
                {selectedProject.status !== 'completed' && 
                 selectedProject.status !== 'cancelled' && 
                 selectedProject.collaboration && 
                 selectedProject.collaboration.sender_marked_complete && 
                 (!selectedProject.completed_collab_alert_dismissed_at || 
                  new Date(selectedProject.collaboration.updated_at) > new Date(selectedProject.completed_collab_alert_dismissed_at)) && (
                    <div className="mb-6 p-4 bg-teal-50 dark:bg-teal-950/20 border border-teal-100 dark:border-teal-900/30 rounded-2xl flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex items-start space-x-3">
                        <div className="p-2 bg-teal-100 dark:bg-teal-900/50 text-teal-600 dark:text-teal-400 rounded-xl mt-0.5 shrink-0">
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <div>
                          <h4 className="text-xs font-black uppercase tracking-wider text-teal-800 dark:text-teal-300">Client Completed Collaboration</h4>
                          <p className="text-xs text-teal-600 dark:text-teal-400 mt-1">
                            The client has marked the linked request as completed.
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 shrink-0 self-end md:self-auto">
                        <button
                          onClick={() => updateProjectStatus(selectedProject.id, 'completed')}
                          className="px-3 py-1.5 bg-teal-600 hover:bg-teal-700 text-white rounded-xl text-[10px] font-black uppercase tracking-widest transition-all"
                        >
                          Mark Completed
                        </button>
                        <button
                          onClick={async () => {
                            try {
                              await crmAPI.dismissCompletedCollabAlert(selectedProject.id);
                              setSelectedProject((prev: any) => prev ? { ...prev, completed_collab_alert_dismissed_at: new Date().toISOString() } : null);
                            } catch (err) {
                              toast.error("Failed to dismiss warning");
                            }
                          }}
                          className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all"
                        >
                          Dismiss
                        </button>
                      </div>
                    </div>
                )}

                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-3 mb-2 flex-wrap gap-y-2">
                            <h2 className="text-2xl sm:text-3xl font-black text-gray-900 dark:text-white break-words">{selectedProject.title}</h2>
                            {selectedProject.status !== 'cancelled' && (
                                <button 
                                    onClick={() => {
                                        setIsEditingProject(true);
                                        setNewProject({
                                            title: selectedProject.title,
                                            description: selectedProject.description || '',
                                            location: selectedProject.location || '',
                                            location_name: selectedProject.location_name || '',
                                            latitude: selectedProject.latitude,
                                            longitude: selectedProject.longitude,
                                            total_amount: selectedProject.total_amount.toString(),
                                            shoot_dates: selectedProject.shoot_dates.map((sd: any) => ({
                                                start_date: new Date(sd.start_date).toISOString().split('T')[0],
                                                end_date: new Date(sd.end_date).toISOString().split('T')[0],
                                                is_range: sd.is_range,
                                                location: sd.location,
                                                location_name: sd.location_name,
                                                latitude: sd.latitude,
                                                longitude: sd.longitude
                                            })),
                                            retainer_amount: (selectedProject.retainer_amount || 0).toString(),
                                            retainer_paid: selectedProject.retainer_paid || false,
                                            collaboration_id: selectedProject.collaboration_id || null,
                                            usage_rights: selectedProject.usage_rights || '',
                                            wardrobe_notes: selectedProject.wardrobe_notes || ''
                                        });
                                        setShowConvertLead({ client_name: selectedProject.client_name, id: selectedProject.id });
                                    }}
                                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-all text-gray-400 hover:text-teal-500"
                                    title="Edit Project"
                                >
                                    <PencilIcon className="w-5 h-5" />
                                </button>
                            )}
                        </div>
                        <p className="text-gray-500 font-medium">Session with {selectedProject.client_name}</p>
                    </div>
                    <div className="flex flex-col items-start sm:items-end gap-2 w-full sm:w-auto">
                        <div className="relative w-full sm:w-auto">
                            <select 
                                value={selectedProject.status}
                                onChange={(e) => updateProjectStatus(selectedProject.id, e.target.value)}
                                disabled={selectedProject.status === 'cancelled'}
                                className={`w-full sm:w-auto bg-black dark:bg-white text-white dark:text-black pl-4 pr-10 py-2 rounded-xl text-xs font-black uppercase tracking-widest border-none focus:ring-0 appearance-none relative z-10 ${selectedProject.status === 'cancelled' ? 'cursor-not-allowed opacity-55' : 'cursor-pointer'}`}
                            >
                                <option value="booked">Booked</option>
                                <option value="shooting">Shooting</option>
                                <option value="editing">Editing</option>
                                <option value="proofing">Proofing</option>
                                <option value="completed">Completed</option>
                                <option value="cancelled">Cancelled</option>
                            </select>
                            <ChevronDownIcon className="w-4 h-4 text-white dark:text-black absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none z-20" />
                        </div>
                        {/* Sync to Community - Commented out for now
                        {selectedProject.status === 'completed' && !selectedProject.posted_as_collaboration && selectedProject.gallery_status === 'published' && (
                            <button
                                onClick={() => {
                                    setProjectToSync(selectedProject);
                                    setShowSyncPrompt(true);
                                }}
                                className="px-3 py-1.5 bg-gradient-to-r from-teal-500 to-blue-500 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:opacity-90 transition-all shadow-sm"
                            >
                                Sync to Community
                            </button>
                        )}
                        {selectedProject.posted_as_collaboration && (
                            <span className="px-3 py-1.5 bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 rounded-xl text-[10px] font-black uppercase tracking-widest">
                                Synced ✓
                            </span>
                        )}
                        */}
                    </div>
                </div>

                <div className="flex border-b border-gray-100 dark:border-gray-800 mb-8 space-x-8 overflow-x-auto scrollbar-none whitespace-nowrap">
                    <button 
                        onClick={() => setModalTab('overview')}
                        className={`pb-4 text-xs font-black uppercase tracking-widest transition-all shrink-0 ${modalTab === 'overview' ? 'text-teal-500 border-b-2 border-teal-500' : 'text-gray-400 hover:text-gray-600'}`}
                    >
                        Overview
                    </button>
                    <button 
                        onClick={() => setModalTab('gallery')}
                        className={`pb-4 text-xs font-black uppercase tracking-widest transition-all shrink-0 ${modalTab === 'gallery' ? 'text-teal-500 border-b-2 border-teal-500' : 'text-gray-400 hover:text-gray-600'}`}
                    >
                        Gallery
                    </button>
                    <button 
                        onClick={() => setModalTab('invoices')}
                        className={`pb-4 text-xs font-black uppercase tracking-widest transition-all shrink-0 ${modalTab === 'invoices' ? 'text-teal-500 border-b-2 border-teal-500' : 'text-gray-400 hover:text-gray-600'}`}
                    >
                        Invoices
                    </button>
                    <button 
                        onClick={() => setModalTab('contracts')}
                        className={`pb-4 text-xs font-black uppercase tracking-widest transition-all shrink-0 ${modalTab === 'contracts' ? 'text-teal-500 border-b-2 border-teal-500' : 'text-gray-400 hover:text-gray-600'}`}
                    >
                        Contracts
                    </button>
                </div>

                {modalTab === 'overview' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10 items-start">
                        {/* 1. Project Description */}
                        {selectedProject.description && (
                            <section className="order-1 md:col-start-1 md:row-start-1 bg-white dark:bg-gray-900/50 p-6 rounded-[2rem] border border-gray-100 dark:border-gray-800">
                                <h4 className="text-xs font-black uppercase tracking-widest text-gray-400 mb-3">Project Description</h4>
                                <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                                    {selectedProject.description}
                                </p>
                            </section>
                        )}

                        {/* 2-5. Financial, Locations, Dates, Access Code */}
                        <section className="order-2 md:col-start-2 md:row-start-1 md:row-span-3 bg-gray-50 dark:bg-gray-800/50 rounded-[2rem] p-5 flex flex-col space-y-6 h-full">
                            <div>
                                <h4 className="text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-2.5 ml-1">Financial Highlights</h4>
                                <div className="bg-white dark:bg-gray-900 rounded-2xl p-5 border border-gray-100 dark:border-gray-800/50 shadow-sm">
                                    <div className="space-y-4">
                                        <div className="flex items-center">
                                            <div className="w-11 h-11 rounded-xl bg-teal-50 dark:bg-teal-500/10 flex items-center justify-center mr-3.5 border border-teal-100 dark:border-teal-500/20 flex-shrink-0">
                                                <BanknotesIcon className="w-5 h-5 text-teal-600 dark:text-teal-400" />
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-0.5">Total Project Value</p>
                                                <p className="text-xl font-black text-gray-900 dark:text-white">
                                                    {selectedProject.total_amount === 0 ? 'Free Collab' : `₹${selectedProject.total_amount}`}
                                                </p>
                                            </div>
                                        </div>

                                        {selectedProject.total_amount > 0 && selectedProject.retainer_amount > 0 && (
                                            <div className="pt-3 border-t border-gray-50 dark:border-gray-800/50 flex items-center justify-between">
                                                <div className="flex items-center">
                                                    <div className="w-9 h-9 rounded-lg bg-orange-50 dark:bg-orange-500/10 flex items-center justify-center mr-3 border border-orange-100 dark:border-orange-500/20 flex-shrink-0">
                                                        <BanknotesIcon className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                                                    </div>
                                                    <div>
                                                        <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest mb-0.5">Advance Payment</p>
                                                        <p className="text-sm font-black text-gray-900 dark:text-white">₹{selectedProject.retainer_amount}</p>
                                                    </div>
                                                </div>
                                                {selectedProject.retainer_paid ? (
                                                    <CheckCircleIconSolid className="w-4 h-4 text-teal-600 dark:text-teal-400" />
                                                ) : (
                                                    <span className="text-[8px] font-black uppercase px-2 py-0.5 rounded-full bg-orange-500 text-white shadow-sm">
                                                        Pending
                                                    </span>
                                                )}
                                            </div>
                                        )}

                                        {selectedProject.total_amount > 0 && (selectedProject.amount_paid > 0 || selectedProject.retainer_paid) && (() => {
                                            const totalPaid = Math.min(selectedProject.total_amount, (selectedProject.amount_paid || 0) + (selectedProject.retainer_paid ? (selectedProject.retainer_amount || 0) : 0));
                                            const remainingBalance = Math.max(0, Math.round((selectedProject.total_amount - totalPaid) * 100) / 100);
                                            const isFullyPaid = remainingBalance <= 0;

                                            return (
                                                <div className="flex items-center justify-between pt-3 border-t border-gray-50 dark:border-gray-800/50">
                                                    <div className="flex items-center gap-1.5">
                                                        <span className={`w-1.5 h-1.5 rounded-full ${isFullyPaid ? 'bg-teal-500' : 'bg-rose-500 animate-pulse'}`}></span>
                                                        <p className="text-[9px] font-bold text-gray-500 uppercase tracking-wider">Remaining Balance</p>
                                                    </div>
                                                    <p className={`text-base font-black ${isFullyPaid ? 'text-teal-600 dark:text-teal-400' : 'text-rose-500'}`}>
                                                        {isFullyPaid ? 'Paid in Full (₹0)' : `₹${remainingBalance.toLocaleString('en-IN')}`}
                                                    </p>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </div>
                            </div>

                            <div>
                                <h4 className="text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-2.5 ml-1">Default Location</h4>
                                <a 
                                    href={getMapsLink(selectedProject.latitude, selectedProject.longitude, selectedProject.location)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center group bg-white dark:bg-gray-900 p-4 rounded-xl border border-gray-100 dark:border-gray-800/50 shadow-sm transition-all hover:border-teal-500/30"
                                >
                                    <div className="w-9 h-9 rounded-lg bg-gray-50 dark:bg-gray-800 flex items-center justify-center mr-3 flex-shrink-0 group-hover:bg-teal-50 dark:group-hover:bg-teal-900/20 transition-colors">
                                        <MapPinIcon className="w-4 h-4 text-gray-400 group-hover:text-teal-500 transition-colors" />
                                    </div>
                                    <div className="flex-1 min-w-0 pr-2">
                                        {selectedProject.location_name && (
                                            <span className="text-[9px] font-black uppercase tracking-widest text-teal-600 block mb-0.5">
                                                {selectedProject.location_name}
                                            </span>
                                        )}
                                        <span className="text-sm font-bold text-gray-800 dark:text-gray-200 truncate block">
                                            {selectedProject.location || 'Studio'}
                                        </span>
                                    </div>
                                    <ArrowTopRightOnSquareIcon className="w-4 h-4 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                                </a>
                            </div>

                            <div>
                                <h4 className="text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-2.5 ml-1">Session Dates</h4>
                                <div className="space-y-2">
                                    {selectedProject.shoot_dates && selectedProject.shoot_dates.length > 0 ? (
                                        selectedProject.shoot_dates.map((sd: any, i: number) => (
                                            <div key={i} className="bg-white dark:bg-gray-900 p-4 rounded-xl border border-gray-100 dark:border-gray-800/50 shadow-sm flex flex-col space-y-2">
                                                <div className="flex items-center space-x-2.5 text-sm font-bold text-gray-900 dark:text-white">
                                                    <CalendarDaysIcon className="w-4 h-4 text-teal-500" />
                                                    <span>
                                                        {sd.is_range 
                                                            ? `${new Date(sd.start_date).toLocaleDateString()} - ${new Date(sd.end_date).toLocaleDateString()}`
                                                            : new Date(sd.start_date).toLocaleDateString()}
                                                    </span>
                                                </div>
                                                {(sd.location_name || sd.location || selectedProject.location) && (
                                                    <div className="pt-2 border-t border-gray-50 dark:border-gray-800/50">
                                                        <a 
                                                            href={getMapsLink(sd.latitude || selectedProject.latitude, sd.longitude || selectedProject.longitude, sd.location || selectedProject.location)}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="hover:text-teal-500 transition-all flex items-start group"
                                                        >
                                                            <MapPinIcon className="w-3 h-3 mr-1.5 mt-0.5 flex-shrink-0 text-gray-400 group-hover:text-teal-500" />
                                                            <div className="flex flex-col min-w-0">
                                                                <span className="font-black text-[8px] uppercase tracking-wider text-teal-600 leading-tight">
                                                                    {sd.location_name || selectedProject.location_name || 'Location'}
                                                                </span>
                                                                <span className="truncate max-w-[170px] leading-tight text-[11px] text-gray-500 dark:text-gray-400 flex items-center font-medium mt-0.5">
                                                                    {sd.location || selectedProject.location}
                                                                    <ArrowTopRightOnSquareIcon className="w-2.5 h-2.5 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                                                                </span>
                                                            </div>
                                                        </a>
                                                    </div>
                                                )}
                                            </div>
                                        ))
                                    ) : (
                                        <div className="bg-white dark:bg-gray-900 p-4 rounded-xl border border-gray-100 dark:border-gray-800/50 shadow-sm flex items-center space-x-2 text-sm font-bold text-gray-900 dark:text-white">
                                            <CalendarDaysIcon className="w-4 h-4 text-gray-400" />
                                            <span>
                                                {selectedProject.shoot_date ? new Date(selectedProject.shoot_date).toLocaleDateString() : 'TBD'}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {isModel && selectedProject.usage_rights && (
                                <div>
                                    <h4 className="text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-2 ml-1">Usage Rights</h4>
                                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-900 p-4 rounded-xl border border-gray-100 dark:border-gray-800/50 shadow-sm">
                                        {selectedProject.usage_rights}
                                    </p>
                                </div>
                            )}

                            {isModel && selectedProject.wardrobe_notes && (
                                <div>
                                    <h4 className="text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-2 ml-1">Wardrobe / Looks</h4>
                                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-900 p-4 rounded-xl border border-gray-100 dark:border-gray-800/50 shadow-sm">
                                        {selectedProject.wardrobe_notes}
                                    </p>
                                </div>
                            )}

                            {selectedProject.gallery_password && (
                                <div>
                                    <h4 className="text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-2.5 ml-1">Access Code</h4>
                                    <div className="flex items-center justify-between p-4 bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800/50 shadow-sm text-sm font-semibold text-gray-400">
                                        <span>Secured (Client Portal Only)</span>
                                    </div>
                                </div>
                            )}
                        </section>

                        {/* 6. Checklist */}
                        <section className="order-3 md:col-start-1 md:row-start-2">
                            <h4 className="text-xs font-black uppercase tracking-widest text-gray-400 mb-4 ml-1">Workflow Checklist</h4>
                            <div className="space-y-3">
                                {JSON.parse(selectedProject.checklist || JSON.stringify(getRoleBasedChecklist(user?.user_type || 'creator'))).map((item: any) => (
                                    <label key={item.id} className="flex items-center space-x-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-2xl cursor-pointer group transition-all hover:bg-teal-50 dark:hover:bg-teal-900/10">
                                        <input 
                                            type="checkbox" 
                                            checked={item.completed}
                                            onChange={() => toggleChecklistItem(selectedProject, item.id)}
                                            className="w-5 h-5 rounded border-gray-200 text-teal-500 focus:ring-teal-500 transition-all cursor-pointer"
                                        />
                                        <span className={`text-sm font-bold ${item.completed ? 'text-gray-400 line-through' : 'text-gray-700 dark:text-gray-200'}`}>
                                            {item.label}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </section>
                        
                        {/* 7. Timeline */}
                        <section className="order-4 md:col-start-1 md:row-start-3">
                            <h4 className="text-xs font-black uppercase tracking-widest text-gray-400 mb-4 ml-1">Timeline</h4>
                            <div className="space-y-4 max-h-[300px] overflow-y-auto pr-2">
                                {activities.length === 0 ? (
                                    <p className="text-xs text-gray-400 font-medium ml-1">No activities recorded yet.</p>
                                ) : (
                                    activities.map((activity, i) => (
                                        <div key={activity.id} className="relative pl-6 pb-4 last:pb-0">
                                            {i !== activities.length - 1 && (
                                                <div className="absolute left-[7px] top-4 bottom-0 w-[2px] bg-gray-100 dark:bg-gray-800" />
                                            )}
                                            <div className="absolute left-0 top-1 w-4 h-4 rounded-full bg-white dark:bg-gray-900 border-2 border-teal-500" />
                                            <p className="text-xs font-bold text-gray-700 dark:text-gray-300">{activity.description}</p>
                                            <p className="text-[10px] text-gray-400 mt-1 font-medium">{new Date(activity.created_at).toLocaleString()}</p>
                                        </div>
                                    ))
                                )}
                            </div>
                        </section>
                    </div>
                )}

                {modalTab === 'invoices' && (
                    <div className="space-y-8 mb-10">
                        <div className="flex justify-between items-center">
                            <h4 className="text-xs font-black uppercase tracking-widest text-gray-400 ml-1">Invoices & Billing</h4>
                            <button 
                                onClick={() => checkBusinessDetails() && setShowAddInvoice(true)}
                                className="px-6 py-2.5 bg-black dark:bg-white text-white dark:text-black rounded-xl text-[10px] font-black uppercase tracking-widest hover:opacity-90 transition-all font-mono"
                            >
                                Create Invoice
                            </button>
                        </div>

                        <div className="space-y-4">
                            {projectInvoices.length === 0 ? (
                                <div className="p-20 bg-gray-50 dark:bg-gray-800 rounded-[3rem] text-center border border-dashed border-gray-200 dark:border-gray-700">
                                    <BanknotesIcon className="w-12 h-12 mx-auto text-gray-200 mb-4" />
                                    <h3 className="text-sm font-black text-gray-900 dark:text-white uppercase tracking-widest">No Invoices</h3>
                                    <p className="text-xs text-gray-400 mt-2">Generate invoices to track payments for this session.</p>
                                </div>
                            ) : (
                                projectInvoices.map(inv => (
                                    <div 
                                        key={inv.id} 
                                        onClick={() => setViewingInvoice(inv)}
                                        className="p-6 bg-white dark:bg-gray-800 rounded-[2rem] border border-gray-100 dark:border-gray-700 flex items-center justify-between group transition-all hover:border-teal-200 dark:hover:border-teal-900 cursor-pointer shadow-sm hover:shadow-md"
                                    >
                                        <div className="flex items-center space-x-4">
                                            <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-2xl group-hover:bg-teal-50 dark:group-hover:bg-teal-950/30 transition-colors">
                                                <DocumentTextIcon className="w-6 h-6 text-gray-400 group-hover:text-teal-500 transition-colors" />
                                            </div>
                                            <div>
                                                <div className="flex items-center space-x-2 mb-1">
                                                    <span className="text-sm font-black text-gray-900 dark:text-white uppercase tracking-widest group-hover:text-teal-500 transition-colors">{inv.invoice_number}</span>
                                                    <span className={`px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-widest ${
                                                        inv.status === 'paid' ? 'bg-teal-50 text-teal-600 dark:bg-teal-900/20 dark:text-teal-400' : 
                                                        inv.status === 'accepted' ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400' :
                                                        inv.status === 'sent' ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400' : 
                                                        inv.status === 'rejected' ? 'bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400' :
                                                        inv.status === 'overdue' ? 'bg-amber-50 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400' :
                                                        'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                                                    }`}>
                                                        {inv.status}
                                                    </span>
                                                </div>
                                                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">
                                                    Due {new Date(inv.due_date).toLocaleDateString()} • {inv.items?.length || 0} items
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-4">
                                            <p className="text-lg font-black text-gray-900 dark:text-white">₹{inv.total_amount}</p>
                                            <ChevronRightIcon className="w-5 h-5 text-gray-300 group-hover:text-teal-500 group-hover:translate-x-0.5 transition-all" />
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                )}

                {modalTab === 'contracts' && (
                    <div className="space-y-8 mb-10">
                        <div className="flex justify-between items-center">
                            <h4 className="text-xs font-black uppercase tracking-widest text-gray-400 ml-1">Contracts & Agreements</h4>
                            <button 
                                onClick={() => {
                                    if (checkBusinessDetails()) {
                                        setContractTitle(`${selectedProject.title} - Contract`);
                                        setShowGenerateContract(true);
                                    }
                                }}
                                className="px-6 py-2.5 bg-black dark:bg-white text-white dark:text-black rounded-xl text-[10px] font-black uppercase tracking-widest hover:opacity-90 transition-all font-mono"
                            >
                                Generate Contract
                            </button>
                        </div>

                        <div className="space-y-4">
                            {projectContracts.length === 0 ? (
                                <div className="p-20 bg-gray-50 dark:bg-gray-800 rounded-[3rem] text-center border border-dashed border-gray-200 dark:border-gray-700">
                                    <DocumentTextIcon className="w-12 h-12 mx-auto text-gray-200 mb-4" />
                                    <h3 className="text-sm font-black text-gray-900 dark:text-white uppercase tracking-widest">No Contracts</h3>
                                    <p className="text-xs text-gray-400 mt-2">Generate a contract for this session.</p>
                                </div>
                            ) : (
                                projectContracts.map(contract => (
                                    <div 
                                        key={contract.id} 
                                        onClick={() => setViewingContract(contract)}
                                        className="p-6 bg-white dark:bg-gray-800 rounded-[2rem] border border-gray-100 dark:border-gray-700 flex items-center justify-between group transition-all hover:border-teal-200 dark:hover:border-teal-900 cursor-pointer shadow-sm hover:shadow-md"
                                    >
                                        <div className="flex items-center space-x-4">
                                            <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-2xl group-hover:bg-teal-50 dark:group-hover:bg-teal-950/30 transition-colors">
                                                <DocumentTextIcon className="w-6 h-6 text-gray-400 group-hover:text-teal-500 transition-colors" />
                                            </div>
                                            <div>
                                                <div className="flex items-center space-x-2 mb-1">
                                                    <span className="text-sm font-black text-gray-900 dark:text-white uppercase tracking-widest group-hover:text-teal-500 transition-colors">{contract.title}</span>
                                                    <span className={`px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-widest ${
                                                        contract.status === 'signed' ? 'bg-teal-50 text-teal-600 dark:bg-teal-900/20 dark:text-teal-400' : 
                                                        contract.status === 'rejected' ? 'bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400' :
                                                        'bg-indigo-50 text-indigo-600 dark:bg-indigo-900/20 dark:text-indigo-400'
                                                    }`}>
                                                        {contract.status === 'signed' ? 'Signed' : contract.status}
                                                    </span>
                                                </div>
                                                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">
                                                    Client: {contract.client_signature ? `✓ ${contract.client_signature}` : 'Awaiting'}
                                                    {' · '}
                                                    {professionLabel}: {contract.photographer_signature ? `✓ ${contract.photographer_signature}` : 'Awaiting'}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-4">
                                            <ChevronRightIcon className="w-5 h-5 text-gray-300 group-hover:text-teal-500 group-hover:translate-x-0.5 transition-all" />
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                )}

                {modalTab === 'gallery' && (
                    <section className="mb-10">
                        <div className="mb-6">
                            <h4 className="text-xs font-black uppercase tracking-widest text-gray-400 ml-1 mb-3">Proofing Gallery</h4>
                            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 w-full">
                                <button 
                                    onClick={() => {
                                        if (selectedProject.gallery_status !== 'published') {
                                            toast.error('Gallery is not published yet');
                                            return;
                                        }
                                        const url = `${window.location.origin}/gallery/${selectedProject.id}`;
                                        navigator.clipboard.writeText(url);
                                        toast.success('Gallery link copied!');
                                    }}
                                    className={`px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all font-mono text-center w-full sm:w-auto ${
                                        selectedProject.gallery_status === 'published' 
                                            ? 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200' 
                                            : 'bg-gray-50 dark:bg-gray-800/50 text-gray-400 dark:text-gray-600 cursor-not-allowed'
                                    }`}
                                    title={selectedProject.gallery_status !== 'published' ? "Publish gallery to share link" : ""}
                                >
                                    Copy Link
                                </button>
                                <label className="px-6 py-2.5 bg-black dark:bg-white text-white dark:text-black rounded-xl text-[10px] font-black uppercase tracking-widest cursor-pointer hover:opacity-90 transition-all text-center w-full sm:w-auto flex items-center justify-center">
                                    Upload Photos
                                    <input 
                                        type="file" 
                                        multiple 
                                        className="hidden" 
                                        accept="image/*"
                                        onChange={(e) => e.target.files && uploadImages(selectedProject.id, e.target.files)}
                                    />
                                </label>
                                <button
                                    onClick={() => setShowVideoOptionsModal(true)}
                                    className="px-6 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-[10px] font-black uppercase tracking-widest transition-all text-center w-full sm:w-auto flex items-center justify-center"
                                >
                                    ☁ Add / Upload Video
                                </button>
                                <input  
                                    type="file" 
                                    ref={videoInputRef}
                                    className="hidden" 
                                    accept="video/*"
                                    onChange={async (e) => {
                                        if (!e.target.files?.[0]) return;
                                        const file = e.target.files[0];
                                        
                                        // Inspect video codecs and formats client-side
                                        const supportCheck = await checkVideoSupport(file);
                                        if (!supportCheck.supported) {
                                            const proceedAnyway = window.confirm(
                                                `⚠️ WARNING: Unsupported Video Format/Codec Detected!\n\n` +
                                                `File: ${file.name}\n` +
                                                `Detected Codec: ${supportCheck.reason}\n\n` +
                                                `Consequences of Proceeding:\n` +
                                                `1. This video WILL NOT play directly inside standard web browsers (like Chrome) without being downloaded first.\n` +
                                                `2. Google Drive's free background transcoder will likely get stuck in a "processing" state forever.\n\n` +
                                                `Recommendation:\n` +
                                                `We highly recommend transcoding your video to standard H.264 (video codec) and AAC (audio codec) before uploading to ensure smooth client preview.\n\n` +
                                                `Do you want to proceed and upload this file anyway?`
                                            );
                                            if (!proceedAnyway) {
                                                e.target.value = '';
                                                return;
                                            }
                                        }

                                        // Confirm Google Drive upload
                                        const proceed = window.confirm(
                                            `This video ("${file.name}") will be uploaded directly to your Google Drive under "ClickCollab Projects" folder and linked to this gallery. Proceed?`
                                        );
                                        if (!proceed) {
                                            e.target.value = '';
                                            return;
                                        }

                                        // Open popup synchronously to bypass browser popup blocker
                                        const popup = window.open('', '_blank', 'width=500,height=600');
                                        if (popup) {
                                            popup.document.write(`
                                                <html>
                                                    <body style="font-family:sans-serif;text-align:center;padding-top:100px;background:#fafafa;color:#333;">
                                                        <h3 style="margin-bottom:8px;">Checking Google Drive...</h3>
                                                        <p style="font-size:12px;color:#666;">Please do not close this window.</p>
                                                    </body>
                                                </html>
                                            `);
                                        }

                                        // Check Drive connection first
                                        try {
                                            const statusRes = await crmAPI.getGoogleDriveStatus();
                                            if (!statusRes.data?.connected) {
                                                const authRes = await crmAPI.getGoogleDriveAuthURL();
                                                if (authRes.data?.url) {
                                                    if (popup) {
                                                        popup.location.href = authRes.data.url;
                                                    }
                                                    toast('Connect your Google Drive in the popup, then retry.', { icon: '🔗' });
                                                    e.target.value = '';
                                                    return;
                                                }
                                            }
                                        } catch {
                                            if (popup) popup.close();
                                            toast.error('Could not check Drive connection');
                                            e.target.value = '';
                                            return;
                                        }
                                        if (popup) popup.close();
                                        
                                        const jobId = `crm-video-upload-${Date.now()}`;
                                        
                                        // Add background job
                                        dispatch(addUploadJob({
                                            id: jobId,
                                            title: `Video: "${file.name}"`,
                                            type: 'gallery',
                                            status: 'uploading',
                                            progress: 0,
                                        }));

                                        const controller = new AbortController();
                                        registerUploadController(jobId, controller);

                                        const uploadProcess = async () => {
                                             try {
                                                 // Generate thumbnail client-side
                                                 const base64Thumbnail = await generateVideoThumbnail(file);

                                                 const initRes = await crmAPI.initiateCRMVideoUpload(selectedProject.id, {
                                                     filename: file.name,
                                                     size: file.size,
                                                     content_type: file.type || 'video/mp4'
                                                 });
                                                 
                                                 const uploadUrl = initRes.data?.upload_url;
                                                 if (!uploadUrl) {
                                                     throw new Error("Failed to get Google Drive upload session URL");
                                                 }

                                                 const cleanAxios = axios.create();
                                                 const driveRes = await cleanAxios.put(uploadUrl, file, {
                                                     signal: controller.signal,
                                                     headers: {
                                                         'Content-Type': file.type || 'video/mp4',
                                                     },
                                                     onUploadProgress: (progressEvent) => {
                                                         if (progressEvent.total) {
                                                             const pct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                                                             dispatch(updateUploadJob({ id: jobId, progress: pct }));
                                                             if (pct === 100) {
                                                                 dispatch(updateUploadJob({ id: jobId, status: 'processing' }));
                                                             }
                                                         }
                                                     }
                                                 });

                                                 const fileId = driveRes.data?.id;
                                                 if (!fileId) {
                                                     throw new Error("Failed to retrieve File ID from Google Drive");
                                                 }

                                                 const completeRes = await crmAPI.completeCRMVideoUpload(selectedProject.id, {
                                                     file_id: fileId,
                                                     filename: file.name,
                                                     thumbnail: base64Thumbnail
                                                 });
                                             const newItem = completeRes.data?.item;
                                                 if (newItem) {
                                                     setGalleryImages(prev => [...prev, newItem]);
                                                 }
                                                 dispatch(updateUploadJob({ id: jobId, status: 'completed' }));
                                                 toast.success('Video successfully uploaded directly to Google Drive!');
                                             } catch (err: any) {
                                                 if (axios.isCancel(err) || err.name === 'CanceledError' || err.message === 'canceled') {
                                                     console.log('Upload canceled');
                                                     return;
                                                 }
                                                 console.error('Google Drive video upload error:', err);
                                                 const errorMsg = err.response?.data?.error || err.message || 'Failed to upload video';
                                                 dispatch(updateUploadJob({ id: jobId, status: 'error', error: errorMsg }));
                                                 
                                                 if (err.response?.data?.code === 'CLOUDRIVE_NOT_CONNECTED' || errorMsg.includes('drive not connected') || errorMsg.includes('reconnect google drive')) {
                                                      try {
                                                          const authRes = await crmAPI.getGoogleDriveAuthURL();
                                                          if (authRes.data?.url) {
                                                              toast((t) => (
                                                                  <span>
                                                                      Google Drive is not connected. <a href={authRes.data.url} target="_blank" rel="noreferrer" style={{ textDecoration: 'underline', fontWeight: 'bold', color: '#0f766e' }} onClick={() => toast.dismiss(t.id)}>Click here to connect</a>, then retry.
                                                                  </span>
                                                              ), { duration: 10000, icon: '🔗' });
                                                          }
                                                      } catch (e) {
                                                          toast.error('Google Drive is not connected. Please try uploading again to connect.');
                                                      }
                                                  } else {
                                                      toast.error(errorMsg);
                                                  }
                                             } finally {
                                                 unregisterUploadController(jobId);
                                             }
                                         };

                                        uploadProcess();
                                        e.target.value = '';
                                    }}
                                
                                />
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3 mb-8">
                            <div className="flex-1 flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700">
                                <div className="flex items-center space-x-3 min-w-0">
                                    <div className="p-2.5 bg-teal-50 dark:bg-teal-900/20 rounded-xl flex-shrink-0">
                                        <KeyIcon className="w-5 h-5 text-teal-600" />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-[9px] font-black uppercase tracking-widest text-gray-400 mb-0.5">Access Code</p>
                                        <div className="flex items-center space-x-2">
                                            <span className="text-sm font-black font-mono tracking-wider truncate">
                                                {selectedProject.gallery_password ? '••••' : 'None'}
                                            </span>

                                        </div>
                                    </div>
                                </div>
                                <button 
                                    onClick={() => resendAccessCode(selectedProject.id)}
                                    className="px-3 py-1.5 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-gray-50 transition-all flex-shrink-0 ml-2"
                                >
                                    Resend to Client
                                </button>
                            </div>

                            <div className="flex-1 flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700">
                                <div className="flex items-center space-x-3 min-w-0">
                                    <div className={`p-2.5 rounded-xl flex-shrink-0 ${selectedProject.gallery_status === 'published' ? 'bg-green-50 dark:bg-green-900/20' : 'bg-gray-100 dark:bg-gray-700'}`}>
                                        <CloudArrowUpIcon className={`w-5 h-5 ${selectedProject.gallery_status === 'published' ? 'text-green-600' : 'text-gray-400'}`} />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-[9px] font-black uppercase tracking-widest text-gray-400 mb-0.5">Status</p>
                                        <span className={`text-[10px] font-black uppercase tracking-widest truncate block ${selectedProject.gallery_status === 'published' ? 'text-green-600' : 'text-gray-400'}`}>
                                            {selectedProject.gallery_status || 'Draft'}
                                        </span>
                                    </div>
                                </div>
                                <button 
                                    onClick={() => toggleGalleryPublish(selectedProject.id, selectedProject.gallery_status)}
                                    className={`px-3 py-1.5 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all flex-shrink-0 ml-2 ${
                                        selectedProject.gallery_status === 'published' 
                                        ? 'bg-red-50 text-red-600 border border-red-100 hover:bg-red-100' 
                                        : 'bg-teal-500 text-white hover:bg-teal-600'
                                    }`}
                                >
                                    {selectedProject.gallery_status === 'published' ? 'Unpublish' : 'Publish'}
                                </button>
                            </div>
                        </div>
                        
                        {galleryImages.length === 0 ? (
                            <div className="p-20 bg-gray-50 dark:bg-gray-800 rounded-[3rem] text-center border border-dashed border-gray-200 dark:border-gray-700">
                                <PresentationChartLineIcon className="w-12 h-12 mx-auto text-gray-200 mb-4" />
                                <h3 className="text-sm font-black text-gray-900 dark:text-white uppercase tracking-widest">No Photos Uploaded</h3>
                                <p className="text-xs text-gray-400 mt-2">Upload session photos to start the proofing process.</p>
                            </div>
) : (
                            <Masonry 
                                breakpointCols={{default: 3, 1100: 3, 768: 2, 500: 2}} 
                                className="flex w-auto -ml-4 max-h-[500px] overflow-y-auto pr-2 custom-scrollbar" 
                                columnClassName="pl-4 bg-clip-padding space-y-4"
                            >
                                {galleryImages.map((img) => (
                                    <div key={img.id} className="relative rounded-[1.5rem] overflow-hidden group border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 min-h-[140px] cursor-pointer" onClick={() => setSelectedImageForView(img)}>
                                        <img 
                                            src={resolveImageUrl(img.thumbnail_signed || img.thumbnail || img.url_signed || img.url)} 
                                            alt="" 
                                            className="w-full h-auto object-cover max-h-[400px] max-w-[100%]"
                                            onError={(e) => {
                                                e.currentTarget.src = "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 9' fill='%231f2937'><rect width='100%' height='100%'/><polygon points='7,3 10,4.5 7,6' fill='%23ffffff' opacity='0.3'/></svg>";
                                            }}
                                        />
                                        
                                        {img.media_type === 'video' && (
                                            img.processing_status === 'processing' ? (
                                                <div className="absolute inset-0 flex items-center justify-center bg-black/50 backdrop-blur-[1px] pointer-events-none">
                                                    <div className="flex flex-col items-center gap-1.5">
                                                        <div className="w-6 h-6 rounded-full border-2 border-white/30 border-t-purple-400 animate-spin" />
                                                        <span className="text-[8px] font-bold text-white/80 uppercase tracking-widest">Processing…</span>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/35 transition-all pointer-events-none">
                                                    <div className="p-3 bg-white/90 text-gray-900 rounded-full shadow-lg transition-transform group-hover:scale-110">
                                                        <PlayIcon className="w-5 h-5" />
                                                    </div>
                                                </div>
                                            )
                                        )}

                                        <div className="absolute bottom-3 left-3 z-20 pointer-events-none">
                                            <div className="bg-black/60 backdrop-blur-md px-2.5 py-1 rounded-lg">
                                                <p className="text-[10px] font-bold text-white truncate max-w-[150px]">
                                                    {img.original_name || img.url?.split('/').pop()?.split('?')[0] || 'Image'}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center space-x-4">
                                            <button 
                                                onClick={() => {
                                                     deleteFromDriveRef.current = false;
                                                     const isGoogleDrive = img.media_type === 'video' && (img.video_platform === 'google_drive' || img.url?.includes('drive.google.com'));

                                                     setConfirmModalState({
                                                         title: img.media_type === 'video' ? 'Delete Video' : 'Delete Photo',
                                                         message: img.media_type === 'video' 
                                                             ? 'Are you sure you want to delete this video from the gallery?' 
                                                             : 'Are you sure you want to delete this photo from the gallery?',
                                                         isDestructive: true,
                                                         confirmText: 'Delete',
                                                         cancelText: 'Cancel',
                                                         onConfirm: () => {
                                                             const deleteFromDrive = deleteFromDriveRef.current;
                                                             crmAPI.deleteGalleryImage(selectedProject.id, img.id, deleteFromDrive).then(() => {
                                                                 fetchGallery(selectedProject.id);
                                                                 toast.success(deleteFromDrive ? 'Video deleted from gallery and Google Drive' : 'Video deleted from gallery');
                                                                 setShowConfirmModal(false);
                                                             }).catch((err) => {
                                                                 console.error(err);
                                                                 toast.error(img.media_type === 'video' ? 'Failed to delete video' : 'Failed to delete photo');
                                                                 setShowConfirmModal(false);
                                                             });
                                                         },
                                                         children: isGoogleDrive ? (
                                                             <label className="flex items-center justify-center space-x-2.5 text-xs font-bold text-gray-600 dark:text-gray-300 cursor-pointer select-none bg-gray-50 dark:bg-gray-800 p-3.5 rounded-xl border border-gray-100 dark:border-gray-700">
                                                                 <input 
                                                                     type="checkbox" 
                                                                     defaultChecked={false}
                                                                     onChange={(e) => {
                                                                         deleteFromDriveRef.current = e.target.checked;
                                                                     }}
                                                                     className="rounded border-gray-300 text-rose-500 focus:ring-rose-500 w-4 h-4 cursor-pointer"
                                                                 />
                                                                 <span>Also delete file from Google Drive</span>
                                                             </label>
                                                         ) : undefined
                                                     });
                                                     setShowConfirmModal(true);
                                                 }}
                                                className="w-10 h-10 rounded-full flex items-center justify-center shadow-2xl transition-all bg-white text-red-500 hover:bg-red-50"
                                                title={img.media_type === 'video' ? 'Delete Video' : 'Delete Photo'}
                                            >
                                                <TrashIcon className="w-5 h-5" />
                                            </button>
                                            <button
                                                onClick={() => setSelectedImageForView(img)}
                                                className="w-10 h-10 rounded-full flex items-center justify-center shadow-2xl transition-all bg-white text-gray-800 hover:bg-gray-100"
                                                title="Preview"
                                            >
                                                <EyeIcon className="w-5 h-5" />
                                            </button>
                                            <button 
                                                onClick={() => toggleImageSelection(img.id)}
                                                className={`w-10 h-10 rounded-full flex items-center justify-center shadow-2xl transition-all ${
                                                    img.is_photographer_selected ? 'bg-teal-500 text-white' : 'bg-white text-black hover:bg-gray-50'
                                                }`}
                                                title={img.is_photographer_selected ? "Deselect" : "Select"}
                                            >
                                                <CheckBadgeIcon className="w-6 h-6" />
                                            </button>
                                        </div>
                                        <div className="absolute top-3 right-3 flex flex-col gap-2">
                                            {img.is_photographer_selected && (
                                                <div className="w-7 h-7 bg-teal-500 rounded-full flex flex-col items-center justify-center text-white text-xs font-bold border-2 border-white shadow-md group-hover:opacity-100 transition-opacity" title={`Selected by ${professionLabel}`}>
                                                    <CheckBadgeIconSolid className="w-4 h-4" />
                                                </div>
                                            )}
                                            {img.is_selected && (
                                                <div className="w-7 h-7 bg-pink-500 rounded-full flex flex-col items-center justify-center text-white text-xs font-bold border-2 border-white shadow-md transition-opacity" title="Selected by Client">
                                                    <HeartIconSolid className="w-4 h-4" />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </Masonry>
                        )}
                    </section>
                )}

                <div className="grid grid-cols-2 gap-4 mt-4">
                    <button 
                        onClick={() => {
                            setConfirmModalState({
                                title: 'Delete Project',
                                message: 'Are you sure you want to permanently delete this project? This will also delete all associated galleries, invoices, and contracts.',
                                isDestructive: true,
                                confirmText: 'Delete Project',
                                cancelText: 'Cancel',
                                onConfirm: () => {
                                    crmAPI.deleteProject(selectedProject.id)
                                        .then(() => {
                                            toast.success('Project deleted successfully');
                                            setSelectedProject(null);
                                            fetchData();
                                            setShowConfirmModal(false);
                                        })
                                        .catch(err => toast.error('Failed to delete project'));
                                }
                            });
                            setShowConfirmModal(true);
                        }}
                        className="w-full py-4 bg-red-50 dark:bg-red-900/20 text-red-500 rounded-2xl font-black text-sm hover:bg-red-100 transition-all border border-red-100 dark:border-red-900/50"
                    >
                        Delete Project
                    </button>
                    <button 
                        onClick={() => setSelectedProject(null)}
                        className="w-full py-4 bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 rounded-2xl font-black text-sm hover:bg-gray-200 transition-all"
                    >
                        Close
                    </button>
                </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Lead Already Converted Modal */}
      <AnimatePresence>
        {leadAlreadyConvertedFor && (
          <div className="fixed inset-0 z-[140] flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/40 backdrop-blur-sm"
              onClick={() => setLeadAlreadyConvertedFor(null)}
            />
            <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative bg-white dark:bg-gray-900 rounded-[2rem] shadow-2xl p-8 max-w-sm w-full text-center z-10"
            >
              <div className="w-16 h-16 rounded-full bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-black text-gray-900 dark:text-white mb-2">Already Converted</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                <strong>{leadAlreadyConvertedFor.lead?.client_name}</strong> has already been converted to a project.
                {leadAlreadyConvertedFor.project && <> You can locate it in the <strong>Projects</strong> tab.</>}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setLeadAlreadyConvertedFor(null)}
                  className="flex-1 py-3 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-2xl font-bold text-sm hover:bg-gray-200 dark:hover:bg-gray-700 transition-all"
                >
                  Dismiss
                </button>
                {leadAlreadyConvertedFor.project && (
                  <button
                    onClick={() => {
                      setLeadAlreadyConvertedFor(null);
                      setActiveTab('projects');
                      setSelectedProject(leadAlreadyConvertedFor.project);
                    }}
                    className="flex-1 py-3 bg-teal-500 hover:bg-teal-600 text-white rounded-2xl font-black text-sm transition-all shadow-lg shadow-teal-500/20"
                  >
                    Go to Project
                  </button>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Convert Lead Modal */}
      <AnimatePresence>
        {showConvertLead && (
          <div className="fixed inset-0 z-[130] flex items-center justify-center p-4 min-h-screen">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowConvertLead(null)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="relative w-full max-w-xl bg-white dark:bg-gray-900 rounded-[2.5rem] overflow-hidden shadow-2xl flex flex-col max-h-[90vh]"
            >
              <div className="p-8 pb-4">
                <h2 className="text-2xl font-black text-gray-900 dark:text-white mb-2">
                  {isEditingProject ? `Edit ${sessionLabel}` : `Book ${sessionLabel}`}
                </h2>
                <p className="text-gray-500 text-sm">Create a project for <span className="text-teal-500 font-bold">{showConvertLead.client_name}</span></p>
              </div>
              
              <div className="flex-1 overflow-y-auto px-8 custom-scrollbar">
                <form id="book-session-form" onSubmit={handleConvertLead} className="space-y-6 pt-4 pb-8">
                <div>
                  <label className="block text-xs font-black uppercase tracking-widest text-gray-400 mb-1.5 ml-1">Project Title</label>
                  <input 
                    type="text" 
                    required 
                    value={newProject.title}
                    onChange={(e) => setNewProject({...newProject, title: e.target.value})}
                    className="w-full px-5 py-3.5 bg-gray-50 dark:bg-gray-800 border-none rounded-2xl focus:ring-2 focus:ring-teal-500 transition-all font-medium"
                    placeholder="e.g. Wedding Shoot"
                  />
                </div>

                <div>
                  <label className="block text-xs font-black uppercase tracking-widest text-gray-400 mb-1.5 ml-1">Project Notes</label>
                  <textarea 
                    value={newProject.description}
                    onChange={(e) => setNewProject({...newProject, description: e.target.value})}
                    className="w-full px-5 py-3.5 bg-gray-50 dark:bg-gray-800 border-none rounded-2xl focus:ring-2 focus:ring-teal-500 transition-all font-medium resize-none shadow-inner"
                    placeholder="Add special instructions or details..."
                    rows={2}
                  />
                </div>

                {isModel && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-black uppercase tracking-widest text-gray-400 mb-1.5 ml-1">Usage Rights</label>
                      <input 
                        type="text" 
                        value={newProject.usage_rights}
                        onChange={(e) => setNewProject({...newProject, usage_rights: e.target.value})}
                        className="w-full px-5 py-3.5 bg-gray-50 dark:bg-gray-800 border-none rounded-2xl focus:ring-2 focus:ring-teal-500 transition-all font-medium"
                        placeholder="e.g. Commercial, Social Media"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-black uppercase tracking-widest text-gray-400 mb-1.5 ml-1">Wardrobe / Looks</label>
                      <input 
                        type="text" 
                        value={newProject.wardrobe_notes}
                        onChange={(e) => setNewProject({...newProject, wardrobe_notes: e.target.value})}
                        className="w-full px-5 py-3.5 bg-gray-50 dark:bg-gray-800 border-none rounded-2xl focus:ring-2 focus:ring-teal-500 transition-all font-medium"
                        placeholder="e.g. 3 changes, Formal"
                      />
                    </div>
                  </div>
                )}

                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-black uppercase tracking-widest text-gray-400 mb-1.5 ml-1">Default Location</label>
                    <LocationPicker 
                      value={newProject.location}
                      onChange={(loc, coords) => setNewProject({
                        ...newProject, 
                        location: loc, 
                        latitude: coords?.lat || 0,
                        longitude: coords?.lng || 0
                      })}
                      placeholder="Search location..."
                      className="w-full px-5 py-3.5 bg-gray-50 dark:bg-gray-800 border-none rounded-2xl focus:ring-2 focus:ring-teal-500 transition-all font-medium"
                    />
                  </div>
                  <div className="md:w-1/2">
                    <label className="block text-xs font-black uppercase tracking-widest text-gray-400 mb-1.5 ml-1">Location Tag</label>
                    <div className="relative">
                      <input 
                        type="text" 
                        value={newProject.location_name}
                        onChange={(e) => setNewProject({...newProject, location_name: e.target.value})}
                        className="w-full px-5 py-3.5 bg-gray-50 dark:bg-gray-800 border-none rounded-2xl focus:ring-2 focus:ring-teal-500 transition-all font-medium"
                        placeholder="e.g. Studio, Home, Outdoor"
                      />
                      <div className="flex gap-1 mt-2 flex-wrap">
                        {['Studio', 'Home', 'Outdoor', 'On Location'].map(tag => (
                          <button
                            key={tag}
                            type="button"
                            onClick={() => setNewProject({...newProject, location_name: tag})}
                            className={`px-2 py-1 rounded-md text-[8px] font-black uppercase tracking-widest transition-all ${newProject.location_name === tag ? 'bg-teal-500 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-400'}`}
                          >
                            {tag}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-black uppercase tracking-widest text-gray-400 ml-1">{isModel ? 'Booking Dates' : 'Shoot Dates'}</label>
                    <button 
                      type="button"
                      onClick={() => setNewProject({
                        ...newProject, 
                        shoot_dates: [...newProject.shoot_dates, { 
                          start_date: '', 
                          end_date: '', 
                          is_range: false,
                          location: '',
                          location_name: '',
                          latitude: 0,
                          longitude: 0
                        }]
                      })}
                      className="text-[10px] font-black text-teal-500 uppercase tracking_widest hover:text-teal-600 flex items-center space-x-1"
                    >
                      <PlusIcon className="w-3 h-3" />
                      <span>Add Date</span>
                    </button>
                  </div>
                  
                  {newProject.shoot_dates.map((sd, idx) => (
                    <div key={idx} className="p-4 bg-gray-50 dark:bg-gray-800 rounded-2xl space-y-3 relative group">
                      {newProject.shoot_dates.length > 1 && (
                        <button 
                          type="button"
                          onClick={() => {
                            const updated = [...newProject.shoot_dates];
                            updated.splice(idx, 1);
                            setNewProject({...newProject, shoot_dates: updated});
                          }}
                          className="absolute -top-2 -right-2 p-1 bg-white dark:bg-gray-700 rounded-full shadow-sm opacity-0 group-hover:opacity-100 transition-opacity z-10"
                        >
                          <XMarkIcon className="w-3 h-3 text-red-500" />
                        </button>
                      )}
                      
                      <div className="flex items-center space-x-3">
                        <button 
                          type="button"
                          onClick={() => {
                            const updated = [...newProject.shoot_dates];
                            updated[idx].is_range = !updated[idx].is_range;
                            setNewProject({...newProject, shoot_dates: updated});
                          }}
                          className={`px-3 py-1 rounded-lg text-[8px] font-black uppercase tracking-widest transition-all ${sd.is_range ? 'bg-black text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-500'}`}
                        >
                          {sd.is_range ? 'Range' : 'Single Date'}
                        </button>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className={sd.is_range ? "" : "col-span-2"}>
                          <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest mb-1 ml-1">{sd.is_range ? 'Start Date' : 'Date'}</p>
                          <input 
                            type="date" 
                            required
                            value={sd.start_date}
                            onChange={(e) => {
                              const updated = [...newProject.shoot_dates];
                              updated[idx].start_date = e.target.value;
                              setNewProject({...newProject, shoot_dates: updated});
                            }}
                            className="w-full px-4 py-2 bg-white dark:bg-gray-900 border-none rounded-xl focus:ring-2 focus:ring-teal-500 text-xs font-medium"
                          />
                        </div>
                        {sd.is_range && (
                          <div>
                            <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest mb-1 ml-1">End Date</p>
                            <input 
                              type="date" 
                              required
                              value={sd.end_date}
                              onChange={(e) => {
                                const updated = [...newProject.shoot_dates];
                                updated[idx].end_date = e.target.value;
                                setNewProject({...newProject, shoot_dates: updated});
                              }}
                              className="w-full px-4 py-2 bg-white dark:bg-gray-900 border-none rounded-xl focus:ring-2 focus:ring-teal-500 text-xs font-medium"
                            />
                          </div>
                        )}
                      </div>

                      <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest">Custom Location for this Date?</p>
                          <button 
                            type="button"
                            onClick={() => {
                              const updated = [...newProject.shoot_dates];
                              if (updated[idx].location) {
                                updated[idx].location = '';
                                updated[idx].location_name = '';
                                updated[idx].latitude = 0;
                                updated[idx].longitude = 0;
                              } else {
                                updated[idx].location = ' '; // Signal override
                              }
                              setNewProject({...newProject, shoot_dates: updated});
                            }}
                            className={`px-2 py-1 rounded-md text-[8px] font-black uppercase tracking-widest transition-all ${sd.location ? 'bg-teal-500 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-400'}`}
                          >
                            {sd.location ? 'Override Active' : 'Use Default'}
                          </button>
                        </div>
                        
                        {sd.location && (
                          <div className="space-y-3 animate-in fade-in slide-in-from-top-1">
                            <div>
                              <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest mb-1 ml-1">Specific Location</p>
                              <LocationPicker 
                                value={sd.location === ' ' ? '' : sd.location}
                                onChange={(loc, coords) => {
                                  const updated = [...newProject.shoot_dates];
                                  updated[idx].location = loc;
                                  updated[idx].latitude = coords?.lat || 0;
                                  updated[idx].longitude = coords?.lng || 0;
                                  setNewProject({...newProject, shoot_dates: updated});
                                }}
                                className="w-full px-4 py-2 bg-white dark:bg-gray-900 border-none rounded-xl focus:ring-2 focus:ring-teal-500 text-xs font-medium"
                              />
                            </div>
                            <div className="md:w-1/2">
                              <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest mb-1 ml-1">Tag</p>
                              <input 
                                type="text" 
                                value={sd.location_name}
                                onChange={(e) => {
                                  const updated = [...newProject.shoot_dates];
                                  updated[idx].location_name = e.target.value;
                                  setNewProject({...newProject, shoot_dates: updated});
                                }}
                                className="w-full px-4 py-2 bg-white dark:bg-gray-900 border-none rounded-xl focus:ring-2 focus:ring-teal-500 text-xs font-medium"
                                placeholder="e.g. Other Studio"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-black uppercase tracking-widest text-gray-400 mb-1.5 ml-1">Final Budget (₹)</label>
                    <input 
                      type="number" 
                      value={newProject.total_amount}
                      onChange={(e) => setNewProject({...newProject, total_amount: e.target.value})}
                      className="w-full px-5 py-3.5 bg-gray-50 dark:bg-gray-800 border-none rounded-2xl focus:ring-2 focus:ring-teal-500 transition-all font-medium"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-black uppercase tracking-widest text-gray-400 mb-1.5 ml-1">Advance (₹)</label>
                    <input 
                      type="number" 
                      value={newProject.retainer_amount}
                      onChange={(e) => setNewProject({...newProject, retainer_amount: e.target.value})}
                      className="w-full px-5 py-3.5 bg-gray-50 dark:bg-gray-800 border-none rounded-2xl focus:ring-2 focus:ring-teal-500 transition-all font-medium"
                      placeholder="0.00"
                    />
                  </div>
                </div>
                <div className="flex items-center space-x-3 mt-4 ml-1">
                  <input
                    type="checkbox"
                    id="retainer_paid"
                    checked={newProject.retainer_paid}
                    onChange={(e) => setNewProject({...newProject, retainer_paid: e.target.checked})}
                    className="w-5 h-5 rounded border-gray-200 text-teal-500 focus:ring-teal-500 transition-all cursor-pointer"
                  />
                  <label htmlFor="retainer_paid" className="text-sm font-bold text-gray-700 dark:text-gray-300 cursor-pointer">
                    Advance Paid
                  </label>
                </div>
              </form>
            </div>

            <div className="px-8 py-6 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-800 flex space-x-3">
              <button 
                type="button"
                onClick={() => setShowConvertLead(null)}
                className="flex-1 py-4 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 rounded-2xl font-black text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-all border border-gray-200 dark:border-gray-700"
              >
                Cancel
              </button>
              <button 
                type="submit"
                form="book-session-form"
                className="flex-[2] py-4 bg-black dark:bg-white text-white dark:text-black rounded-2xl font-black text-sm hover:opacity-90 transition-all shadow-xl shadow-black/10"
              >
                {isEditingProject ? 'Update Project' : 'Book Now'}
              </button>
            </div>
          </motion.div>
          </div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showLeadConfirmationModal && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 min-h-screen">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowLeadConfirmationModal(false)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="relative w-full max-w-md bg-white dark:bg-gray-900 rounded-[2.5rem] overflow-hidden shadow-2xl p-8 text-center"
            >
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-orange-100 dark:bg-orange-950/30 text-primary mb-6">
                <EnvelopeIcon className="h-8 w-8" />
              </div>
              <h2 className="text-2xl font-black text-gray-900 dark:text-white mb-3">Confirmation Request Sent! ✉️</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 leading-relaxed">
                We've successfully sent a secure confirmation email and notification to your client. 
                <br /><br />
                To maintain collaboration integrity, they must confirm this link before you can transition their status to <strong>Converted</strong>. 
                In the meantime, feel free to update notes, log contacted status, or send proposals!
              </p>
              <button
                onClick={() => setShowLeadConfirmationModal(false)}
                className="w-full py-4 bg-primary text-white rounded-2xl font-black text-sm hover:bg-primary-hover transition-all shadow-xl shadow-primary/10"
              >
                Got it, thanks!
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showAddLead && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 min-h-screen">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowAddLead(false)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="relative w-full max-w-lg bg-white dark:bg-gray-900 rounded-[2.5rem] overflow-hidden shadow-2xl p-8"
            >
              <h2 className="text-2xl font-black text-gray-900 dark:text-white mb-6">Add New Lead</h2>
              <form onSubmit={handleAddLead} className="space-y-4">
                <div className="relative">
                  <label className="block text-xs font-black uppercase tracking-widest text-gray-400 mb-1.5 ml-1">Client Name</label>
                  <input 
                    type="text" 
                    required 
                    value={newLead.client_name}
                    onChange={(e) => {
                        setNewLead({...newLead, client_name: e.target.value});
                        debouncedSearchUsers(e.target.value);
                    }}
                    onFocus={() => {
                        if (newLead.client_name.length >= 2) setShowSuggestions(true);
                    }}
                    className="w-full px-5 py-3.5 bg-gray-50 dark:bg-gray-800 border-none rounded-2xl focus:ring-2 focus:ring-teal-500 transition-all font-medium"
                    placeholder="Search Portal User or Enter Name"
                  />
                  {/* Suggestions Dropdown */}
                  {showSuggestions && userSuggestions.length > 0 && (
                    <div 
                        ref={suggestionsRef}
                        className="absolute z-[60] left-0 right-0 mt-2 bg-white dark:bg-gray-800 rounded-3xl shadow-2xl border border-teal-100 dark:border-teal-900/30 max-h-64 overflow-y-auto overflow-x-hidden animate-in fade-in slide-in-from-top-2 duration-200"
                    >
                      <div className="p-2 border-b border-gray-50 dark:border-gray-700/50 flex justify-between items-center px-4">
                        <span className="text-[10px] font-black uppercase tracking-widest text-teal-600">Portal Users Found</span>
                        <button 
                          type="button"
                          onClick={() => setShowSuggestions(false)}
                          className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                        >
                          <XMarkIcon className="w-3 h-3 text-gray-400" />
                        </button>
                      </div>
                      {userSuggestions.map(u => (
                        <button
                          key={u.id}
                          type="button"
                          onClick={() => {
                              setNewLead({
                                  ...newLead, 
                                  client_name: u.full_name, 
                                  client_email: u.email || '', 
                                  client_phone: '',
                                  portal_user_id: u.id
                              });
                              setShowSuggestions(false);
                          }}
                          className="w-full text-left px-5 py-4 hover:bg-teal-50 dark:hover:bg-teal-900/10 font-medium text-gray-900 dark:text-white flex items-center gap-4 transition-all group border-b border-gray-50 dark:border-gray-700/50 last:border-0"
                        >
                          <Avatar
                            src={u.profile_pic_signed || u.profile_pic}
                            alt={u.full_name}
                            size="md"
                            className="ring-2 ring-white dark:ring-gray-800 shadow-sm"
                          />
                          <div className="flex flex-col min-w-0">
                            <p className="text-sm font-bold truncate group-hover:text-teal-600 transition-colors">{u.full_name}</p>
                            <div className="flex items-center space-x-2">
                                <span className="text-[10px] bg-teal-100 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400 px-1.5 py-0.5 rounded-md font-bold uppercase tracking-tighter">Portal User</span>
                                {u.username && <span className="text-[10px] text-gray-400 truncate">@{u.username}</span>}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-black uppercase tracking-widest text-gray-400 mb-1.5 ml-1">Email</label>
                    <input 
                      type="email" 
                      required 
                      value={newLead.client_email}
                      onChange={(e) => setNewLead({...newLead, client_email: e.target.value})}
                      className="w-full px-5 py-3.5 bg-gray-50 dark:bg-gray-800 border-none rounded-2xl focus:ring-2 focus:ring-teal-500 transition-all font-medium"
                      placeholder="Email address"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-black uppercase tracking-widest text-gray-400 mb-1.5 ml-1">Phone</label>
                    <input 
                      type="tel" 
                      value={newLead.client_phone}
                      onChange={(e) => setNewLead({...newLead, client_phone: e.target.value.replace(/\D/g, '')})}
                      className="w-full px-5 py-3.5 bg-gray-50 dark:bg-gray-800 border-none rounded-2xl focus:ring-2 focus:ring-teal-500 transition-all font-medium"
                      placeholder="Phone number"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-black uppercase tracking-widest text-gray-400 mb-1.5 ml-1">Interest</label>
                    <select 
                      value={newLead.interest_type}
                      onChange={(e) => setNewLead({...newLead, interest_type: e.target.value})}
                      className="w-full px-5 py-3.5 bg-gray-50 dark:bg-gray-800 border-none rounded-2xl focus:ring-2 focus:ring-teal-500 transition-all font-medium"
                    >
                      <option>Portrait</option>
                      <option>Wedding</option>
                      <option>Fashion</option>
                      <option>Commercial</option>
                      <option>Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-black uppercase tracking-widest text-gray-400 mb-1.5 ml-1">Budget (₹)</label>
                    <input 
                      type="number" 
                      value={newLead.estimated_budget}
                      onChange={(e) => setNewLead({...newLead, estimated_budget: e.target.value})}
                      className="w-full px-5 py-3.5 bg-gray-50 dark:bg-gray-800 border-none rounded-2xl focus:ring-2 focus:ring-teal-500 transition-all font-medium"
                      placeholder="0.00"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-black uppercase tracking-widest text-gray-400 mb-1.5 ml-1">Notes</label>
                  <textarea 
                    value={newLead.notes}
                    onChange={(e) => setNewLead({...newLead, notes: e.target.value})}
                    rows={3}
                    className="w-full px-5 py-3.5 bg-gray-50 dark:bg-gray-800 border-none rounded-2xl focus:ring-2 focus:ring-teal-500 transition-all font-medium resize-none"
                    placeholder="Specific requirements..."
                  />
                </div>
                <div className="pt-4 flex space-x-3">
                  <button 
                    type="button"
                    onClick={() => setShowAddLead(false)}
                    className="flex-1 py-4 bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 rounded-2xl font-black text-sm hover:bg-gray-200 dark:hover:bg-gray-700 transition-all"
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit"
                    className="flex-[2] py-4 bg-teal-500 text-white rounded-2xl font-black text-sm hover:bg-teal-600 shadow-lg shadow-teal-500/20 transition-all"
                  >
                    Save Lead
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
      {/* Add Invoice Modal */}
      <AnimatePresence>
        {showAddInvoice && (
          <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowAddInvoice(false)}
              className="absolute inset-0 bg-black/60 backdrop-blur-md"
            />
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative w-full max-w-xl bg-white dark:bg-gray-900 rounded-[3rem] shadow-2xl p-10 max-h-[85vh] overflow-y-auto"
            >
              <div className="flex justify-between items-center mb-8">
                <h3 className="text-2xl font-black text-gray-900 dark:text-white uppercase tracking-tight">Create Invoice</h3>
                <button onClick={() => setShowAddInvoice(false)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors">
                    <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleCreateInvoice} className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Invoice Number</label>
                        <input 
                            type="text" 
                            value={newInvoice.invoice_number}
                            onChange={(e) => setNewInvoice({...newInvoice, invoice_number: e.target.value})}
                            className="w-full bg-gray-50 dark:bg-gray-800 border-none rounded-2xl p-4 text-sm font-bold focus:ring-2 focus:ring-teal-500 transition-all font-mono"
                            required
                        />
                    </div>
                    <div className="space-y-2">
                        <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Due Date</label>
                        <input 
                            type="date" 
                            value={newInvoice.due_date}
                            onChange={(e) => setNewInvoice({...newInvoice, due_date: e.target.value})}
                            className="w-full bg-gray-50 dark:bg-gray-800 border-none rounded-2xl p-4 text-sm font-bold focus:ring-2 focus:ring-teal-500 transition-all"
                            required
                        />
                    </div>
                </div>

                <div className="space-y-4">
                    <div className="flex justify-between items-center">
                        <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Items</label>
                        <button 
                            type="button"
                            onClick={() => setNewInvoice({...newInvoice, items: [...newInvoice.items, { description: '', quantity: 1, unit_price: 0 }]})}
                            className="text-[10px] font-black text-teal-500 uppercase tracking-widest"
                        >
                            + Add Item
                        </button>
                    </div>
                    {newInvoice.items.map((item, index) => (
                        <div key={index} className="grid grid-cols-12 gap-3 items-center">
                            <div className="col-span-6">
                                <input 
                                    type="text" 
                                    placeholder="Description"
                                    value={item.description}
                                    onChange={(e) => {
                                        const items = [...newInvoice.items];
                                        items[index].description = e.target.value;
                                        setNewInvoice({...newInvoice, items});
                                    }}
                                    className="w-full bg-gray-50 dark:bg-gray-800 border-none rounded-xl p-3 text-xs font-bold"
                                    required
                                />
                            </div>
                            <div className="col-span-2">
                                <input 
                                    type="number" 
                                    placeholder="Qty"
                                    value={item.quantity}
                                    onChange={(e) => {
                                        const items = [...newInvoice.items];
                                        items[index].quantity = parseFloat(e.target.value);
                                        setNewInvoice({...newInvoice, items});
                                    }}
                                    className="w-full bg-gray-50 dark:bg-gray-800 border-none rounded-xl p-3 text-xs font-bold"
                                    required
                                />
                            </div>
                            <div className="col-span-3">
                                <input 
                                    type="number" 
                                    placeholder="Price"
                                    value={item.unit_price}
                                    onChange={(e) => {
                                        const items = [...newInvoice.items];
                                        items[index].unit_price = parseFloat(e.target.value);
                                        setNewInvoice({...newInvoice, items});
                                    }}
                                    className="w-full bg-gray-50 dark:bg-gray-800 border-none rounded-xl p-3 text-xs font-bold"
                                    required
                                />
                            </div>
                            <div className="col-span-1">
                                <button 
                                    type="button"
                                    onClick={() => {
                                        const items = newInvoice.items.filter((_, i) => i !== index);
                                        setNewInvoice({...newInvoice, items});
                                    }}
                                    className="text-gray-300 hover:text-red-500 transition-colors"
                                >
                                    <XMarkIcon className="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="bg-gray-50 dark:bg-gray-800/50 p-6 rounded-2xl space-y-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-xs font-bold text-gray-900 dark:text-white">Discount Configuration</p>
                            <p className="text-[10px] text-gray-500 font-medium">Apply a discount to this invoice</p>
                        </div>
                    </div>
                    <div className="flex items-center space-x-4 pt-2">
                        <div className="flex-1">
                            <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Discount Amount</label>
                            <input 
                                type="number" 
                                value={newInvoice.discount_amount}
                                onChange={(e) => setNewInvoice({...newInvoice, discount_amount: e.target.value})}
                                className="w-full bg-white dark:bg-gray-900 border-none rounded-xl p-3 text-xs font-bold mt-1"
                                placeholder="0"
                            />
                        </div>
                        <div className="flex-1">
                            <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Type</label>
                            <select
                                value={newInvoice.discount_type}
                                onChange={(e) => setNewInvoice({...newInvoice, discount_type: e.target.value as 'fixed' | 'percentage'})}
                                className="w-full bg-white dark:bg-gray-900 border-none rounded-xl p-3 text-xs font-bold mt-1 cursor-pointer"
                            >
                                <option value="fixed">Fixed (₹)</option>
                                <option value="percentage">Percentage (%)</option>
                            </select>
                        </div>
                    </div>
                </div>

                <div className="bg-gray-50 dark:bg-gray-800/50 p-6 rounded-2xl space-y-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-xs font-bold text-gray-900 dark:text-white">Tax Configuration</p>
                            <p className="text-[10px] text-gray-500 font-medium">Apply GST to this invoice</p>
                        </div>
                        <button 
                            type="button"
                            onClick={() => setNewInvoice({...newInvoice, include_gst: !newInvoice.include_gst})}
                            className={`w-12 h-6 rounded-full transition-colors relative ${newInvoice.include_gst ? 'bg-teal-500' : 'bg-gray-300'}`}
                        >
                            <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${newInvoice.include_gst ? 'left-7' : 'left-1'}`} />
                        </button>
                    </div>
                    {newInvoice.include_gst && (
                        <div className="flex items-center space-x-4 pt-2">
                            <div className="flex-1">
                                <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">GST Percentage (%)</label>
                                <input 
                                    type="number" 
                                    value={newInvoice.gst_percentage}
                                    onChange={(e) => setNewInvoice({...newInvoice, gst_percentage: parseFloat(e.target.value)})}
                                    className="w-full bg-white dark:bg-gray-900 border-none rounded-xl p-3 text-xs font-bold mt-1"
                                />
                            </div>
                            <div className="flex-1 pt-5">
                                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Estimated Tax</p>
                                <p className="text-sm font-black text-gray-900 dark:text-white">
                                    ₹{(newInvoice.items.reduce((acc, item) => acc + (item.quantity * item.unit_price), 0) * (newInvoice.gst_percentage / 100)).toFixed(2)}
                                </p>
                            </div>
                        </div>
                    )}
                </div>

                <div className="p-6 bg-teal-50 dark:bg-teal-900/20 rounded-2xl">
                    <div className="flex justify-between items-center">
                        <span className="text-sm font-black uppercase tracking-widest text-teal-600">Total Amount</span>
                        <span className="text-2xl font-black text-teal-600">
                            ₹{
                                (() => {
                                    const sub = newInvoice.items.reduce((acc, item) => acc + (item.quantity * item.unit_price), 0);
                                    const tx = newInvoice.include_gst ? (sub * (newInvoice.gst_percentage / 100)) : 0;
                                    const dRaw = parseFloat(newInvoice.discount_amount.toString() || '0');
                                    const dev = newInvoice.discount_type === 'percentage' ? sub * (dRaw / 100) : dRaw;
                                    return (Math.max(0, sub + tx - dev)).toFixed(2);
                                })()
                            }
                        </span>
                    </div>
                </div>

                <div className="pt-6 border-t border-gray-100 dark:border-gray-800">
                    <button 
                    type="submit"
                    className="w-full py-5 bg-black dark:bg-white text-white dark:text-black rounded-[2rem] text-sm font-black uppercase tracking-widest hover:opacity-90 transition-all font-mono"
                    >
                    Create & Send Invoice
                    </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* View Invoice Modal */}
      <AnimatePresence>
        {viewingInvoice && (
          <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setViewingInvoice(null)}
              className="absolute inset-0 bg-black/60 backdrop-blur-md"
            />
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative w-full max-w-xl bg-white dark:bg-gray-900 rounded-[3rem] overflow-hidden shadow-2xl p-10"
            >
              <div className="flex justify-between items-start mb-8">
                <div>
                    <h3 className="text-2xl font-black text-gray-900 dark:text-white uppercase tracking-tight">{viewingInvoice.invoice_number}</h3>
                    <p className="text-xs text-gray-400 font-bold uppercase tracking-widest mt-1">Due {new Date(viewingInvoice.due_date).toLocaleDateString()}</p>
                </div>
                <div className="flex items-center space-x-3">
                    <span className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest ${
                        viewingInvoice.status === 'paid' ? 'bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400' 
                        : viewingInvoice.status === 'accepted' ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400' 
                        : viewingInvoice.status === 'sent' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' 
                        : viewingInvoice.status === 'rejected' ? 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400' 
                        : viewingInvoice.status === 'overdue' ? 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400' 
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                    }`}>
                        {viewingInvoice.status}
                    </span>
                    <button 
                        onClick={() => {
                            const url = `${window.location.origin}/invoice/${viewingInvoice.id}`;
                            navigator.clipboard.writeText(url);
                            toast.success('Invoice link copied!');
                        }}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors group"
                        title="Copy Invoice Link"
                    >
                        <LinkIcon className="w-6 h-6 text-gray-400 group-hover:text-teal-500" />
                    </button>
                    {viewingInvoice.status !== 'paid' && (
                        <button 
                            onClick={() => {
                                setConfirmModalState({
                                    title: 'Delete Invoice',
                                    message: 'Are you sure you want to delete this invoice?',
                                    isDestructive: true,
                                    confirmText: 'Delete',
                                    cancelText: 'Cancel',
                                    onConfirm: async () => {
                                        try {
                                            await crmAPI.deleteInvoice(viewingInvoice.id);
                                            toast.success('Invoice deleted');
                                            setViewingInvoice(null);
                                            setShowConfirmModal(false);
                                            fetchInvoices(selectedProject.id);
                                            fetchData();
                                        } catch (err) {
                                            toast.error('Failed to delete invoice');
                                        }
                                    }
                                });
                                setShowConfirmModal(true);
                            }}
                            className="p-2 hover:bg-red-50 dark:hover:bg-red-950/20 rounded-full transition-colors group"
                            title="Delete Invoice"
                        >
                            <TrashIcon className="w-6 h-6 text-gray-400 group-hover:text-red-500" />
                        </button>
                    )}
                    <button onClick={() => setViewingInvoice(null)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors">
                        <XMarkIcon className="w-6 h-6" />
                    </button>
                </div>
              </div>

              <div className="space-y-6">
                {viewingInvoice.items && viewingInvoice.items.length > 0 && (
                <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-[2rem]">
                    <div className="space-y-4">
                        {viewingInvoice.items.map((item: any, i: number) => (
                            <div key={i} className="flex justify-between items-center text-sm">
                                <span className="font-bold text-gray-700 dark:text-gray-300">{item.description} (x{item.quantity})</span>
                                <span className="font-mono text-gray-900 dark:text-white">₹{item.unit_price * item.quantity}</span>
                            </div>
                        ))}
                    </div>
                </div>
                )}

                <div className="p-6 space-y-3">
                    <div className="flex justify-between items-center text-xs font-bold uppercase tracking-widest text-gray-400">
                        <span>Subtotal</span>
                        <span className="font-mono">₹{viewingInvoice.total_amount - viewingInvoice.tax + (viewingInvoice.discount_amount || 0)}</span>
                    </div>
                    {viewingInvoice.discount_amount > 0 && (
                        <div className="flex justify-between items-center text-xs font-bold uppercase tracking-widest text-emerald-500">
                            <span>Discount</span>
                            <span className="font-mono">-₹{viewingInvoice.discount_amount}</span>
                        </div>
                    )}
                    {viewingInvoice.tax > 0 && (
                        <div className="flex justify-between items-center text-xs font-bold uppercase tracking-widest text-gray-400">
                            <span>Tax</span>
                            <span className="font-mono">₹{viewingInvoice.tax}</span>
                        </div>
                    )}
                    <div className="flex justify-between items-center pt-3 border-t border-gray-100 dark:border-gray-700">
                        <span className="text-sm font-black uppercase tracking-widest">Total Amount</span>
                        <span className="text-2xl font-black">₹{viewingInvoice.total_amount}</span>
                    </div>
                </div>

                {viewingInvoice.notes && (
                    <div className="p-6 bg-amber-50/50 dark:bg-amber-900/10 rounded-2xl">
                        <p className="text-[10px] font-black uppercase tracking-widest text-amber-600 mb-1">Notes</p>
                        <p className="text-xs text-amber-800 dark:text-amber-200 font-medium">{viewingInvoice.notes}</p>
                    </div>
                )}

                <div className="flex flex-col sm:flex-row gap-4 mt-6">
                    {viewingInvoice.status === 'draft' && (
                        <>
                            <button 
                                onClick={() => {
                                    setConfirmModalState({
                                        title: 'Send Invoice',
                                        message: 'Are you sure you want to send this invoice to the client?',
                                        isDestructive: false,
                                        confirmText: 'Send',
                                        cancelText: 'Cancel',
                                        onConfirm: () => {
                                            updateInvoiceStatus(viewingInvoice.id, 'sent');
                                            setViewingInvoice(null);
                                            setShowConfirmModal(false);
                                        }
                                    });
                                    setShowConfirmModal(true);
                                }}
                                className="flex-1 py-4 bg-teal-500 hover:bg-teal-600 text-white rounded-[2rem] text-sm font-black uppercase tracking-widest transition-all shadow-lg shadow-teal-500/20"
                            >
                                Send Invoice
                            </button>
                            <button 
                                onClick={() => {
                                    setConfirmModalState({
                                        title: 'Mark as Paid',
                                        message: 'Are you sure you want to mark this invoice as paid?',
                                        isDestructive: false,
                                        confirmText: 'Confirm',
                                        cancelText: 'Cancel',
                                        onConfirm: () => {
                                            updateInvoiceStatus(viewingInvoice.id, 'paid');
                                            setViewingInvoice(null);
                                            setShowConfirmModal(false);
                                        }
                                    });
                                    setShowConfirmModal(true);
                                }}
                                className="flex-1 py-4 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-[2rem] text-sm font-black uppercase tracking-widest transition-all"
                            >
                                Mark as Paid
                            </button>
                        </>
                    )}
                    {(viewingInvoice.status === 'sent' || viewingInvoice.status === 'accepted' || viewingInvoice.status === 'overdue') && (
                        <>
                            <button 
                                onClick={() => {
                                    setConfirmModalState({
                                        title: 'Mark as Paid',
                                        message: 'Are you sure you want to mark this invoice as paid?',
                                        isDestructive: false,
                                        confirmText: 'Confirm',
                                        cancelText: 'Cancel',
                                        onConfirm: () => {
                                            updateInvoiceStatus(viewingInvoice.id, 'paid');
                                            setViewingInvoice(null);
                                            setShowConfirmModal(false);
                                        }
                                    });
                                    setShowConfirmModal(true);
                                }}
                                className="flex-1 py-4 bg-teal-500 hover:bg-teal-600 text-white rounded-[2rem] text-sm font-black uppercase tracking-widest transition-all shadow-lg shadow-teal-500/20"
                            >
                                Mark as Paid
                            </button>
                            <button 
                                onClick={() => {
                                    setConfirmModalState({
                                        title: 'Cancel Invoice',
                                        message: 'Are you sure you want to cancel this invoice? This action cannot be undone.',
                                        isDestructive: true,
                                        confirmText: 'Cancel Invoice',
                                        cancelText: 'Keep',
                                        onConfirm: () => {
                                            updateInvoiceStatus(viewingInvoice.id, 'cancelled');
                                            setViewingInvoice(null);
                                            setShowConfirmModal(false);
                                        }
                                    });
                                    setShowConfirmModal(true);
                                }}
                                className="flex-1 py-4 bg-red-50 hover:bg-red-100 dark:bg-red-950/20 text-red-600 rounded-[2rem] text-sm font-black uppercase tracking-widest transition-all"
                            >
                                Cancel Invoice
                            </button>
                        </>
                    )}
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showGenerateContract && (
          <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 min-h-screen">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowGenerateContract(false)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="relative w-full max-w-2xl bg-white dark:bg-gray-900 rounded-[3rem] overflow-hidden shadow-2xl flex flex-col max-h-[90vh]"
            >
              <div className="p-8 pb-6 border-b border-gray-100 dark:border-gray-800">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-2xl font-black text-gray-900 dark:text-white mb-2">Generate Contract</h2>
                    <p className="text-gray-500 font-medium">Create a new agreement for {selectedProject?.client_name}</p>
                  </div>
                  <button onClick={() => setShowGenerateContract(false)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors">
                    <XMarkIcon className="w-6 h-6" />
                  </button>
                </div>
              </div>

              <div className="p-8 overflow-y-auto custom-scrollbar flex-1">
                <form id="generate-contract-form" onSubmit={handleGenerateContract} className="space-y-6">
                    <div>
                      <label className="block text-xs font-black uppercase tracking-widest text-gray-400 mb-1.5 ml-1">Contract Title</label>
                      <input 
                        type="text" 
                        required
                        value={contractTitle}
                        onChange={(e) => setContractTitle(e.target.value)}
                        className="w-full px-5 py-3.5 bg-gray-50 dark:bg-gray-800 border-none rounded-2xl focus:ring-2 focus:ring-teal-500 transition-all font-medium"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs font-black uppercase tracking-widest text-gray-400 mb-1.5 ml-1">Select Template</label>
                      <select 
                        value={selectedTemplate}
                        onChange={(e) => setSelectedTemplate(e.target.value)}
                        className="w-full px-5 py-3.5 bg-gray-50 dark:bg-gray-800 border-none rounded-2xl focus:ring-2 focus:ring-teal-500 transition-all font-medium appearance-none"
                      >
                        <option value="">Start from blank...</option>
                        {templates.map(t => (
                            <option key={t.id} value={t.id}>{t.title}</option>
                        ))}
                      </select>
                    </div>

                    <div className="flex flex-col h-full bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800">
                      <label className="block text-xs font-black uppercase tracking-widest text-gray-400 mb-1.5 ml-4 mt-4">Contract Body</label>
                      <div className="flex-1 min-h-[300px] quill-modern">
                        <ReactQuill 
                          theme="snow"
                          value={contractBody}
                          onChange={setContractBody}
                          placeholder="Define terms, conditions, and deliverables..."
                          className="h-[250px]"
                        />
                      </div>
                    </div>
                </form>
              </div>

              <div className="px-8 py-6 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-800 flex space-x-3">
                <button 
                  type="button"
                  onClick={() => setShowGenerateContract(false)}
                  className="flex-1 py-4 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 rounded-2xl font-black text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-all border border-gray-200 dark:border-gray-700"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  form="generate-contract-form"
                  className="flex-[2] py-4 bg-black dark:bg-white text-white dark:text-black rounded-2xl font-black text-sm hover:opacity-90 transition-all shadow-xl shadow-black/10"
                >
                  Generate & Save
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {viewingContract && (
          <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 min-h-screen">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setViewingContract(null)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="relative w-full max-w-2xl bg-white dark:bg-gray-900 rounded-[3rem] print:rounded-none overflow-hidden shadow-2xl print:shadow-none flex flex-col max-h-[90vh] print:max-h-none print:h-auto"
            >
              <div className="p-8 pb-6 border-b border-gray-100 dark:border-gray-800 print:hidden">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-2xl font-black text-gray-900 dark:text-white mb-2">{viewingContract.title}</h2>
                    <div className="flex items-center space-x-2">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest ${
                            viewingContract.status === 'signed' ? 'bg-teal-50 text-teal-600' : 
                            (viewingContract.client_signature || viewingContract.photographer_signature) ? 'bg-amber-50 text-amber-600' :
                            'bg-indigo-50 text-indigo-600'
                        }`}>
                            {viewingContract.status === 'signed' ? 'Fully Executed' : (viewingContract.client_signature || viewingContract.photographer_signature ? 'Partially Signed' : viewingContract.status)}
                        </span>
                        {viewingContract.status === 'signed' && (
                            <span className="text-xs text-gray-400 font-bold uppercase tracking-widest">
                                Signed {new Date(viewingContract.signed_at).toLocaleDateString()}
                            </span>
                        )}
                    </div>
                  </div>
                  <button 
                    onClick={() => {
                      const url = `${window.location.origin}/crm/contract/${viewingContract.id}`;
                      navigator.clipboard.writeText(url);
                      toast.success('Contract link copied!');
                    }}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors text-gray-400 hover:text-teal-500 print:hidden" 
                    title="Copy Contract Link"
                  >
                    <LinkIcon className="w-6 h-6" />
                  </button>
                  {viewingContract.status !== 'signed' && (
                    <button 
                      onClick={() => {
                        setConfirmModalState({
                          title: 'Delete Contract',
                          message: 'Are you sure you want to delete this contract?',
                          isDestructive: true,
                          confirmText: 'Delete',
                          cancelText: 'Cancel',
                          onConfirm: async () => {
                            try {
                              await crmAPI.deleteContract(viewingContract.id);
                              toast.success('Contract deleted');
                              setViewingContract(null);
                              setShowConfirmModal(false);
                              const res = await crmAPI.getProjectContracts(selectedProject.id);
                              setProjectContracts(res.data.data.contracts || []);
                            } catch (err) {
                              toast.error('Failed to delete contract');
                            }
                          }
                        });
                        setShowConfirmModal(true);
                      }}
                      className="p-2 hover:bg-red-50 dark:hover:bg-red-950/20 rounded-full transition-colors text-gray-400 hover:text-red-500 print:hidden" 
                      title="Delete Contract"
                    >
                      <TrashIcon className="w-6 h-6" />
                    </button>
                  )}
                  <button onClick={() => setViewingContract(null)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors print:hidden">
                    <XMarkIcon className="w-6 h-6" />
                  </button>
                </div>
              </div>

              <div className="p-8 print:p-0 overflow-y-auto print:overflow-visible custom-scrollbar flex-1 bg-gray-50 dark:bg-gray-800 print:bg-white dark:print:bg-white text-gray-900">
                <div className="hidden print:block mb-8 pb-4 border-b border-gray-200">
                    <h1 className="text-3xl font-black uppercase tracking-tighter mb-2">{viewingContract.title}</h1>
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-widest">Client: {selectedProject?.client_name}</p>
                </div>
                <div className="bg-white dark:bg-gray-900 print:bg-transparent p-8 print:p-0 rounded-[2rem] print:rounded-none shadow-sm print:shadow-none">
                    <div 
                        className="prose dark:prose-invert max-w-none prose-sm font-serif whitespace-pre-wrap break-words"
                        dangerouslySetInnerHTML={{ __html: viewingContract.body.replace(/\n/g, '<br/>') }}
                    />
                </div>
                
                <div className="mt-8 space-y-4 print:page-break-inside-avoid text-gray-900">
                    {viewingContract.status === 'rejected' ? (
                        <div className="p-6 bg-red-50 dark:bg-red-900/20 print:bg-transparent border border-red-100 dark:border-red-900 print:border-gray-300 rounded-[2rem] print:rounded-none text-center">
                            <p className="text-xs font-black uppercase tracking-widest text-red-600 dark:text-red-400 print:text-gray-900 mb-2">Client Signature Status</p>
                            <p className="text-xl font-black text-red-700 dark:text-red-400 print:text-gray-900 uppercase">Declined</p>
                            <p className="text-[10px] font-bold text-red-500 print:text-gray-500 uppercase tracking-widest mt-2">
                                {new Date(viewingContract.updated_at).toLocaleString()}
                            </p>
                        </div>
                    ) : viewingContract.client_signature ? (
                        <div className="p-6 bg-teal-50 dark:bg-teal-900/20 print:bg-transparent border border-teal-100 dark:border-teal-900 print:border-gray-300 rounded-[2rem] print:rounded-none text-center">
                            <p className="text-xs font-black uppercase tracking-widest text-teal-600 print:text-gray-900 mb-2">Client Signature</p>
                            <p className="text-2xl font-black text-teal-800 dark:text-teal-400 print:text-gray-900 signature-font">{viewingContract.client_signature}</p>
                            <p className="text-[10px] font-bold text-teal-500 print:text-gray-500 uppercase tracking-widest mt-2">
                                {new Date(viewingContract.signed_at).toLocaleString()}
                            </p>
                        </div>
                    ) : (
                        <div className="p-6 bg-gray-50 print:bg-transparent border border-gray-100 print:border-gray-300 rounded-2xl print:rounded-none text-center">
                            <p className="text-xs text-gray-400 print:text-gray-600 font-medium italic">Awaiting client signature via shared link.</p>
                        </div>
                    )}

                    {/* ROLE-SPECIFIC SIGNATURE DISPLAY / FORM */}
                    {viewingContract.photographer_signature ? (
                        <div className="p-6 bg-indigo-50 dark:bg-indigo-900/20 print:bg-transparent border border-indigo-100 dark:border-indigo-900 print:border-gray-300 rounded-[2rem] print:rounded-none text-center">
                            <p className="text-xs font-black uppercase tracking-widest text-indigo-600 print:text-gray-900 mb-2">{professionLabel} Countersign</p>
                            <p className="text-2xl font-black text-indigo-800 dark:text-indigo-400 print:text-gray-900 signature-font">{viewingContract.photographer_signature}</p>
                            <p className="text-[10px] font-bold text-indigo-500 print:text-gray-500 uppercase tracking-widest mt-2">
                                {new Date(viewingContract.photographer_signed_at).toLocaleString()}
                            </p>
                        </div>
                    ) : viewingContract.status !== 'rejected' && (
                        <form id="sign-contract-form" onSubmit={handleSignContract} className="mt-4 print:hidden">
                            <div className="p-6 bg-white dark:bg-gray-900 rounded-[2rem] border border-gray-200 dark:border-gray-700">
                                <h4 className="text-sm font-black uppercase tracking-widest text-gray-900 dark:text-white mb-4">Acceptance & Signature</h4>
                                <p className="text-xs text-gray-500 mb-4">By typing your name below, you electronically agree, as the {professionLabel.toLowerCase()}, to the terms and conditions outlined in the agreement above.</p>
                                <div>
                                    <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1.5 ml-1">Type Full Name to Sign</label>
                                    <input 
                                        type="text" 
                                        required
                                        value={clientSignature}
                                        onChange={(e) => setClientSignature(e.target.value)}
                                        className="w-full px-5 py-3.5 bg-gray-50 dark:bg-gray-800 dark:text-white border-none rounded-2xl focus:ring-2 focus:ring-teal-500 transition-all font-medium"
                                        placeholder="e.g. John Doe"
                                    />
                                </div>
                            </div>
                        </form>
                    )}
                </div>
              </div>

              <div className="px-8 py-6 bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800 flex flex-col gap-4 print:hidden">
                {/* Sign Button */}
                {viewingContract.status !== 'signed' && viewingContract.status !== 'rejected' && !viewingContract.photographer_signature && (
                  <button 
                    type="submit"
                    form="sign-contract-form"
                    className="w-full py-4 bg-teal-500 hover:bg-teal-600 text-white rounded-[2rem] font-black text-sm uppercase tracking-widest transition-all shadow-xl shadow-teal-500/20"
                  >
                    Sign Agreement
                  </button>
                )}

                {/* Send Button */}
                {viewingContract.status === 'draft' && viewingContract.photographer_signature && (
                  <button 
                    onClick={() => {
                      setConfirmModalState({
                        title: 'Send Contract',
                        message: 'Are you sure you want to send this contract to the client?',
                        isDestructive: false,
                        confirmText: 'Send',
                        cancelText: 'Cancel',
                        onConfirm: () => {
                          updateContractStatus(viewingContract.id, 'sent');
                          setViewingContract(null);
                          setShowConfirmModal(false);
                        }
                      });
                      setShowConfirmModal(true);
                    }}
                    className="w-full py-4 bg-teal-500 hover:bg-teal-600 text-white rounded-[2rem] text-sm font-black uppercase tracking-widest transition-all shadow-lg shadow-teal-500/20"
                  >
                    Send Contract
                  </button>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Sync Prompt Modal */}
      <AnimatePresence>
        {false && showSyncPrompt && projectToSync && (
          <div className="fixed inset-0 z-[150] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowSyncPrompt(false)}
              className="absolute inset-0 bg-black/60 backdrop-blur-md"
            />
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              className="relative w-full max-w-lg bg-white dark:bg-gray-900 rounded-[2.5rem] overflow-hidden shadow-2xl p-8 text-center"
            >
              <div className="w-20 h-20 bg-teal-50 dark:bg-teal-900/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <GlobeAltIcon className="w-10 h-10 text-teal-500" />
              </div>
              <h3 className="text-2xl font-black text-gray-900 dark:text-white mb-4">Post-Project Sync</h3>
              <p className="text-gray-500 font-medium mb-8">
                Great job completing <span className="text-teal-500 font-bold">{projectToSync.title}</span>! 
                Would you like to publish this project's gallery as a community collaboration post to showcase your work?
              </p>
              
              <div className="flex space-x-4">
                <button
                  onClick={() => setShowSyncPrompt(false)}
                  className="flex-1 py-4 bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 rounded-[1.5rem] font-black text-sm hover:bg-gray-200 dark:hover:bg-gray-700 transition-all"
                >
                  Maybe Later
                </button>
                <button
                  onClick={async () => {
                      toast.loading("Preparing post...", { id: 'sync' });
                      try {
                          await crmAPI.syncToCollaboration(projectToSync.id);
                          toast.success("Successfully prepared! Redirecting to draft...", { id: 'sync' });
                          setShowSyncPrompt(false);
                          
                          // TODO: Redirect to pre-filled draft in 'Create Collaboration' modal with images and details
                          // For now wait briefly then mock it or guide the user
                          setTimeout(() => {
                              window.location.href = `/profile?action=create-collaboration&crm_project_id=${projectToSync.id}`;
                          }, 1000);

                      } catch(err) {
                           toast.error("Failed to sync.", { id: 'sync' });
                      }
                  }}
                  className="flex-[2] py-4 bg-teal-500 text-white rounded-[1.5rem] font-black text-sm uppercase tracking-widest hover:bg-teal-600 transition-all shadow-lg shadow-teal-500/20"
                >
                  Create Post Draft
                </button>
              </div>
            </motion.div>
          </div>
        )}

        {/* Add Event Modal */}
        {showAddEvent && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowAddEvent(false)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative w-full max-w-lg bg-white dark:bg-gray-900 rounded-[2rem] overflow-hidden shadow-2xl"
            >
              <div className="p-6 md:p-8 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center">
                <h2 className="text-2xl font-black text-gray-900 dark:text-white">Add Calendar Event</h2>
                <button
                  onClick={() => setShowAddEvent(false)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
                >
                  <XMarkIcon className="w-6 h-6 text-gray-500" />
                </button>
              </div>

              <form onSubmit={handleCreateEvent} className="p-6 md:p-8 space-y-6">
                <div>
                  <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Event Title</label>
                  <input
                    type="text"
                    required
                    value={newEvent.title}
                    onChange={e => setNewEvent({ ...newEvent, title: e.target.value })}
                    className="w-full px-5 py-3.5 bg-gray-50 dark:bg-gray-800 border-none rounded-2xl focus:ring-2 focus:ring-teal-500 transition-all font-medium"
                    placeholder="e.g. Brainstorming Session"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Description</label>
                  <textarea
                    value={newEvent.description}
                    onChange={e => setNewEvent({ ...newEvent, description: e.target.value })}
                    className="w-full px-5 py-3.5 bg-gray-50 dark:bg-gray-800 border-none rounded-2xl focus:ring-2 focus:ring-teal-500 transition-all font-medium resize-none h-24"
                    placeholder="Optional details..."
                  />
                </div>

                <div className="flex items-center space-x-3 bg-gray-50 dark:bg-gray-800 p-4 rounded-2xl">
                    <input
                        type="checkbox"
                        id="is_all_day_toggle"
                        checked={newEvent.is_all_day}
                        onChange={(e) => setNewEvent({ ...newEvent, is_all_day: e.target.checked })}
                        className="w-5 h-5 rounded border-gray-300 text-teal-500 focus:ring-teal-500"
                    />
                    <label htmlFor="is_all_day_toggle" className="text-sm font-bold text-gray-700 dark:text-gray-300 cursor-pointer">
                        All-Day Event
                    </label>
                </div>

                {!newEvent.is_all_day && (
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Start Time</label>
                            <input
                                type="datetime-local"
                                required={!newEvent.is_all_day}
                                value={newEvent.start_time}
                                onChange={e => setNewEvent({ ...newEvent, start_time: e.target.value })}
                                className="w-full px-5 py-3.5 bg-gray-50 dark:bg-gray-800 border-none rounded-2xl focus:ring-2 focus:ring-teal-500 transition-all text-sm font-medium"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">End Time</label>
                            <input
                                type="datetime-local"
                                required={!newEvent.is_all_day}
                                value={newEvent.end_time}
                                onChange={e => setNewEvent({ ...newEvent, end_time: e.target.value })}
                                className="w-full px-5 py-3.5 bg-gray-50 dark:bg-gray-800 border-none rounded-2xl focus:ring-2 focus:ring-teal-500 transition-all text-sm font-medium"
                            />
                        </div>
                    </div>
                )}
                
                {newEvent.is_all_day && (
                     <div>
                        <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Date</label>
                        <input
                            type="date"
                            required
                            value={newEvent.start_time.split('T')[0]}
                            onChange={e => {
                                const newDate = e.target.value;
                                if(newDate) {
                                    setNewEvent({ ...newEvent, start_time: `${newDate}T00:00`, end_time: `${newDate}T23:59` });
                                }
                            }}
                            className="w-full px-5 py-3.5 bg-gray-50 dark:bg-gray-800 border-none rounded-2xl focus:ring-2 focus:ring-teal-500 transition-all text-sm font-medium"
                        />
                    </div>
                )}

                <div className="pt-4 flex space-x-4">
                  <button
                    type="button"
                    onClick={() => setShowAddEvent(false)}
                    className="flex-1 py-4 bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 rounded-2xl font-bold hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 py-4 bg-black dark:bg-white text-white dark:text-black rounded-2xl font-bold hover:scale-[1.02] active:scale-[0.98] transition-all"
                  >
                    Add Event
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <div className="hidden print:block fixed inset-0 z-[200] bg-white overflow-y-auto print-container">
        {viewingInvoice && (
          <InvoicePrintView 
            invoice={viewingInvoice} 
            project={selectedProject} 
            user={user} 
          />
        )}
      </div>
      {/* Calendar Event Details Modal */}
      <AnimatePresence>
        {viewingCalendarEvent && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => {
                setViewingCalendarEvent(null);
                setIsEditingEvent(false);
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-gray-900 w-full max-w-lg rounded-[2.5rem] overflow-hidden border border-gray-100 dark:border-gray-800 shadow-2xl"
            >
              <div className="p-8">
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center space-x-3">
                    <div className="p-3 bg-teal-50 dark:bg-teal-900/20 rounded-2xl">
                      <CalendarDaysIcon className="w-6 h-6 text-teal-600 dark:text-teal-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-black text-gray-900 dark:text-white">Event Details</h3>
                      <p className="text-xs text-gray-500 font-medium tracking-wide uppercase">Manage your calendar entry</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => {
                        setViewingCalendarEvent(null);
                        setIsEditingEvent(false);
                    }}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors"
                  >
                    <XMarkIcon className="w-6 h-6 text-gray-400" />
                  </button>
                </div>

                {!isEditingEvent ? (
                    <div className="space-y-6">
                        <div>
                            <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1 block">Title</label>
                            <p className="text-lg font-bold text-gray-900 dark:text-white">{viewingCalendarEvent.title}</p>
                        </div>
                        
                        {viewingCalendarEvent.description && (
                            <div>
                                <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1 block">Description</label>
                                <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{viewingCalendarEvent.description}</p>
                            </div>
                        )}

                        <div className="grid grid-cols-2 gap-6">
                            <div>
                                <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1 block">Starts</label>
                                <p className="text-sm font-bold text-gray-900 dark:text-white">
                                    {new Date(viewingCalendarEvent.start_time).toLocaleString([], { dateStyle: 'medium', timeStyle: viewingCalendarEvent.is_all_day ? undefined : 'short' })}
                                </p>
                            </div>
                            <div>
                                <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1 block">Ends</label>
                                <p className="text-sm font-bold text-gray-900 dark:text-white">
                                    {new Date(viewingCalendarEvent.end_time).toLocaleString([], { dateStyle: 'medium', timeStyle: viewingCalendarEvent.is_all_day ? undefined : 'short' })}
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center justify-between pt-8 border-t border-gray-100 dark:border-gray-800">
                            <button
                                onClick={() => handleDeleteEvent(viewingCalendarEvent.id)}
                                className="flex items-center space-x-2 px-6 py-3 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 rounded-2xl font-bold transition-colors"
                            >
                                <TrashIcon className="w-5 h-5" />
                                <span>Delete</span>
                            </button>
                            <button
                                onClick={() => setIsEditingEvent(true)}
                                className="px-8 py-3 bg-black dark:bg-white text-white dark:text-black rounded-2xl font-bold hover:scale-105 transition-transform"
                            >
                                Edit Details
                            </button>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={handleUpdateEvent} className="space-y-6">
                        <div>
                            <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2 block">Event Title</label>
                            <input
                                type="text"
                                value={viewingCalendarEvent.title}
                                onChange={(e) => setViewingCalendarEvent({ ...viewingCalendarEvent, title: e.target.value })}
                                className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border-none rounded-2xl focus:ring-2 focus:ring-teal-500 font-bold"
                                required
                            />
                        </div>

                        <div>
                            <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2 block">Description</label>
                            <textarea
                                value={viewingCalendarEvent.description}
                                onChange={(e) => setViewingCalendarEvent({ ...viewingCalendarEvent, description: e.target.value })}
                                className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border-none rounded-2xl focus:ring-2 focus:ring-teal-500 font-bold min-h-[100px]"
                            />
                        </div>

                        <div className="flex items-center space-x-2 mb-6 cursor-pointer" onClick={() => setViewingCalendarEvent({ ...viewingCalendarEvent, is_all_day: !viewingCalendarEvent.is_all_day })}>
                            <div className={`w-10 h-6 rounded-full p-1 transition-colors ${viewingCalendarEvent.is_all_day ? 'bg-teal-500' : 'bg-gray-200'}`}>
                                <div className={`w-4 h-4 bg-white rounded-full transition-transform ${viewingCalendarEvent.is_all_day ? 'translate-x-4' : ''}`} />
                            </div>
                            <span className="text-sm font-bold text-gray-700 dark:text-gray-300">All Day Event</span>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2 block">Start Time</label>
                                <input
                                    type={viewingCalendarEvent.is_all_day ? "date" : "datetime-local"}
                                    value={viewingCalendarEvent.start_time?.substring(0, viewingCalendarEvent.is_all_day ? 10 : 16)}
                                    onChange={(e) => setViewingCalendarEvent({ ...viewingCalendarEvent, start_time: e.target.value })}
                                    className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border-none rounded-2xl focus:ring-2 focus:ring-teal-500 font-bold"
                                />
                            </div>
                            <div>
                                <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2 block">End Time</label>
                                <input
                                    type={viewingCalendarEvent.is_all_day ? "date" : "datetime-local"}
                                    value={viewingCalendarEvent.end_time?.substring(0, viewingCalendarEvent.is_all_day ? 10 : 16)}
                                    onChange={(e) => setViewingCalendarEvent({ ...viewingCalendarEvent, end_time: e.target.value })}
                                    className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border-none rounded-2xl focus:ring-2 focus:ring-teal-500 font-bold"
                                />
                            </div>
                        </div>

                        <div className="flex items-center justify-end space-x-4 pt-8 border-t border-gray-100 dark:border-gray-800">
                            <button
                                type="button"
                                onClick={() => setIsEditingEvent(false)}
                                className="px-6 py-3 text-gray-500 font-bold hover:bg-gray-50 dark:hover:bg-gray-800 rounded-2xl transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="px-8 py-3 bg-teal-500 text-white rounded-2xl font-bold hover:bg-teal-600 transition-colors shadow-lg shadow-teal-500/20"
                            >
                                Save Changes
                            </button>
                        </div>
                    </form>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      <ConfirmModal 
        {...confirmModalState} 
        isOpen={showConfirmModal} 
        onCancel={() => setShowConfirmModal(false)} 
      />
      <PromptModal 
        {...promptModalState} 
        isOpen={showPromptModal} 
        onClose={() => setShowPromptModal(false)}
      />
      {renderLeadDetailModal()}

      {/* Lightbox Gallery Item Preview Modal */}
      <AnimatePresence>
        {selectedImageForView && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] bg-black/95 backdrop-blur-md flex items-center justify-center p-4 md:p-10"
            onClick={() => setSelectedImageForView(null)}
          >
            {/* Navigation Buttons */}
            {galleryImages.length > 1 && (
              <>
                <button 
                  onClick={(e) => { e.stopPropagation(); navigateCRMGallery('prev'); }}
                  className="absolute left-2 md:left-10 p-3 md:p-4 bg-white/10 hover:bg-white/20 text-white rounded-full backdrop-blur-md transition-all group active:scale-90 z-[210]"
                  title="Previous (Left Arrow)"
                >
                  <ChevronLeftIcon className="w-6 h-6 md:w-8 md:h-8 group-hover:-translate-x-1 transition-transform" />
                </button>
                <button 
                  onClick={(e) => { e.stopPropagation(); navigateCRMGallery('next'); }}
                  className="absolute right-2 md:right-10 p-3 md:p-4 bg-white/10 hover:bg-white/20 text-white rounded-full backdrop-blur-md transition-all group active:scale-90 z-[210]"
                  title="Next (Right Arrow)"
                >
                  <ChevronRightIcon className="w-6 h-6 md:w-8 md:h-8 group-hover:translate-x-1 transition-transform" />
                </button>
              </>
            )}

            <div className="absolute top-4 right-4 md:top-6 md:right-6 flex items-center space-x-3 md:space-x-4 z-[210]">
              <a 
                href={(() => {
                  const item = selectedImageForView;
                  if (item.video_platform === 'google_drive') {
                    let fileId = "";
                    try {
                      const urlObj = new URL(item.url);
                      const pathParts = urlObj.pathname.split('/').filter(Boolean);
                      for (let i = 0; i < pathParts.length; i++) {
                        if (pathParts[i] === 'd' && i + 1 < pathParts.length) {
                          fileId = pathParts[i+1];
                          break;
                        }
                      }
                      if (!fileId) {
                        const idParam = urlObj.searchParams.get('id');
                        if (idParam) fileId = idParam;
                      }
                    } catch (e) {}
                    if (fileId) {
                      return `https://drive.google.com/uc?export=download&id=${fileId}`;
                    }
                  }
                  return item.url_signed || item.url;
                })()}
                download={selectedImageForView.original_name || "download"}
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 md:p-4 bg-white/10 text-white backdrop-blur-md rounded-full hover:bg-white/20 transition-all active:scale-90 flex items-center justify-center"
                title="Download File"
                onClick={(e) => e.stopPropagation()}
              >
                <CloudArrowUpIcon className="w-5 h-5 md:w-6 md:h-6 rotate-180" />
              </a>

              <button 
                onClick={(e) => { e.stopPropagation(); toggleImageSelection(selectedImageForView.id); }}
                className={`p-3 md:p-4 rounded-full transition-all active:scale-90 ${
                  selectedImageForView.is_photographer_selected
                    ? 'bg-teal-500 text-white shadow-xl shadow-teal-500/40' 
                    : 'bg-white/10 text-white backdrop-blur-md hover:bg-white/20'
                }`}
                title={selectedImageForView.is_photographer_selected ? "Deselect Photographer Pick" : "Mark as Photographer Pick"}
              >
                <CheckBadgeIcon className="w-5 h-5 md:w-6 md:h-6" />
              </button>

              <motion.button 
                onClick={() => setSelectedImageForView(null)}
                className="p-3 md:p-4 bg-white/10 text-white backdrop-blur-md rounded-full hover:bg-white/20 transition-all active:scale-90"
                title="Close (Esc)"
              >
                <XMarkIcon className="w-5 h-5 md:w-6 md:h-6" />
              </motion.button>
            </div>

            <motion.div 
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative max-w-[90vw] max-h-[85vh] flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
               {selectedImageForView.media_type === 'video' ? (
                selectedImageForView.processing_status === 'processing' ? (
                  // Video is still being processed by Google Drive
                  <div className="relative w-full max-w-4xl aspect-video rounded-2xl overflow-hidden shadow-2xl bg-black/80 border border-white/10 z-10 flex flex-col items-center justify-center gap-4 px-8 text-center">
                    <div className="w-14 h-14 rounded-full border-4 border-white/20 border-t-purple-400 animate-spin" />
                    <p className="text-white text-lg font-bold">Video is being processed</p>
                    <p className="text-white/50 text-sm">Google Drive is still processing this video. Please check back in a few minutes.</p>
                  </div>
                ) : (
                  <div className="relative w-full max-w-4xl aspect-video rounded-2xl overflow-hidden shadow-2xl bg-black border border-white/10 z-10">
                    {(selectedImageForView.video_platform === 'google_drive' || (selectedImageForView.url && selectedImageForView.url.includes('drive.google.com'))) ? (
                      <GoogleDriveVideoEmbed
                        src={selectedImageForView.url}
                        title={selectedImageForView.original_name || "Video Preview"}
                        allowDownload={true}
                      />
                    ) : (
                      <iframe
                        src={(() => {
                          const videoUrl = selectedImageForView.url;
                          const platform = selectedImageForView.video_platform;
                          if (platform === 'vimeo') {
                            try {
                              const urlObj = new URL(videoUrl);
                              const parts = urlObj.pathname.split('/').filter(Boolean);
                              if (parts.length > 0) return `https://player.vimeo.com/video/${parts[parts.length - 1]}?autoplay=1`;
                            } catch (e) {}
                          } else if (platform === 'instagram') {
                            try {
                              const urlObj = new URL(videoUrl);
                              const parts = urlObj.pathname.split('/').filter(Boolean);
                              for (let i = 0; i < parts.length; i++) {
                                if ((parts[i] === 'reel' || parts[i] === 'p' || parts[i] === 'reels') && i + 1 < parts.length) {
                                  const pathType = parts[i] === 'reels' ? 'reel' : parts[i];
                                  return `https://www.instagram.com/${pathType}/${parts[i+1]}/embed/`;
                                }
                              }
                            } catch (e) {}
                          } else {
                            try {
                              const urlObj = new URL(videoUrl);
                              if (urlObj.hostname.toLowerCase() === 'youtu.be') return `https://www.youtube.com/embed/${urlObj.pathname.slice(1)}?autoplay=1`;
                              const v = urlObj.searchParams.get('v');
                              if (v) return `https://www.youtube.com/embed/${v}?autoplay=1`;
                              if (urlObj.pathname.startsWith('/embed/')) return `https://www.youtube.com/embed/${urlObj.pathname.slice(7)}?autoplay=1`;
                              if (urlObj.pathname.startsWith('/shorts/')) return `https://www.youtube.com/embed/${urlObj.pathname.slice(8)}?autoplay=1`;
                            } catch (e) {}
                          }
                          return videoUrl;
                        })()}
                        title={selectedImageForView.original_name || "Video Preview"}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="absolute inset-0 w-full h-full border-0 rounded-2xl"
                      ></iframe>
                    )}
                  </div>
                )
              ) : (
                <img 
                  src={resolveImageUrl(selectedImageForView.url_signed || selectedImageForView.url)} 
                  alt="" 
                  className="max-w-full max-h-[85vh] object-contain rounded-xl shadow-2xl select-none"
                />
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Video Upload & Linking Choice Modal */}
      <AnimatePresence>
        {showVideoOptionsModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => {
              setShowVideoOptionsModal(false);
              setIsLinkingVideo(false);
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-gray-900 w-full max-w-md rounded-[2.5rem] overflow-hidden border border-gray-100 dark:border-gray-800 shadow-2xl p-8"
            >
              {!isLinkingVideo ? (
                <div>
                  <h3 className="text-xl font-black text-gray-900 dark:text-white mb-2">Add Video to Proof Gallery</h3>
                  <p className="text-xs text-gray-500 font-medium mb-6">Choose how you want to add this video to the project gallery.</p>
                  
                  <div className="space-y-4">
                    <button
                      onClick={async () => {
                        setShowVideoOptionsModal(false);
                        // Trigger file input
                        videoInputRef.current?.click();
                      }}
                      className="w-full flex flex-col items-start p-5 bg-purple-50 hover:bg-purple-100 dark:bg-purple-950/20 dark:hover:bg-purple-950/40 border border-purple-100 dark:border-purple-900/30 rounded-2xl transition-all group text-left"
                    >
                      <span className="text-xs font-black text-purple-700 dark:text-purple-400 uppercase tracking-widest mb-1.5 flex items-center">
                        ☁ Upload Video File
                      </span>
                      <span className="text-[11px] text-gray-500 dark:text-gray-400 font-semibold leading-relaxed">
                        Upload directly from your local machine. This video will be stored in your connected Google Drive.
                      </span>
                    </button>

                    <button
                      onClick={() => setIsLinkingVideo(true)}
                      className="w-full flex flex-col items-start p-5 bg-teal-50 hover:bg-teal-100 dark:bg-teal-950/20 dark:hover:bg-teal-950/40 border border-teal-100 dark:border-teal-900/30 rounded-2xl transition-all group text-left"
                    >
                      <span className="text-xs font-black text-teal-700 dark:text-teal-400 uppercase tracking-widest mb-1.5 flex items-center">
                        🔗 Link External Video
                      </span>
                      <span className="text-[11px] text-gray-500 dark:text-gray-400 font-semibold leading-relaxed">
                        Paste a link to a video hosted on YouTube, Vimeo, or Instagram Reels.
                      </span>
                    </button>
                  </div>

                  <div className="mt-8 flex justify-end">
                    <button
                      onClick={() => setShowVideoOptionsModal(false)}
                      className="px-6 py-2.5 text-[10px] font-black uppercase tracking-widest text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-xl transition-all border border-transparent"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <h3 className="text-xl font-black text-gray-900 dark:text-white mb-2">Link External Video</h3>
                  <p className="text-xs text-gray-500 font-medium mb-6">Paste the video URL to embed it. Supports YouTube, Vimeo, Instagram Reels, and Google Drive.</p>

                  <div className="space-y-5">
                    <div>
                      <label className="text-[9px] font-black uppercase tracking-widest text-gray-400 mb-2 block">Video URL</label>
                      <input
                        type="url"
                        placeholder="e.g. YouTube, Vimeo, Instagram Reels, or Google Drive URL"
                        value={videoLinkUrl}
                        onChange={(e) => setVideoLinkUrl(e.target.value)}
                        className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border-none rounded-2xl focus:ring-2 focus:ring-teal-500 font-bold text-sm text-gray-900 dark:text-white"
                        required
                      />
                    </div>

                    <div>
                      <label className="text-[9px] font-black uppercase tracking-widest text-gray-400 mb-2 block">Title (Optional)</label>
                      <input
                        type="text"
                        placeholder="e.g. Wedding Highlight Video"
                        value={videoLinkTitle}
                        onChange={(e) => setVideoLinkTitle(e.target.value)}
                        className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border-none rounded-2xl focus:ring-2 focus:ring-teal-500 font-bold text-sm text-gray-900 dark:text-white"
                      />
                    </div>

                    <div>
                      <label className="text-[9px] font-black uppercase tracking-widest text-gray-400 mb-2 block">Description (Optional)</label>
                      <textarea
                        placeholder="e.g. Cinematic cut of the ceremony..."
                        value={videoLinkDesc}
                        onChange={(e) => setVideoLinkDesc(e.target.value)}
                        className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border-none rounded-2xl focus:ring-2 focus:ring-teal-500 font-bold text-sm text-gray-900 dark:text-white min-h-[90px]"
                      />
                    </div>
                  </div>

                  <div className="mt-8 flex justify-end space-x-3">
                    <button
                      onClick={() => setIsLinkingVideo(false)}
                      className="px-6 py-2.5 text-[10px] font-black uppercase tracking-widest text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-xl transition-all"
                    >
                      Back
                    </button>
                    <button
                      onClick={async () => {
                        if (!videoLinkUrl) {
                          toast.error("Please enter a video URL");
                          return;
                        }
                        const toastId = toast.loading("Adding video link to project gallery…");
                        try {
                          const res = await crmAPI.addCRMGalleryVideoLink(selectedProject.id, {
                            video_url: videoLinkUrl,
                            title: videoLinkTitle,
                            description: videoLinkDesc,
                          });
                          if (res.data?.success || res.data?.item) {
                            toast.success("Video link added to gallery!", { id: toastId });
                            const newItem = res.data.item || res.data.data?.item || res.data.data;
                            if (newItem) {
                              setGalleryImages(prev => [...prev, newItem]);
                            }
                            // Clear state
                            setVideoLinkUrl('');
                            setVideoLinkTitle('');
                            setVideoLinkDesc('');
                            setIsLinkingVideo(false);
                            setShowVideoOptionsModal(false);
                          } else {
                            throw new Error(res.data?.error || "Failed to add video link");
                          }
                        } catch (err: any) {
                          toast.error(err.response?.data?.error || "Failed to add video link", { id: toastId });
                        }
                      }}
                      className="px-6 py-2.5 bg-teal-500 hover:bg-teal-600 text-white text-[10px] font-black uppercase tracking-widest rounded-xl transition-all shadow-lg shadow-teal-500/20"
                    >
                      Add Video Link
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* [Subscription removed] UpgradePopup removed — platform is fully free. */}
    </div>
  );
};
export default CRMPage;